import React, { useState, useEffect, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, onSnapshot, doc, setDoc, updateDoc, query, orderBy, addDoc, serverTimestamp, where, limit, deleteDoc, getDoc, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithCredential, RecaptchaVerifier, signInWithPhoneNumber, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut, sendEmailVerification, sendPasswordResetEmail, linkWithPhoneNumber } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import ChatMessage from './backend/services/ChatMessage.jsx';
import EmojiPicker from 'emoji-picker-react';
import { exportToCSV } from './utils/csvExport';
import emailjs from '@emailjs/browser';
import notificationSoundFile from './not.mp3';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { exportProfilesToPDF } from './utils/pdfExport';
import { compressImage } from './utils/imageCompression';

const firebaseConfig = {
    apiKey: "AIzaSyByT4GQ5QZ5XRLINujzeu2SjmyCi_65F6U",
    authDomain: "myluckyspouse.firebaseapp.com",
    projectId: "myluckyspouse",
    storageBucket: "myluckyspouse.firebasestorage.app",
    messagingSenderId: "427402437130",
    appId: "1:427402437130:web:18ba897351871f9f89f5f3",
    measurementId: "G-8BJFV03VL0"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app, "myluckyspouse"); // <-- Connect to your named database
const auth = getAuth(app);
const functions = getFunctions(app);
const storage = getStorage(app);
const messaging = getMessaging(app);

// --- Sample Data ---
const successStories = [
    { id: 1, names: 'Rahul & Anjali', image: 'https://placehold.co/600x400/fda4af/9f1239?text=R+%26+A', story: "We met through MyLuckySpouse and instantly connected over our shared love for classic movies. We're getting married next spring!" },
    { id: 2, names: 'Sameer & Tina', image: 'https://placehold.co/600x400/f9a8d4/86198f?text=S+%26+T', story: "Finding someone who understood my passion for art was a dream come true. Thank you MyLuckySpouse for introducing me to my life partner." },
    { id: 3, names: 'Aarav & Meera', image: 'https://placehold.co/600x400/fbcfe8/9d174d?text=A+%26+M', story: "Neither of us thought online platforms would work, but we were proven wrong. Our journey together has been incredible." },
];

// --- Helper Functions & Components ---
const calculateAge = (dobString) => {
    if (!dobString) return null;
    const birthDate = new Date(dobString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + "mo ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + "m ago";
    return Math.floor(seconds) + "s ago";
};

const isProfileOnline = (profile) => {
    if (!profile) return false;
    
    // 1. If explicitly set to offline (boolean or string), they are offline immediately
    if (profile.isOnline === false || profile.isOnline === 'false') {
        return false;
    }
    
    // 2. To be online, they MUST have a valid heartbeat timestamp in lastActive
    if (!profile.lastActive) return false;
    
    let lastActiveMillis = 0;
    if (typeof profile.lastActive.toMillis === 'function') {
        lastActiveMillis = profile.lastActive.toMillis();
    } else if (profile.lastActive instanceof Date) {
        lastActiveMillis = profile.lastActive.getTime();
    } else if (typeof profile.lastActive === 'number') {
        lastActiveMillis = profile.lastActive;
    } else {
        lastActiveMillis = new Date(profile.lastActive).getTime();
    }
    
    if (isNaN(lastActiveMillis) || lastActiveMillis === 0) return false;
    
    const diff = Date.now() - lastActiveMillis;
    // Allow up to 2 minutes client clock skew (diff can be slightly negative)
    const isRecent = diff > -2 * 60 * 1000 && diff < 5 * 60 * 1000;
    
    // 3. User is online if their heartbeat is fresh AND isOnline is not explicitly false
    return isRecent && (profile.isOnline === true || profile.isOnline === 'true' || profile.isOnline === undefined || profile.isOnline === null);
};

const getLastActive = (profile) => {
    if (!profile || !profile.lastActive) return 'Never';
    
    let lastActiveMillis = 0;
    if (typeof profile.lastActive.toMillis === 'function') {
        lastActiveMillis = profile.lastActive.toMillis();
    } else if (profile.lastActive instanceof Date) {
        lastActiveMillis = profile.lastActive.getTime();
    } else if (typeof profile.lastActive === 'number') {
        lastActiveMillis = profile.lastActive;
    } else {
        lastActiveMillis = new Date(profile.lastActive).getTime();
    }
    
    if (isNaN(lastActiveMillis) || lastActiveMillis === 0) return 'Never';
    
    const now = Date.now();
    const diff = now - lastActiveMillis;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};

// --- Constants ---
const VIEWER_EMAILS = ['swapnalim.w@gmail.com', 'gunesh.p@gmail.com', 'chiranjivimk001@gmail.com'];

// Image compression helper moved to utils/pdfExport.js


const PROFILE_COMPLETION_FIELDS = [
    'name', 'lastName', 'gender', 'status', 'phone', 'country', 'state', 'city',
    'dob', 'height', 'religion', 'caste', 'motherTongue', 'education', 'employmentType', 'createdBy', 'images'
];

const calculateProfileCompletion = (profile, excludeImages = false) => {
    if (!profile) return 0;

    let requiredFields = [...PROFILE_COMPLETION_FIELDS];
    if (excludeImages) {
        requiredFields = requiredFields.filter(f => f !== 'images');
    }

    const empType = String(profile.employmentType || '').toLowerCase();

    if (empType.includes('job') || empType.includes('government')) {
        requiredFields.push('workPlace', 'designation', 'jobLocation', 'income');
    } else if (empType.includes('business') || empType.includes('व्यवसाय') || empType.includes('self') || empType.includes('स्वयंरोजगार')) {
        requiredFields.push('businessName', 'businessType', 'businessLocation', 'income');
    }

    let completedFields = 0;
    requiredFields.forEach(field => {
        if (field === 'images') {
            if (profile.images && profile.images.length > 0) completedFields++;
        } else if (profile[field] && String(profile[field]).trim() !== '') {
            completedFields++;
        }
    });
    return Math.round((completedFields / requiredFields.length) * 100);
};

const ParticleBackground = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let particles = [];
        let animationFrameId;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 2 + 1;
                this.speedX = Math.random() * 0.5 - 0.25;
                this.speedY = Math.random() * 0.5 - 0.25;
            }
            update() {
                if (this.x > canvas.width || this.x < 0) this.speedX *= -1;
                if (this.y > canvas.height || this.y < 0) this.speedY *= -1;
                this.x += this.speedX;
                this.y += this.speedY;
            }
            draw() {
                ctx.fillStyle = 'rgba(232, 72, 130, 0.15)';
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const initParticles = () => {
            particles = [];
            let numberOfParticles = (canvas.height * canvas.width) / 10000;
            for (let i = 0; i < numberOfParticles; i++) {
                particles.push(new Particle());
            }
        };
        initParticles();

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => { p.update(); p.draw(); });
            animationFrameId = requestAnimationFrame(animate);
        };
        animate();

        window.addEventListener('resize', () => { resizeCanvas(); initParticles(); });
        return () => { cancelAnimationFrame(animationFrameId); window.removeEventListener('resize', resizeCanvas); };
    }, []);
    return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: -1, background: 'transparent' }} />;
};


const ProfileCompletionBanner = ({ isProfileComplete, onUpdate }) => {
    if (isProfileComplete) return null;

    return (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[60] w-full max-w-lg px-4 animate-slide-up">
            <div className="bg-white/95 backdrop-blur-xl border-l-4 border-l-matrimony-500 rounded-2xl p-4 flex items-center justify-between shadow-premium transition-all duration-500 hover:-translate-y-1">
                <div className="flex items-center gap-4 relative z-10">
                    <div className="relative">
                        <div className="p-2 bg-matrimony-50 text-matrimony-600 rounded-full relative shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-bold text-stone-900 text-base tracking-wide font-serif">Action Required</h4>
                        <p className="text-stone-500 text-xs font-medium">Complete your profile to unlock full access.</p>
                    </div>
                </div>
                <button
                    onClick={onUpdate}
                    className="relative z-10 ml-4 px-6 py-2 bg-matrimony-600 hover:bg-matrimony-700 text-white text-sm font-bold rounded-xl shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
                >
                    Update Now
                </button>
            </div>
        </div>
    );
};

const ProfilePictureOverlay = ({ currentUser, showNotification }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsSubmitting(true);
        setUploadProgress(0);

        try {
            // 1. Compress
            let fileToUpload = file;
            try {
                fileToUpload = await compressImage(file, 0.7);
            } catch (err) {
                console.error("Compression failed, using original", err);
            }

            // 4. Upload to Firebase Storage
            const storageRef = ref(storage, `profile_pictures/${currentUser.id}/image_1_${Date.now()}`);
            const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Upload failed:", error);
                    alert("Upload failed. Please try again.");
                    setIsSubmitting(false);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                    // 5. Update Firestore
                    // If images array exists, replace the first one or add to it. 
                    // For simplicity in this overlay, we might just set it as the first image.
                    const currentImages = currentUser.images || [];
                    const newImages = [downloadURL, ...currentImages.slice(1)];
                    // Or if we want to force this as the MAIN image.

                    await updateDoc(doc(db, "profiles", currentUser.id), {
                        images: newImages,
                        isProfilePictureValid: true,
                        profileComplete: false // Maybe keep it false until other details are filled? 
                        // But looking at the prompt "until that all profile should be pending profile",
                        // it seems "Pending" status IS the blocking state.
                    });

                    // Reload or let the real-time listener update the UI
                    window.location.reload();
                }
            );

        } catch (error) {
            console.error("Error in profile picture upload:", error);
            alert("An error occurred. Please try again.");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] bg-stone-900/40 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-white/95 backdrop-blur-xl border border-stone-100 rounded-3xl p-8 max-w-md w-full shadow-soft text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-matrimony-500"></div>
                
                {/* 1. Upload Option (Primary Actions) First */}
                <div className="mt-4 mb-6">
                    <label className={`relative block w-full cursor-pointer border-2 border-dashed border-matrimony-300 hover:border-matrimony-500 bg-matrimony-50/30 hover:bg-matrimony-50/60 transition-all rounded-2xl p-6 shadow-sm hover:shadow-md text-center group ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                        <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            capture="user"
                            onChange={handleImageUpload}
                            disabled={isSubmitting}
                        />
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 bg-matrimony-100 text-matrimony-600 rounded-full flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110">
                                {isSubmitting ? (
                                    <svg className="animate-spin h-8 w-8 text-matrimony-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                            </div>
                            <span className="font-bold text-matrimony-700 text-base">
                                {isSubmitting 
                                    ? (uploadProgress > 0 ? `Uploading ${Math.round(uploadProgress)}%` : 'Processing...') 
                                    : 'Upload Photo'
                                }
                            </span>
                        </div>
                    </label>
                </div>

                {/* 2. Text/Heading Elements Second */}
                <h2 className="text-3xl font-bold text-stone-900 mb-2 font-serif tracking-tight">Make it Memorable</h2>
                <p className="text-stone-600 mb-6 leading-relaxed font-medium">
                    Profiles with clear photos receive <span className="font-bold text-matrimony-600">500% more interest</span>. Upload your best moments.
                </p>

                {/* 3. Auxiliary Actions */}
                <div className="space-y-4">
                    <button
                        type="button"
                        onClick={() => signOut(auth)}
                        className="text-sm font-semibold text-stone-500 hover:text-stone-800 underline decoration-2 decoration-stone-200 hover:decoration-matrimony-500 transition-all focus:outline-none focus:ring-2 focus:ring-stone-200 rounded px-2 py-1"
                    >
                        Sign Out & Try Later
                    </button>
                </div>

                {/* Tips */}
                <div className="mt-6 pt-6 border-t border-stone-100 text-left">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Photo Tips:</p>
                    <ul className="text-xs text-stone-500 space-y-1 font-medium">
                        <li className="flex items-center gap-1">✅ Photo should be clearly visible and well-lit.</li>
                        <li className="flex items-center gap-1">✅ No sunglasses or face masks.</li>
                        <li className="flex items-center gap-1">✅ Use a recent photo for best matches.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

const ProfileCard = ({ profile, onView, onChat, onLike, currentUser, showNotification, onEditProfile, chatRequests = [], onSendChatRequest, onAcceptChatRequest }) => {

    const isProfileComplete = calculateProfileCompletion(currentUser) >= 100;
    const hasImages = currentUser?.images && currentUser.images.length > 0;
    const canBypassCompletion = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor;
    const isPremium = currentUser?.profileComplete;
    const canViewFullDetails = canBypassCompletion || (isPremium && isProfileComplete && hasImages);
    
    // Blur photo only if they have NOT completed their own profile
    const shouldBlurPhoto = !canBypassCompletion && (!isProfileComplete || !hasImages);

    const handleAction = (action) => {
        if (!canBypassCompletion && (!isProfileComplete || !hasImages)) {
            showNotification("Please complete your profile and upload a photo to interact with matches.", "error");
            if (onEditProfile) onEditProfile();
            return;
        }
        action(profile);
    };

    return (
        <div className="glass-card rounded-3xl overflow-hidden relative flex flex-col group h-full cursor-pointer hover:-translate-y-1" onClick={() => handleAction(onView)}>
            {profile.compatibilityScore && canViewFullDetails && (
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md border border-white/50 text-matrimony-600 text-[11px] font-extrabold px-3 py-1.5 rounded-full flex items-center gap-1.5 z-20 shadow-sm" title="AI Compatibility Score">
                    <span>{Math.round(profile.compatibilityScore)}%</span>
                    <span>✨</span>
                </div>
            )}



            {/* Online Status */}
            {canViewFullDetails && <div className={`absolute top-5 left-5 w-3.5 h-3.5 rounded-full border-2 border-white z-20 transition-all duration-300 ${isProfileOnline(profile) ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-stone-300'}`} title={isProfileOnline(profile) ? "Online" : "Offline"}></div>}

            <div className={`relative overflow-hidden bg-stone-100 aspect-[4/4.5] ${shouldBlurPhoto ? 'blur-md select-none' : ''} transition-all duration-300`}>
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent z-10 transition-opacity duration-300 group-hover:opacity-60 pointer-events-none"></div>
                <img
                    src={profile.images?.[0] || 'https://placehold.co/400x400/e2e8f0/94a3b8?text=No+Photo'}
                    alt={profile.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {shouldBlurPhoto && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-950/20 z-20 pointer-events-none">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                    </div>
                )}

                {/* Name Overlay anchored to the bottom of the image container to prevent overlap on buttons */}
                <div className="absolute bottom-0 left-0 w-full p-5 z-20 flex flex-col justify-end pointer-events-none">
                    <h3 className="text-2xl font-bold text-white tracking-tight drop-shadow-md pointer-events-auto">{`${profile.name} ${profile.lastName || ''}`}</h3>
                    {canViewFullDetails ? (
                        <p className="text-white/90 text-sm font-medium mt-0.5 drop-shadow-sm pointer-events-auto">{calculateAge(profile.dob)} yrs • {profile.city}</p>
                    ) : (
                        <p className="text-white/80 text-xs font-medium mt-1 drop-shadow-sm italic flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {calculateProfileCompletion(currentUser) >= 100 ? "Unlock Premium to view details" : "Complete profile to view details"}
                        </p>
                    )}
                </div>
            </div>

            <div className="p-5 flex flex-col flex-grow bg-white z-30 relative">
                {/* Info Pills */}
                {canViewFullDetails && (
                    <div className="flex flex-wrap gap-1.5 mb-5">
                        {/* Professional Info */}
                        {profile.employmentType && (profile.employmentType.toLowerCase().includes('business') || profile.employmentType.toLowerCase().includes('self')) ? (
                            <>
                                {profile.businessName && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 truncate max-w-full"><span className="mr-1">💼</span>{profile.businessName}</span>}
                                {profile.businessLocation && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-slate-50 text-slate-600 border border-slate-100"><span className="mr-1">📍</span>{profile.businessLocation}</span>}
                            </>
                        ) : (
                            <>
                                {profile.designation ? <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 truncate max-w-full"><span className="mr-1">💼</span>{profile.designation}</span> : (profile.profession && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100"><span className="mr-1">💼</span>{profile.profession}</span>)}
                                {profile.workPlace && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-slate-50 text-slate-600 border border-slate-100"><span className="mr-1">🏢</span>{profile.workPlace}</span>}
                            </>
                        )}

                        {/* Financial Info */}
                        {profile.income && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-green-50 text-green-700 border border-green-100"><span className="mr-1">💰</span>{profile.income}</span>}

                        {/* Education & Background */}
                        {profile.education && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-100 truncate max-w-[150px]"><span className="mr-1">🎓</span>{profile.education}</span>}
                        {profile.specificDegree && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 max-w-full"><span className="mr-1">🎓</span><span className="truncate">{profile.specificDegree}</span></span>}
                        {(profile.religion || profile.caste) && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-100">{'🕉️ '} {[profile.religion, profile.caste].filter(Boolean).join(', ')}</span>}
                        {profile.status && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100"><span className="mr-1">💍</span>{profile.status}</span>}
                    </div>
                )}
                {!canViewFullDetails && (
                    <div className="flex flex-wrap gap-2 mb-3 opacity-40 blur-[2px] select-none pointer-events-none">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-50 text-slate-600 border border-slate-100">💼 Software Engineer at Google</span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-50 text-slate-600 border border-slate-100">📍 Bangalore, India</span>
                    </div>
                )}

                {/* Current Address Info */}
                {canViewFullDetails && (
                    <div className="text-[11px] sm:text-xs text-stone-600 mb-3 bg-stone-50 border border-stone-100 rounded-xl p-2.5 flex items-start gap-2 shadow-sm" title={profile.sameAddress ? `${profile.permanentCity || profile.city || ''}, ${profile.permanentState || profile.state || ''} - ${profile.permanentPinCode || profile.pinCode || ''}` : `${profile.currentCity || profile.city || ''}, ${profile.currentState || profile.state || ''} - ${profile.currentPinCode || profile.pinCode || ''}`}>
                        <span className="text-rose-500 text-sm mt-0.5 leading-none">📍</span>
                        <div className="min-w-0 flex-1">
                            <span className="font-bold text-stone-700 block mb-0.5 text-[10px] tracking-wider uppercase">Location</span>
                            <span className="truncate block text-stone-600 leading-tight">
                                {profile.sameAddress 
                                    ? `${profile.permanentCity || profile.city || ''}, ${profile.permanentState || profile.state || ''} - ${profile.permanentPinCode || profile.pinCode || ''}` 
                                    : `${profile.currentCity || profile.city || ''}, ${profile.currentState || profile.state || ''} - ${profile.currentPinCode || profile.pinCode || ''}`
                                }
                            </span>
                        </div>
                    </div>
                )}

                <div className="mt-auto flex gap-2 sm:gap-3">
                    {(() => {
                        const isLiked = currentUser?.likedUsers?.includes(profile.id);
                        return (
                            <button 
                                onClick={(e) => { e.stopPropagation(); handleAction(onLike); }} 
                                className={`flex-[0.7] font-semibold py-3 rounded-xl transition-colors text-sm border flex items-center justify-center gap-1.5 ${isLiked ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700' : 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'}`} 
                                title={isLiked ? "Unlike Profile" : "Like Profile"}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill={isLiked ? "white" : "currentColor"}>
                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                </svg> 
                                <span className="hidden sm:inline">{isLiked ? "Liked" : "Like"}</span>
                            </button>
                        );
                    })()}
                    <button onClick={(e) => { e.stopPropagation(); handleAction(onView); }} className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-700 font-semibold py-3 rounded-xl transition-colors text-sm border border-slate-200">View</button>
                    {!currentUser?.isViewer && (() => {
                        if (!canViewFullDetails) {
                            return (
                                <button onClick={(e) => { e.stopPropagation(); handleAction(onChat); }} className="flex-1 bg-matrimony-600 hover:bg-matrimony-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all text-sm">Unlock</button>
                            );
                        }
                        
                        const request = (chatRequests || []).find(r => r.participants?.includes(profile.id));
                        if (!request) {
                            return (
                                <button onClick={(e) => { e.stopPropagation(); handleAction(() => onSendChatRequest(profile.id)); }} className="flex-1 bg-gradient-to-r from-matrimony-600 to-rose-500 text-white font-semibold py-3 rounded-xl shadow-md transition-all text-sm">Connect</button>
                            );
                        }
                        
                        if (request.status === 'pending') {
                            if (request.senderId === currentUser?.id) {
                                return (
                                    <button disabled onClick={(e) => e.stopPropagation()} className="flex-1 bg-stone-100 text-stone-400 font-semibold py-3 rounded-xl border border-stone-200 text-sm cursor-not-allowed">Pending</button>
                                );
                            } else {
                                const requestId = request.id;
                                return (
                                    <button onClick={(e) => { e.stopPropagation(); handleAction(() => onAcceptChatRequest(requestId)); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all text-sm">Accept</button>
                                );
                            }
                        }
                        
                        return (
                            <button onClick={(e) => { e.stopPropagation(); handleAction(onChat); }} className="flex-1 bg-matrimony-600 hover:bg-matrimony-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all text-sm">Message</button>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

const ProfileCardSkeleton = () => (
    <div className="glass-card rounded-3xl overflow-hidden h-full flex flex-col border border-stone-100 bg-white shadow-sm">
        <div className="w-full aspect-[4/4.5] bg-stone-50 animate-pulse relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-12 animate-shimmer"></div>
        </div>
        <div className="p-5 flex flex-col flex-grow space-y-4">
            <div className="flex gap-2 mb-1">
                <div className="h-6 w-16 bg-stone-100 rounded-md animate-pulse"></div>
                <div className="h-6 w-24 bg-stone-100 rounded-md animate-pulse"></div>
            </div>
            <div className="mt-auto flex gap-3">
                <div className="h-11 flex-1 bg-stone-50 border border-stone-100 rounded-xl animate-pulse"></div>
                <div className="h-11 flex-1 bg-matrimony-100 rounded-xl animate-pulse"></div>
            </div>
        </div>
    </div>
);

// --- State & City Data for Dependent Dropdowns ---
const indianStates = {
    "Andaman and Nicobar Islands": ["Port Blair"],
    "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Rajahmundry", "Tirupati"],
    "Arunachal Pradesh": ["Itanagar", "Tawang", "Naharlagun"],
    "Assam": ["Guwahati", "Dibrugarh", "Silchar", "Jorhat", "Tezpur"],
    "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia"],
    "Chandigarh": ["Chandigarh"],
    "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur", "Korba"],
    "Dadra and Nagar Haveli and Daman and Diu": ["Daman", "Diu", "Silvassa"],
    "Delhi": ["New Delhi", "Delhi"],
    "Goa": ["Panaji", "Margao", "Vasco da Gama"],
    "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar"],
    "Haryana": ["Gurugram", "Faridabad", "Panipat", "Ambala", "Hisar"],
    "Himachal Pradesh": ["Shimla", "Manali", "Dharamshala", "Solan"],
    "Jammu and Kashmir": ["Srinagar", "Jammu", "Anantnag"],
    "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro"],
    "Karnataka": ["Bengaluru", "Mysuru", "Hubli", "Mangaluru", "Belagavi"],
    "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam"],
    "Ladakh": ["Leh", "Kargil"],
    "Lakshadweep": ["Kavaratti"],
    "Madhya Pradesh": ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain"],
    "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Nashik", "Thane", "Aurangabad", "Solapur", "Sangamner", "Navi Mumbai", "Kalyan-Dombivli", "Vasai-Virar", "Amravati", "Kolhapur", "Akola", "Latur", "Dhule", "Jalgaon", "Ahmednagar", "Satara", "Chandrapur", "Parbhani", "Jalna", "Bhusawal", "Nanded", "Wardha", "Udgir", "Yavatmal"],
    "Manipur": ["Imphal"],
    "Meghalaya": ["Shillong"],
    "Mizoram": ["Aizawl"],
    "Nagaland": ["Kohima", "Dimapur"],
    "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela", "Puri"],
    "Puducherry": ["Puducherry"],
    "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala"],
    "Rajasthan": ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Bikaner"],
    "Sikkim": ["Gangtok"],
    "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem"],
    "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar"],
    "Tripura": ["Agartala"],
    "Uttar Pradesh": ["Lucknow", "Kanpur", "Agra", "Varanasi", "Meerut", "Prayagraj"],
    "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee", "Nainital"],
    "West Bengal": ["Kolkata", "Asansol", "Siliguri", "Durgapur"],
};

// --- Main Page Components ---

// --- Data Structure for the new Conversational Signup ---
const signupQuestions = {
    1: [ // Basic Information
        {
            key: 'createdBy', label: "First, who is creating this profile? / प्रथम, हे प्रोफाइल कोण तयार करत आहे?", subtext: "This helps others understand who they're talking to. / यामुळे इतरांना समजण्यास मदत होईल.", type: 'select', fullWidth: true, options: [
                { value: 'Self', label: 'Self (स्वतः)' },
                { value: 'Relative', label: 'Relative (नातेवाईक)' },
                { value: 'Mother', label: 'Mother (आई)' },
                { value: 'Father', label: 'Father (वडील)' },
                { value: 'Sister', label: 'Sister (बहीण)' },
                { value: 'Brother', label: 'Brother (भाऊ)' },
                { value: 'Friend', label: 'Friend (मित्र)' }
            ]
        },
        { key: 'creatorName', label: "What is your name? (Creator) / तुमचे नाव काय आहे? (निर्माता)", condition: (data) => !data.createdBy || !data.createdBy.startsWith('Self'), subtext: "Since you are creating this profile for someone else. / कारण तुम्ही हे प्रोफाइल दुसऱ्या कुणासाठी तरी बनवत आहात.", type: 'text', inputType: 'text', placeholder: "Your Name" },
        { key: 'creatorPhone', label: "What is your phone number? (Creator) / तुमचा फोन नंबर काय आहे?", condition: (data) => !data.createdBy || !data.createdBy.startsWith('Self'), subtext: "For us to contact you if needed. / आवश्यक असल्यास संपर्क साधण्यासाठी.", type: 'tel', inputType: 'tel', placeholder: "Your Mobile Number" },
        { key: 'name', label: "Candidate's First Name / उमेदवाराचे पहिले नाव", subtext: "Primary display name. / मुख्य नाव.", type: 'text', inputType: 'text', placeholder: "e.g., Rohan" },
        { key: 'middleName', label: "Father's/Middle Name / वडिलांचे नाव", required: false, subtext: "Optional but good. / ऐच्छिक.", type: 'text', inputType: 'text', placeholder: "e.g., Kumar" },
        { key: 'lastName', label: "Last Name / आडनाव", subtext: "Family name. / आडनाव.", type: 'text', inputType: 'text', placeholder: "e.g., Sharma" },
        { key: 'gender', label: "Gender / लिंग", type: 'select', options: [{ value: 'male', label: 'Male (पुरुष)' }, { value: 'female', label: 'Female (स्त्री)' }] },
        {
            key: 'status', label: "Marital Status / वैवाहिक स्थिती", type: 'select', options: [
                { value: 'Unmarried', label: 'Unmarried (अविवाहित)' },
                { value: 'Divorced', label: 'Divorced (घटस्फोटित)' },
                { value: 'Widowed', label: 'Widowed (विधवा/विधुर)' }
            ]
        },
    ],
    2: [ // Contact & Location
        { key: 'phone', label: "Mobile Number / मोबाईल नंबर", subtext: "Kept private by default. / सुरक्षेसाठी खाजगी.", type: 'tel', inputType: 'tel', fullWidth: true, placeholder: "10-digit number" },
        { key: 'sec_permanent', label: "Permanent Address Details / कायमचा पत्ता तपशील", type: 'section_header', fullWidth: true, required: false },
        { key: 'permanentState', label: "Permanent State / कायमचे राज्य", type: 'text', inputType: 'text', placeholder: "Select State" },
        { key: 'permanentCity', label: "Permanent City / कायमचे शहर", type: 'text', inputType: 'text', placeholder: "Select City" },
        { key: 'permanentAddress', label: "Permanent Address / कायमचा पत्ता", subtext: "Where you reside permanently. / कायमस्वरूपी वास्तव्याचे ठिकाण.", type: 'text', inputType: 'text', fullWidth: true, placeholder: "Area, Landmark, Village, etc." },
        { key: 'permanentPinCode', label: "Permanent Pin Code / कायमचा पिन कोड", type: 'text', inputType: 'text', placeholder: "e.g., 411001" },
        { key: 'sameAddress', label: "My permanent and current address is same / सध्याचा आणि कायमचा पत्ता एकच आहे", type: 'checkbox', fullWidth: true },
        { key: 'sec_current', label: "Current Address Details / सध्याचा पत्ता तपशील", condition: (data) => !data.sameAddress, type: 'section_header', fullWidth: true, required: false },
        { key: 'currentState', label: "Current State / सध्याचे राज्य", condition: (data) => !data.sameAddress, type: 'text', inputType: 'text', placeholder: "Select State" },
        { key: 'currentCity', label: "Current City / सध्याचे शहर", condition: (data) => !data.sameAddress, type: 'text', inputType: 'text', placeholder: "Select City" },
        { key: 'currentAddress', label: "Current Address / सध्याचा पत्ता", condition: (data) => !data.sameAddress, subtext: "Where you live currently. / सध्याचे वास्तव्याचे ठिकाण.", type: 'text', inputType: 'text', fullWidth: true, placeholder: "Area, Landmark, City, etc." },
        { key: 'currentPinCode', label: "Current Pin Code / सध्याचा पिन कोड", condition: (data) => !data.sameAddress, type: 'text', inputType: 'text', placeholder: "e.g., 411001" },
    ],
    3: [ // Personal & Religious
        { key: 'dob', label: "Date of Birth / जन्म तारीख", type: 'date', inputType: 'date' },
        { key: 'birthTime', label: "Time of Birth / जन्म वेळ", type: 'custom_time' },
        { key: 'birthPlace', label: "Birth Place / जन्म ठिकाण", type: 'text', inputType: 'text', placeholder: "City" },
        { key: 'height', label: "Height / उंची", type: 'text', inputType: 'text', placeholder: "e.g., 5.8" },
        {
            key: 'religion', label: "Religion / धर्म", type: 'select', options: [
                { value: 'Hindu', label: 'Hindu (हिंदू)' },
                { value: 'Sikh', label: 'Sikh (शीख)' },
                { value: 'Christian', label: 'Christian (ख्रिश्चन)' },
                { value: 'Buddhist', label: 'Buddhist (बौद्ध)' },
                { value: 'Jain', label: 'Jain (जैन)' },
                { value: 'Parsi', label: 'Parsi (पारशी)' },
                { value: 'Other', label: 'Other (इतर)' }
            ]
        },
        {
            key: 'caste', label: "Caste / जात", type: 'select', options: [
                { value: 'Brahmin', label: 'Brahmin (ब्राम्हण)' },
                { value: 'Kshatriya', label: 'Kshatriya (क्षत्रिय)' },
                { value: 'Vaishya', label: 'Vaishya (वैश्य)' },
                { value: 'Shudra', label: 'Shudra (शूद्र)' },
                { value: 'Maratha', label: 'Maratha (मराठा)' },
                { value: 'Jat', label: 'Jat (जाट)' },
                { value: 'Reddy', label: 'Reddy (रेड्डी)' },
                { value: 'Kamma', label: 'Kamma (कम्मा)' },
                { value: 'Other', label: 'Other (इतर)' }
            ]
        },
        { key: 'gotra', label: "Gotra / गोत्र", required: false, type: 'text', inputType: 'text', placeholder: "(Optional)" },
        {
            key: 'motherTongue', label: "Mother Tongue / मातृभाषा", type: 'select', options: [
                { value: 'Marathi', label: 'Marathi (मराठी)' },
                { value: 'Hindi', label: 'Hindi (हिंदी)' },
                { value: 'English', label: 'English (इंग्रजी)' },
                { value: 'Bengali', label: 'Bengali (बंगाली)' },
                { value: 'Telugu', label: 'Telugu (तेलुगु)' },
                { value: 'Tamil', label: 'Tamil (तमिळ)' },
                { value: 'Gujarati', label: 'Gujarati (गुजराती)' },
                { value: 'Urdu', label: 'Urdu (उर्दू)' },
                { value: 'Kannada', label: 'Kannada (कानडा)' },
                { value: 'Odia', label: 'Odia (ओडिया)' },
                { value: 'Malayalam', label: 'Malayalam (मल्याळम)' },
                { value: 'Punjabi', label: 'Punjabi (पंजाबी)' },
                { value: 'Other', label: 'Other (इतर)' }
            ]
        },
    ],
    4: [ // Professional & Family
        {
            key: 'education', label: "Education / शिक्षण", type: 'select', fullWidth: true, options: [
                { value: 'High School', label: 'High School (हायस्कूल)' },
                { value: 'Diploma', label: 'Diploma (डिप्लोमा)' },
                { value: 'Bachelors Degree', label: 'Bachelors Degree (बॅचलर पदवी)' },
                { value: 'Masters Degree', label: 'Masters Degree (मास्टर्स पदवी)' },
                { value: 'PhD', label: 'PhD (पीएचडी)' },
                { value: 'Other', label: 'Other (इतर)' }
            ]
        },
        { key: 'specificDegree', label: "Degree Name / पदवीचे नाव", subtext: "e.g. B.Tech, MBA", type: 'text', inputType: 'text', fullWidth: true, placeholder: "Degree Name" },
        {
            key: 'employmentType', label: "Employment / व्यवसाय", type: 'select', fullWidth: true, options: [
                { value: 'Private Job', label: 'Private Job (खाजगी नोकरी)' },
                { value: 'Government Job', label: 'Government Job (सरकारी नोकरी)' },
                { value: 'Business', label: 'Business (व्यवसाय)' },
                { value: 'Self Employed', label: 'Self Employed (स्वयंरोजगार)' },
                { value: 'Not Working', label: 'Not Working (काम करत नाही)' }
            ]
        },
        { key: 'profession', label: "Profession / व्यवसाय", condition: (data) => data.employmentType !== 'Not Working', type: 'text', inputType: 'text', fullWidth: true, placeholder: "Profession" },
        { key: 'workPlace', label: "Company Name / कंपनीचे नाव", condition: (data) => data.employmentType !== 'Not Working', type: 'text', inputType: 'text', placeholder: "Company Name" },
        { key: 'designation', label: "Designation / हुद्दा", condition: (data) => data.employmentType !== 'Not Working', type: 'text', inputType: 'text', placeholder: "Designation" },
        { key: 'jobLocation', label: "Job City / नोकरीचे शहर", condition: (data) => data.employmentType && data.employmentType.includes('Job'), type: 'text', inputType: 'text', placeholder: "City" },
        { key: 'businessName', label: "Business Name / व्यवसायाचे नाव", condition: (data) => { const emp = String(data.employmentType || '').toLowerCase(); return emp.includes('business') || emp.includes('self'); }, type: 'text', inputType: 'text', fullWidth: true, placeholder: "Business Name" },
        { key: 'businessType', label: "Business Type / व्यवसायाचा प्रकार", condition: (data) => { const emp = String(data.employmentType || '').toLowerCase(); return emp.includes('business') || emp.includes('self'); }, type: 'text', inputType: 'text', placeholder: "e.g. Retail, Tech, Freelance" },
        { key: 'businessLocation', label: "Business City / व्यवसायाचे शहर", condition: (data) => { const emp = String(data.employmentType || '').toLowerCase(); return emp.includes('business') || emp.includes('self'); }, type: 'text', inputType: 'text', placeholder: "City" },
        { key: 'income', label: "Annual Income (e.g. 10L) / वार्षिक उत्पन्न", condition: (data) => data.employmentType !== 'Not Working', subtext: "e.g. 10L", type: 'text', inputType: 'text', placeholder: "e.g. 10L" },
        { key: 'hobbies', label: "Hobbies / छंद", subtext: "Comma separated", type: 'text', inputType: 'text', fullWidth: true, placeholder: "Reading, Trekking" },
        { key: 'parentName', label: "Parent's Name / पालकांचे नाव", type: 'text', inputType: 'text', placeholder: "Parent's Name" },
        { key: 'parentPhone', label: "Parent's Mobile / पालकांचा नंबर", type: 'tel', inputType: 'tel', placeholder: "Mobile Number" },
    ],
    5: [ // About & Account
        { key: 'about', label: "About You / स्वतःबद्दल", subtext: "Write a short bio to attract matches.", type: 'textarea', fullWidth: true, required: false, placeholder: "I am a..." },
        { key: 'partnerPreference', label: "Partner Preference / जोडीदाराच्या अपेक्षा", subtext: "What are you looking for?", type: 'textarea', fullWidth: true, required: false, placeholder: "Looking for..." }
    ]
};

const AuthPage = ({ setView, showNotification, currentUser, setCurrentUser }) => {
    const isNative = Capacitor.isNativePlatform();
    const [authMethod, setAuthMethod] = useState('choice'); // 'choice', 'email', 'forgot'
    const [isLoginView, setIsLoginView] = useState(true);
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        profileComplete: false,
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            GoogleAuth.initialize();
        }
    }, []);

    const handleGoogleSignIn = async () => {
        setError('');
        setIsProcessing(true);
        try {
            let user;
            if (Capacitor.isNativePlatform()) {
                const googleUser = await GoogleAuth.signIn();
                const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
                const result = await signInWithCredential(auth, credential);
                user = result.user;
            } else {
                const provider = new GoogleAuthProvider();
                const result = await signInWithPopup(auth, provider);
                user = result.user;
            }

            const profileRef = doc(db, "profiles", user.uid);
            const profileSnap = await getDoc(profileRef);
            const currentPlatform = Capacitor.isNativePlatform() ? 'App' : 'Web';

            if (!profileSnap.exists()) {
                const newProfile = {
                    id: user.uid,
                    name: '',
                    email: user.email,
                    phone: user.phoneNumber || '',
                    profileComplete: false,
                    isProfilePictureValid: false,
                    createdAt: serverTimestamp(),
                    platform: currentPlatform
                };
                await setDoc(profileRef, newProfile);
                setCurrentUser(newProfile);
                setView('matches');
            } else {
                const updates = { platform: currentPlatform };
                const data = profileSnap.data();
                if (!data.profileComplete) updates.name = '';

                await updateDoc(profileRef, updates);
                setCurrentUser({ ...data, ...updates });
                setView('matches');
            }
        } catch (err) {
            console.error("Google Sign In Error:", err);
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleAuth = async (e, isLoginSubmit = false) => {
        e.preventDefault();
        setError('');
        setIsProcessing(true);

        try {
            if (isLoginView && isLoginSubmit) { // Login
                const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
                if (!userCredential.user.emailVerified) {
                    await signOut(auth);
                    setError("Please verify your email before logging in.");
                    setIsProcessing(false);
                    return;
                }
                await updateDoc(doc(db, "profiles", userCredential.user.uid), {
                    platform: Capacitor.isNativePlatform() ? 'App' : 'Web'
                });
            } else if (!isLoginView && !isLoginSubmit) { // Signup
                const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
                const user = userCredential.user;
                const newUserProfile = {
                    id: user.uid,
                    name: formData.name,
                    email: formData.email,
                    profileComplete: false,
                    createdAt: serverTimestamp(),
                    platform: Capacitor.isNativePlatform() ? 'App' : 'Web',
                    createdBy: 'Self',
                    isProfilePictureValid: false
                };
                await setDoc(doc(db, "profiles", user.uid), newUserProfile);
                await sendEmailVerification(user);
                await signOut(auth);
                alert("Account created! Please verify your email.");
                setIsLoginView(true);
                setAuthMethod('email');
            }
        } catch (err) {
            console.error("Auth Error:", err);
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError("Invalid email or password.");
            } else if (err.code === 'auth/email-already-in-use') {
                setError("Email is already registered.");
            } else {
                setError(err.message);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!formData.email) { setError("Please enter your email."); return; }
        setIsProcessing(true);
        try {
            await sendPasswordResetEmail(auth, formData.email);
            alert("Password reset link sent to your email.");
            setAuthMethod('email'); // Return to login
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };



    const NewContentSection = () => (
        <>
            {/* --- Stats Section --- */}
            <div className="bg-white border-y border-stone-100 py-16 shadow-sm">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-stone-100">
                        <div className="py-4 md:py-0">
                            <h3 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-matrimony-600 to-matrimony-400 font-serif">9+</h3>
                            <p className="text-stone-500 mt-2 font-medium uppercase tracking-wider text-sm">Years of Experience</p>
                        </div>
                        <div className="py-4 md:py-0">
                            <h3 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-matrimony-600 to-matrimony-400 font-serif">500+</h3>
                            <p className="text-stone-500 mt-2 font-medium uppercase tracking-wider text-sm">Happy Clients</p>
                        </div>
                        <div className="py-4 md:py-0">
                            <h3 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-matrimony-600 to-matrimony-400 font-serif">100+</h3>
                            <p className="text-stone-500 mt-2 font-medium uppercase tracking-wider text-sm">Successful Unions</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Our Philosophy Section --- */}
            <div className="py-20 md:py-32 bg-stone-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-matrimony-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                <div className="container mx-auto px-6 max-w-5xl relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-matrimony-600 font-semibold tracking-wider uppercase text-sm mb-4 block">Our Philosophy</span>
                        <h2 className="text-4xl md:text-5xl font-bold text-stone-800 font-serif">The Heart of MyLuckySpouse</h2>
                        <p className="text-xl text-stone-500 mt-6 max-w-2xl mx-auto">Understanding Marriage as a Lifelong, Social Commitment</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-stone-600 text-lg leading-relaxed">
                        <div className="bg-white p-10 rounded-3xl shadow-soft border border-stone-100 relative group overflow-hidden">
                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-matrimony-50 rounded-full group-hover:scale-150 transition-transform duration-700 ease-out z-0"></div>
                            <div className="relative z-10">
                                <div className="w-12 h-12 bg-matrimony-50 text-matrimony-600 rounded-xl flex items-center justify-center mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                </div>
                                <p>We see the development of culture and civilization in the world in the order of the individual, family, society, and nation. The foundation of the vital family institution is "Marriage".</p>
                            </div>
                        </div>
                        <div className="bg-white p-10 rounded-3xl shadow-soft border border-stone-100 relative group overflow-hidden">
                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-matrimony-50 rounded-full group-hover:scale-150 transition-transform duration-700 ease-out z-0"></div>
                            <div className="relative z-10">
                                <div className="w-12 h-12 bg-matrimony-50 text-matrimony-600 rounded-xl flex items-center justify-center mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                </div>
                                <p>We emphasize the importance of social commitment, strengthening not only personal bonds but also the community ties that support a lasting relationship for generations to come.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Call to Action Section --- */}
            <div className="bg-matrimony-600 py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-stone-900/10 mix-blend-overlay"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-matrimony-400 rounded-full blur-3xl opacity-30 -translate-y-1/2 translate-x-1/3"></div>
                <div className="container mx-auto px-6 text-center relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold text-white font-serif tracking-tight">Ready for Your Forever?</h2>
                    <p className="text-matrimony-100 my-8 max-w-2xl mx-auto text-lg/relaxed font-medium">Start your journey towards a beautiful, fulfilling marriage today.</p>
                    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-white text-matrimony-700 font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-lg tracking-wide">
                        Create Your Free Profile
                    </button>
                </div>
            </div>
        </>
    );

    const ContactSection = () => (
        <div className="py-20 md:py-32 bg-white relative">
            <div className="container mx-auto px-6 text-center relative z-10">
                <span className="text-matrimony-600 font-semibold tracking-wider uppercase text-sm mb-4 block">Get In Touch</span>
                <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4 font-serif">Connect with Us</h2>
                <p className="text-xl text-stone-500 mb-16 max-w-2xl mx-auto">We’d love to hear from you today. Reach out for any assistance!</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left max-w-5xl mx-auto">
                    <div className="bg-stone-50 p-8 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-white text-matrimony-600 rounded-full flex items-center justify-center mb-6 shadow-sm">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-2">Phone</h3>
                        <p className="text-stone-600 font-medium">09175654627</p>
                    </div>
                    <div className="bg-stone-50 p-8 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-white text-matrimony-600 rounded-full flex items-center justify-center mb-6 shadow-sm">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-2">Address</h3>
                        <p className="text-stone-600 font-medium leading-relaxed">Gurumantra I Media, Jaihind Bhawan, Maharaj Galli, Parner.</p>
                    </div>
                    <div className="bg-stone-50 p-8 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform">
                        <div className="w-12 h-12 bg-white text-matrimony-600 rounded-full flex items-center justify-center mb-6 shadow-sm">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-2">Email</h3>
                        <p className="text-stone-600 font-medium text-sm break-all">support@myluckyspouse.com</p>
                    </div>
                </div>
            </div>
        </div>
    );

    // --- Notification Logic ---
    const setupNativeNotifications = async () => {
        console.log("Running Native Notification Logic");
        try {
            const permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'prompt') {
                const newStatus = await PushNotifications.requestPermissions();
                if (newStatus.receive !== 'granted') {
                    throw new Error('User denied permissions!');
                }
            }
            if (permStatus.receive !== 'granted') {
                throw new Error('User denied permissions!');
            }

            // Also check LocalNotifications permission for the fallback
            const localPerm = await LocalNotifications.checkPermissions();
            if (localPerm.display === 'prompt') {
                await LocalNotifications.requestPermissions();
            }

            // Create Channel for Android 8+
            // Using a specific ID to ensure fresh creation with correct importance
            await PushNotifications.createChannel({
                id: 'fcm_default_channel_v1',
                name: 'MyLuckySpouse Notifications',
                description: 'General Notifications',
                importance: 5, // High: Makes sound and peeks
                visibility: 1, // Public
                vibration: true,
                sound: 'default' // Explicitly request default system sound
            });

            await PushNotifications.register();

            PushNotifications.addListener('registration', async (t) => {
                console.log('Push Registration Token: ', t.value);
                await updateDoc(doc(db, "profiles", auth.currentUser.uid), {
                    fcmToken: t.value,
                    platform: 'App'
                });
            });

            PushNotifications.addListener('registrationError', (error) => {
                console.error('Error on registration: ', error);
            });

            PushNotifications.addListener('pushNotificationReceived', async (notification) => {
                console.log('Push received: ', notification);
                showNotification(notification.title || 'New Message', "success");

                // Manual Playback (kept for redundancy)
                const audio = new Audio(notificationSoundFile);
                audio.play().catch(e => console.log("Audio play error", e));

                // --- FALLBACK: Manually trigger System Tray Notification ---
                // This guarantees a tray item even if the OS suppresses the Push in foreground.
                try {
                    await LocalNotifications.schedule({
                        notifications: [{
                            title: notification.title || "New Message",
                            body: notification.body || "You have a new match!",
                            id: new Date().getTime(), // Unique ID
                            schedule: { at: new Date(Date.now() + 100) }, // Trigger immediately
                            sound: 'default',
                            channelId: 'fcm_default_channel_v1', // Use the immutable channel
                            actionTypeId: "",
                            extra: notification.data
                        }]
                    });
                } catch (lnError) {
                    console.error("LocalNotification Fallback Error:", lnError);
                }
            });

            PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                // Handle action if needed
            });

        } catch (error) {
            console.error("Native setup error:", error);
        }
    };

    const setupWebNotifications = async () => {
        console.log("Running Web Notification Logic");

        // 1. Availability Check
        if (typeof window === 'undefined' || !('Notification' in window)) {
            console.warn("This browser does not support desktop notification");
            return;
        }

        // 2. Safety Check for API existence
        if (typeof Notification.requestPermission !== 'function') {
            console.warn("Notification.requestPermission is NOT available. Likely a WebView.");
            return;
        }

        console.log("Requesting permission for notifications...");

        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                console.log("Permission denied for notifications");
                return;
            }

            if (!messaging) {
                console.error("Messaging not initialized");
                return;
            }

            try {
                const token = await getToken(messaging, {
                    vapidKey: "BMQ_h_s-R1cM1o6gR0c5G6u2v7kL8p9h0j3n5b7v4c1x2z3a4s5d6f7"
                });

                if (!token) {
                    console.log("No registration token available");
                    return;
                }

                console.log("Web Push Token:", token);

                await updateDoc(doc(db, "profiles", auth.currentUser.uid), {
                    fcmToken: token,
                    platform: 'Web'
                });

                onMessage(messaging, (payload) => {
                    console.log('Message received. ', payload);
                    showNotification(payload.notification.title, "success");
                    const audio = new Audio(notificationSoundFile);
                    audio.play().catch(e => console.error("Audio play error:", e));
                });

            } catch (error) {
                console.error("Error retrieving token:", error);
            }
        } catch (err) {
            console.error("Notification permission error:", err);
        }
    };


    const InfoSection = () => (
        <div className="py-20 md:py-32 bg-stone-50">
            <div className="container mx-auto px-6 text-center">
                <span className="text-matrimony-600 font-semibold tracking-wider uppercase text-sm mb-4 block">Features</span>
                <h2 className="text-4xl md:text-5xl font-bold text-stone-900 font-serif mb-16">Why Choose MyLuckySpouse?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                    <div className="bg-white p-10 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform text-center flex flex-col items-center">
                        <div className="w-20 h-20 bg-matrimony-50 text-matrimony-600 rounded-full flex items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-4">Verified Profiles</h3>
                        <p className="text-stone-500 leading-relaxed">Every profile is carefully reviewed by our team to ensure a safe and genuine community for everyone.</p>
                    </div>
                    <div className="bg-white p-10 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform text-center flex flex-col items-center relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gold-50 rounded-full blur-2xl opacity-60"></div>
                        <div className="w-20 h-20 bg-matrimony-50 text-matrimony-600 rounded-full flex items-center justify-center mb-6 relative z-10">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-4 relative z-10">Smart AI Matching</h3>
                        <p className="text-stone-500 leading-relaxed relative z-10">Our advanced AI helps you find the most compatible matches based on your preferences, values, and personality.</p>
                    </div>
                    <div className="bg-white p-10 rounded-3xl shadow-soft border border-stone-100 hover:-translate-y-1 transition-transform text-center flex flex-col items-center">
                        <div className="w-20 h-20 bg-matrimony-50 text-matrimony-600 rounded-full flex items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6.364-3.636l-1.414 1.414M21 12h-2M4 12H2m15.364-6.364l-1.414-1.414M6.364 6.364L4.95 4.95M12 3v2m0 4a2 2 0 100 4 2 2 0 000-4z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 mb-4">Private & Secure</h3>
                        <p className="text-stone-500 leading-relaxed">You control what you share. Connect privately and securely with your matches through our platform.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const HowItWorksSection = () => (
        <div className="py-20 md:py-32 bg-white">
            <div className="container mx-auto px-6 text-center">
                <span className="text-matrimony-600 font-semibold tracking-wider uppercase text-sm mb-4 block">Process</span>
                <h2 className="text-4xl md:text-5xl font-bold text-stone-900 font-serif mb-16">Start Your Journey in 3 Simple Steps</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto relative cursor-default">
                    {/* Connecting line for desktop */}
                    <div className="hidden md:block absolute top-[60px] left-[15%] right-[15%] h-px bg-stone-200 z-0"></div>

                    <div className="relative z-10 w-full group">
                        <div className="w-24 h-24 mx-auto bg-stone-50 border-4 border-white text-5xl font-serif italic font-bold text-matrimony-300 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:bg-matrimony-50 group-hover:text-matrimony-600 transition-colors">1</div>
                        <h3 className="text-2xl font-bold text-stone-900 mb-3">Create Profile</h3>
                        <p className="text-stone-500 max-w-xs mx-auto">Sign up in seconds and build a profile that truly represents your values.</p>
                    </div>
                    <div className="relative z-10 w-full group">
                        <div className="w-24 h-24 mx-auto bg-white border-4 border-white text-5xl font-serif italic font-bold text-matrimony-300 rounded-full flex items-center justify-center mb-6 shadow-premium shadow-matrimony-500/10 group-hover:bg-matrimony-50 group-hover:text-matrimony-600 transition-colors">2</div>
                        <h3 className="text-2xl font-bold text-stone-900 mb-3">Discover Matches</h3>
                        <p className="text-stone-500 max-w-xs mx-auto">Browse verified profiles or let our AI find the perfect partner for you.</p>
                    </div>
                    <div className="relative z-10 w-full group">
                        <div className="w-24 h-24 mx-auto bg-stone-50 border-4 border-white text-5xl font-serif italic font-bold text-matrimony-300 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:bg-matrimony-50 group-hover:text-matrimony-600 transition-colors">3</div>
                        <h3 className="text-2xl font-bold text-stone-900 mb-3">Start Connecting</h3>
                        <p className="text-stone-500 max-w-xs mx-auto">Begin your conversation and take the first step towards a happy future.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const SuccessStoriesSection = () => (
        <div className="py-20 md:py-32 bg-stone-50">
            <div className="container mx-auto px-6 text-center">
                <span className="text-matrimony-600 font-semibold tracking-wider uppercase text-sm mb-4 block">Testimonials</span>
                <h2 className="text-4xl md:text-5xl font-bold text-stone-900 font-serif mb-16">Our Success Stories</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {successStories.slice(0, 3).map(story => (
                        <div key={story.id} className="bg-white rounded-3xl shadow-soft border border-stone-100 p-8 flex flex-col group hover:-translate-y-1 transition-transform cursor-pointer">
                            <div className="overflow-hidden rounded-2xl mb-6 shadow-sm">
                                <img src={story.image} alt={story.names} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
                            </div>
                            <h3 className="text-2xl font-bold text-stone-900 mb-3 font-serif">{story.names}</h3>
                            <div className="flex justify-center gap-1 mb-4 text-gold-400">
                                {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>)}
                            </div>
                            <p className="text-stone-500 italic mt-auto">"{story.story}"</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // State for Floating Card visibility
    const [showGoogleCard, setShowGoogleCard] = useState(true);

    return (
        <div className="min-h-screen bg-stone-50 font-sans text-stone-800">
            {/* SPLIT SCREEN LAYOUT */}
            <div className="flex flex-col md:flex-row min-h-screen">
                {/* LEFT: Hero Image (Golden Hour) */}
                <div className="relative w-full md:w-1/2 h-[40vh] md:h-screen overflow-hidden order-1 md:order-1">
                    <div className="absolute inset-0 bg-stone-900/10 z-10"></div>
                    <img src="/golden-hour.png" alt="Golden Hour Wedding" className="w-full h-full object-cover animate-scale-subtle" />

                    {/* Brand Overlay (Mobile Only) */}
                    <div className="absolute top-6 left-6 z-20 md:hidden">
                        <img src="/logo-v3.png" alt="Logo" className="h-10 w-auto drop-shadow-md" />
                    </div>

                    {/* Quote (Desktop Only) */}
                    <div className="hidden md:flex absolute bottom-0 left-0 w-full p-16 z-20 bg-gradient-to-t from-stone-900/80 to-transparent flex-col justify-end h-1/2">
                        <h2 className="text-4xl lg:text-5xl font-serif text-white leading-tight drop-shadow-lg mb-4">
                            "Soulmates aren't found. <br />
                            <span className="italic text-rose-200">They're recognized."</span>
                        </h2>
                        <p className="text-white/80 text-lg">Join India's most trusted matchmaking platform.</p>
                    </div>
                </div>

                {/* RIGHT: Login Form (Clean & Minimal) */}
                <div className="w-full md:w-1/2 flex flex-col order-2 md:order-2 bg-white -mt-8 md:mt-0 rounded-t-[2rem] md:rounded-none z-30 shadow-2xl md:shadow-none min-h-[60vh] md:min-h-screen">

                    {/* Desktop Header/Nav */}
                    <div className="hidden md:flex justify-between items-center p-8">
                        <img src="/logo-v3.png" alt="Logo" className="h-10 w-auto" />
                        <div className="text-sm font-medium text-stone-500">
                            {isLoginView ? "Don't have an account?" : "Already explicitly matched?"}
                            <button
                                onClick={() => setIsLoginView(!isLoginView)}
                                className="ml-2 text-rose-600 hover:text-rose-700 font-bold transition-colors"
                            >
                                {isLoginView ? "Register Now" : "Sign In"}
                            </button>
                        </div>
                    </div>

                    {/* Form Container */}
                    <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-24 py-12 md:py-0">

                        <div className="max-w-md mx-auto w-full">
                            <div className="text-center md:text-left mb-10 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                                <h3 className="text-3xl md:text-4xl font-serif text-stone-900 mb-2">
                                    {authMethod === 'forgot' ? "Reset Password" : (isLoginView ? "Welcome Back" : "Begin Your Journey")}
                                </h3>
                                <p className="text-stone-500 text-lg">
                                    {authMethod === 'forgot' ? "Enter your email to receive a reset link." : (isLoginView ? "Sign in to meet your perfect match." : "Create a profile in seconds.")}
                                </p>
                            </div>

                            {/* Error Alert */}
                            {error && (
                                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-100 flex items-start gap-3 text-red-600 text-sm animate-fade-in-up" style={{ animationFillMode: 'both' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                    {error}
                                </div>
                            )}

                            {/* GOOGLE BUTTON (Primary) */}
                            {authMethod === 'choice' && (
                                <div className="space-y-6">
                                    <button
                                        onClick={handleGoogleSignIn}
                                        disabled={isProcessing}
                                        className="w-full py-4 bg-white border border-stone-200 text-stone-700 font-bold rounded-2xl hover:bg-stone-50 hover:border-stone-300 shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-3 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-200 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
                                    >
                                        {isProcessing ? (
                                            <span className="w-5 h-5 border-2 border-stone-300 border-t-matrimony-500 rounded-full animate-spin"></span>
                                        ) : (
                                            <>
                                                <svg className="w-6 h-6 transition-transform group-hover:scale-110" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                                                <span className="text-lg">Continue with Google</span>
                                            </>
                                        )}
                                    </button>

                                    <div className="relative flex py-2 items-center animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                                        <div className="flex-grow border-t border-stone-200"></div>
                                        <span className="flex-shrink-0 mx-4 text-stone-400 text-sm font-serif italic">or continue with email</span>
                                        <div className="flex-grow border-t border-stone-200"></div>
                                    </div>

                                    <button
                                        onClick={() => setAuthMethod('email')}
                                        className="w-full py-4 bg-matrimony-600 hover:bg-matrimony-700 text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-matrimony-500 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}
                                    >
                                        Log in with Email
                                    </button>

                                    <a href="/myluckyspouse.apk" download className="w-full py-4 bg-stone-800 hover:bg-stone-900 text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-800 animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
                                        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24"><path d="M17.523 15.3414C17.523 15.3414 17.564 15.3414 17.564 15.3414C17.564 15.3414 17.564 15.3414 17.523 15.3414ZM6.649 15.3414C6.608 15.3414 6.649 15.3414 6.649 15.3414C6.649 15.3414 6.649 15.3414 6.649 15.3414ZM12.086 10.9994C9.539 10.9994 7.309 12.8094 6.649 15.3414L3.659 15.3414C4.319 11.2094 8.049 8.0894 12.086 8.0894C16.123 8.0894 19.853 11.2094 20.513 15.3414L17.523 15.3414C16.863 12.8094 14.633 10.9994 12.086 10.9994ZM16.586 6.5594L18.436 3.0194C18.575 2.7664 18.47 2.4404 18.239 2.3164C18.008 2.1924 17.701 2.2984 17.575 2.5514L15.688 6.1604C14.593 5.5894 13.382 5.2594 12.086 5.2594C10.79 5.2594 9.579 5.5894 8.484 6.1604L6.597 2.5514C6.471 2.2984 6.164 2.1924 5.933 2.3164C5.702 2.4404 5.597 2.7664 5.736 3.0194L7.586 6.5594C3.899 8.3594 1.371 11.8994 0.941 16.2094L0.908 16.2094L0.908 18.4894L23.264 18.4894L23.264 16.2094L23.232 16.2094C22.801 11.8994 20.273 8.3594 16.586 6.5594ZM7.586 13.7594C6.983 13.7594 6.496 13.2724 6.496 12.6694C6.496 12.0664 6.983 11.5794 7.586 11.5794C8.189 11.5794 8.676 12.0664 8.676 12.6694C8.676 13.2724 8.189 13.7594 7.586 13.7594ZM16.586 13.7594C15.983 13.7594 15.496 13.2724 15.496 12.6694C15.496 12.0664 15.983 11.5794 16.586 11.5794C17.189 11.5794 17.676 12.0664 17.676 12.6694C17.676 13.2724 17.189 13.7594 16.586 13.7594Z" /></svg>
                                        Download Android App
                                    </a>
                                </div>
                            )}

                            {/* EMAIL FORM */}
                            {authMethod === 'email' && (
                                <form onSubmit={(e) => handleAuth(e, isLoginView)} className="space-y-5 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                                    {!isLoginView && (
                                        <input type="text" name="name" placeholder="Your Full Name" value={formData.name} onChange={handleInputChange} className="w-full px-5 py-4 bg-stone-50 border border-stone-200 rounded-2xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-matrimony-500/50 focus:border-matrimony-500 transition-all font-medium shadow-sm" required={!isLoginView} />
                                    )}
                                    <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleInputChange} className="w-full px-5 py-4 bg-stone-50 border border-stone-200 rounded-2xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-matrimony-500/50 focus:border-matrimony-500 transition-all font-medium shadow-sm" required />
                                    <div className="relative">
                                        <input type="password" name="password" placeholder="Password" value={formData.password} onChange={handleInputChange} className="w-full px-5 py-4 bg-stone-50 border border-stone-200 rounded-2xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-matrimony-500/50 focus:border-matrimony-500 transition-all font-medium shadow-sm" required />
                                    </div>

                                    {isLoginView && (
                                        <div className="flex justify-end">
                                            <button type="button" onClick={() => setAuthMethod('forgot')} className="text-sm font-medium text-rose-500 hover:text-rose-600 transition-colors">Forgot Password?</button>
                                        </div>
                                    )}

                                    <button type="submit" disabled={isProcessing} className="w-full py-4 mt-4 rounded-xl bg-stone-900 hover:bg-black text-white font-bold text-lg shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all transform disabled:opacity-70 disabled:cursor-not-allowed">
                                        {isProcessing ? 'Processing...' : (isLoginView ? 'Sign In Securely' : 'Create Free Account')}
                                    </button>

                                    <button type="button" onClick={() => setAuthMethod('choice')} className="w-full mt-4 text-stone-400 hover:text-stone-600 text-sm font-medium transition-colors">Cancel</button>
                                </form>
                            )}

                            {/* FORGOT PASSWORD */}
                            {authMethod === 'forgot' && (
                                <form onSubmit={handlePasswordReset} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                                    <input type="email" name="email" placeholder="Enter your registered email" value={formData.email} onChange={handleInputChange} className="w-full px-5 py-4 bg-stone-50 border border-stone-200 rounded-2xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 transition-all font-medium" required />
                                    <button type="submit" disabled={isProcessing} className="w-full py-4 rounded-xl bg-stone-900 hover:bg-black text-white font-bold text-lg shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all transform">
                                        {isProcessing ? "Sending Link..." : "Send Reset Link"}
                                    </button>
                                    <button type="button" onClick={() => setAuthMethod('email')} className="w-full py-3 text-stone-400 hover:text-stone-600 text-sm font-medium">Back to Login</button>
                                </form>
                            )}

                            {/* Mobile Toggle Footer */}
                            <div className="mt-8 text-center md:hidden animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                                <p className="text-stone-500 text-sm">
                                    {isLoginView ? "New here?" : "Already a member?"}
                                    <button onClick={() => setIsLoginView(!isLoginView)} className="ml-1 font-bold text-rose-600 tracking-wide uppercase text-xs">
                                        {isLoginView ? "Create Account" : "Log In"}
                                    </button>
                                </p>
                            </div>

                        </div>
                    </div>

                    {/* Desktop Footer (Copyright) */}
                    <div className="hidden md:block p-8 text-center text-xs text-stone-400">
                        &copy; 2026 MyLuckySpouse. Crafted with &hearts; for India.
                    </div>
                </div>

            </div>

            {/* RESTORED CONTENT SECTION (Below Fold) */}
            <div className="relative z-10 bg-stone-900 border-t border-stone-800 shadow-xl">
                <NewContentSection />
            </div>


        </div>
    );
};



const MatchesPage = ({ profiles, setView, setSelectedProfile, openChat, onLike, isLoading, currentUser, showNotification, scrollY, setScrollY, filter, setFilter, onEditProfile, chatRequests = [], onSendChatRequest, onAcceptChatRequest }) => { // const [adminGenderFilter, setAdminGenderFilter] = useState(currentUser?.isViewer ? 'female' : 'all'); // Removed local state
    const [searchQuery, setSearchQuery] = useState('');

    // Scroll Persistence - Restore immediately before paint to prevent layout bounce
    React.useLayoutEffect(() => {
        if (scrollY > 0) {
            window.scrollTo(0, scrollY);
            // Fallback for slower rendering components
            const timer = setTimeout(() => window.scrollTo(0, scrollY), 50);
            return () => clearTimeout(timer);
        }
    }, [scrollY]);

    // Save scroll position on unmount - using layout effect to run synchronously before DOM mutations shrink the page height
    React.useLayoutEffect(() => {
        return () => {
            const currentScroll = window.scrollY;
            if (currentScroll > 0) {
                setScrollY(currentScroll);
            }
        };
    }, []);

    useEffect(() => {
        if (currentUser?.isViewer && filter === 'all') {
            setFilter('female');
        }
    }, [currentUser, filter]);

    const recordProfileView = async (targetUserId) => {
        // Profile view tracking disabled per user request
    };

    const filteredProfiles = profiles.filter(p => {
        if (!currentUser || p.id === currentUser.id) return false; // Don't show self

        // Mutual Blocking Filter - REMOVED to keep profiles visible as requested

        // Smart Search Logic
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase().trim();
            const searchableText = [
                p.name, p.lastName, p.city, p.state, p.profession, p.education,
                p.businessName, p.businessType, p.about, p.hobbies, p.id, p.email, p.phone
            ].filter(Boolean).join(' ').toLowerCase();

            if (!searchableText.includes(query)) return false;
        }

        // Admin/Viewer/Editor Logic
        if (currentUser.isAdmin || currentUser.isViewer || currentUser.isEditor) {
            // Viewers see ONLY Verified profiles
            if (currentUser.isViewer && !p.profileComplete) return false;
            // Viewers should NOT see Admins (resolves duplicate display if one is admin)
            if (currentUser.isViewer && p.isAdmin) return false;

            if (filter === 'all') {
                // Safety Check: If Viewers somehow have 'all' selected, default to 'female' (or hide all)
                if (currentUser.isViewer) return p.gender === 'female';
                return true;
            }

            // Exception for specific user "Gauri Dixit" - always show if gender matches
            const isGauri = p.name && p.lastName &&
                ((p.name.toLowerCase() === 'gauri' && p.lastName.toLowerCase() === 'dixit') ||
                (p.name.toLowerCase().includes('gauri') && p.name.toLowerCase().includes('dixit')));

            if (isGauri) {
                return p.gender === filter;
            }

            // If filtering by gender, also apply strict filters to see what users see
            if (!p.profileComplete) return false;
            if (!p.images || p.images.length === 0) return false;
            if (p.name && (p.name.includes('.') || p.name.includes(','))) return false;

            return p.gender === filter;
        }

        // Regular User Logic
        if (p.isAdmin || p.isEditor) return false; // Don't show admins/editors to regular users
        if (p.isMarried) return false; // Don't show married profiles to regular users

        // Exception for specific user "Gauri Dixit" - always show if gender matches
        const isGauri = p.name && p.lastName &&
            ((p.name.toLowerCase() === 'gauri' && p.lastName.toLowerCase() === 'dixit') ||
            (p.name.toLowerCase().includes('gauri') && p.name.toLowerCase().includes('dixit')));

        if (isGauri) {
            if (currentUser.gender === 'male') return p.gender === 'female';
            if (currentUser.gender === 'female') return p.gender === 'male';
            return true;
        }

        // if (!p.profileComplete) return false; // Show even if incomplete per user request

        // Strict filtering: Must have at least one image
        if (!p.images || p.images.length === 0) return false;

        // Strict filtering: Name must not contain dots or commas (fake profile check)
        if (p.name && (p.name.includes('.') || p.name.includes(','))) return false;

        let passGenderCheck = true;
        if (currentUser.gender === 'male') passGenderCheck = p.gender === 'female';
        else if (currentUser.gender === 'female') passGenderCheck = p.gender === 'male';

        if (!passGenderCheck) return false;

        return true;
    });

    const sortedProfiles = [...filteredProfiles].sort((a, b) => {
        const aOnline = isProfileOnline(a);
        const bOnline = isProfileOnline(b);
        
        if (aOnline && !bOnline) return -1;
        if (!aOnline && bOnline) return 1;
        
        // If both are online or both are offline, sort by lastActive (newest first)
        const getActiveTime = (p) => {
            if (!p || !p.lastActive) return 0;
            if (typeof p.lastActive.toMillis === 'function') return p.lastActive.toMillis();
            if (p.lastActive instanceof Date) return p.lastActive.getTime();
            if (typeof p.lastActive === 'number') return p.lastActive;
            return new Date(p.lastActive).getTime() || 0;
        };
        
        const aTime = getActiveTime(a);
        const bTime = getActiveTime(b);
        
        return bTime - aTime;
    });

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8 bg-stone-50 md:bg-transparent rounded-none min-h-screen relative overflow-hidden">
            <ProfileCompletionBanner isProfileComplete={calculateProfileCompletion(currentUser) >= 100} onUpdate={onEditProfile} />
            <div className="text-center mb-12 relative z-10 pt-4 md:pt-8 mt-10">

                {/* Admin/Viewer Filter Controls */}
                {(currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor) && (
                    <div className="mt-8 flex flex-wrap justify-center items-center gap-4 bg-white p-4 rounded-2xl shadow-sm border border-stone-100 max-w-fit mx-auto">
                        <span className="text-stone-500 font-bold uppercase tracking-wider text-xs">{(currentUser.isAdmin || currentUser.isEditor) ? 'Admin View:' : 'Filter View:'}</span>
                        <div className="flex gap-2 bg-stone-100 p-1 rounded-full">
                            {(currentUser.isViewer ? ['male', 'female'] : ['all', 'male', 'female']).map(tab => (
                                <button key={tab} onClick={() => setFilter(tab)} className={`px-6 py-2 rounded-full font-bold transition-all text-sm ${filter === tab ? 'bg-white text-matrimony-600 shadow-sm' : 'text-stone-500 hover:text-stone-800'}`}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>
                            ))}
                        </div>
                        {/* Sync Button */}
                        {currentUser.isAdmin && (
                            <button onClick={async () => {
                                if (!confirm("Sync ALL profiles to Google Sheet? This will overwrite existing sheet data.")) return;
                                try {
                                    showNotification("Syncing started...", "info");
                                    const { httpsCallable } = await import('firebase/functions');
                                    const syncFn = httpsCallable(functions, 'syncAllProfilesToSheet');
                                    const res = await syncFn();
                                    showNotification(res.data.message || "Sync Complete!", "success");
                                } catch (e) {
                                    console.error(e);
                                    showNotification("Sync Failed: " + e.message, "error");
                                }
                            }} className="bg-emerald-50 text-emerald-600 border border-emerald-200 px-5 py-2 rounded-full font-bold hover:bg-emerald-600 hover:text-white transition-colors shadow-sm text-sm flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                Sync Sheets
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Smart Search Bar (Visible to All) */}
            <div className="relative z-10 max-w-xl mx-auto mb-10 px-4 mt-8">
                <div className="relative flex items-center group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400 group-focus-within:text-matrimony-500 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    </div>
                    <input
                        type="text"
                        placeholder="Smart Search (Name, City, Profession, Education...)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-11 pr-12 py-3.5 bg-white border border-stone-200 rounded-full text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 shadow-sm transition-all text-sm md:text-base"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center text-stone-400 hover:text-stone-600 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 relative z-10">
                {isLoading ? Array.from({ length: 12 }).map((_, index) => <ProfileCardSkeleton key={index} />) : sortedProfiles.map(p => (
                    <ProfileCard 
                        key={p.id} 
                        profile={p} 
                        onView={(profile) => { 
                            const isPremiumOrStaff = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor || currentUser?.profileComplete; 
                            if (!isPremiumOrStaff) { 
                                setShowPaywallModal(true); 
                                showNotification("Unlock Premium to view detailed profiles.", "info"); 
                                return; 
                            } 
                            setScrollY(window.scrollY); 
                            setSelectedProfile(profile); 
                            setView('detailed-profile'); 
                            recordProfileView(profile.id); 
                        }} 
                        onChat={openChat} 
                        onLike={onLike} 
                        currentUser={currentUser} 
                        showNotification={showNotification} 
                        onEditProfile={onEditProfile} 
                        chatRequests={chatRequests} 
                        onSendChatRequest={onSendChatRequest} 
                        onAcceptChatRequest={onAcceptChatRequest} 
                    />
                ))}
            </div>
        </div>
    );
};

const AIMatchesPage = ({ profiles, setView, setSelectedProfile, openChat, currentUser, showNotification }) => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedMatchId, setExpandedMatchId] = useState(null);

    useEffect(() => {
        const fetchMatches = async () => {
            setLoading(true);
            try {
                const getAiMatches = httpsCallable(functions, 'getaimatches');
                const result = await getAiMatches();
                if (result.data && Array.isArray(result.data.matches)) {
                    setMatches(result.data.matches);
                } else {
                    setMatches([]);
                }
            } catch (e) {
                console.error("Error fetching AI matches:", e);
                showNotification(e.message || "Failed to fetch AI matches.", "error");
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchMatches();
        }
    }, [currentUser]);

    const toggleExpand = (id) => {
        setExpandedMatchId(expandedMatchId === id ? null : id);
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8 min-h-screen pb-24">
            {/* Header Area */}
            <div className="text-center mb-12 pt-4 md:pt-8 mt-10 animate-fade-in">
                <span className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-100 text-rose-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
                    ✨ Neural Network Matchmaker
                </span>
                <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 font-serif drop-shadow-sm flex items-center justify-center gap-2">
                    AI Matchmaking <span className="text-matrimony-600">Preferences</span>
                </h1>
                <p className="mt-4 text-base text-stone-500 font-medium max-w-2xl mx-auto leading-relaxed">
                    Our AI has analyzed your registration profile, Gotra lineage, caste community, mother tongue, career, and family expectations to compute compatibility suitable to Poornawad philosophy and traditional Indian matrimonial standards.
                </p>
            </div>

            {loading ? (
                /* Loading State Screen */
                <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                    <div className="relative w-20 h-20 mb-6">
                        <div className="absolute inset-0 border-4 border-rose-100 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-matrimony-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-stone-600 font-bold text-lg">AI Matchmaker is analyzing profiles...</p>
                    <p className="text-stone-400 text-xs mt-1 font-medium">Evaluating Poornawad & Gotra, caste community, mother tongue, career, and physical preferences</p>
                </div>
            ) : matches.length === 0 ? (
                /* Empty Matches Screen */
                <div className="text-center py-16 px-6 bg-white border border-stone-150 rounded-3xl shadow-sm max-w-2xl mx-auto relative overflow-hidden animate-scale-up">
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-50/20 to-indigo-50/20 opacity-50"></div>
                    <div className="relative z-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-50 border border-stone-100 rounded-2xl mb-6 shadow-sm text-stone-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-stone-800 font-serif mb-2">No AI Matches Found Yet</h3>
                        <p className="text-stone-500 text-sm max-w-md mx-auto leading-relaxed">
                            To generate advanced matches, please ensure your profile is verified, complete with clear photos, and has an active embedding initialized.
                        </p>
                    </div>
                </div>
            ) : (
                /* Matches Render Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {matches.map((match) => {
                        const score = match.compatibilityScore || 70;
                        const breakdown = match.matchBreakdown || {};

                        // Determine score badge colors
                        let scoreColor = "bg-rose-50 text-matrimony-600 border-rose-100";
                        if (score < 80) scoreColor = "bg-teal-50 text-teal-700 border-teal-100";
                        else if (score < 90) scoreColor = "bg-indigo-50 text-indigo-700 border-indigo-100";

                        return (
                            <div
                                key={match.id}
                                className={`bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md hover:border-stone-300 transition-all duration-300 transform hover:-translate-y-1 relative ${expandedMatchId === match.id ? 'ring-2 ring-matrimony-500/20' : ''}`}
                            >
                                <div className="p-6">
                                    {/* Image & Main Info Row */}
                                    <div className="flex gap-4 items-start">
                                        <div className="relative flex-shrink-0">
                                            <img
                                                src={match.images?.[0] || 'https://placehold.co/150x150/f5f5f4/a8a29e?text=No+Pic'}
                                                alt={match.name}
                                                onClick={() => {
                                                    setSelectedProfile(match);
                                                    setView('profile');
                                                }}
                                                className="w-20 h-20 rounded-2xl object-cover shadow-sm ring-2 ring-stone-100 hover:scale-105 transition-all cursor-pointer"
                                            />
                                            {/* Online status indicator */}
                                            {isProfileOnline(match) && (
                                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-sm animate-pulse"></span>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex justify-between items-start gap-2">
                                                <h3
                                                    onClick={() => {
                                                        setSelectedProfile(match);
                                                        setView('profile');
                                                    }}
                                                    className="font-extrabold text-stone-900 text-lg hover:text-matrimony-600 transition-colors truncate cursor-pointer leading-tight"
                                                >
                                                    {match.name} {match.lastName}
                                                </h3>
                                            </div>
                                            <p className="text-xs font-semibold text-stone-500 mt-1 uppercase tracking-wider">{match.profession || 'Professional'}</p>
                                            <p className="text-xs text-stone-400 font-medium mt-0.5">{calculateAge(match.dob) || match.age || 'N/A'} yrs • {match.currentState || match.city || 'India'}</p>
                                        </div>
                                    </div>

                                    {/* Compatibility Score circular block */}
                                    <div className="bg-stone-50 rounded-2xl p-4 mt-5 flex justify-between items-center border border-stone-100">
                                        <div className="text-left">
                                            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block">Match Rating</span>
                                            <span className="text-stone-600 font-bold text-xs mt-0.5">High Compatibility</span>
                                        </div>
                                        <span className={`px-3 py-1.5 rounded-xl text-sm font-black border tracking-wide select-none ${scoreColor}`}>
                                            🔥 {score}% Match
                                        </span>
                                    </div>
                                </div>

                                {/* Card footer actions */}
                                <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex gap-2 justify-stretch items-center">
                                    <button
                                        onClick={() => openChat(match)}
                                        className="bg-gradient-to-r from-matrimony-600 to-rose-600 hover:from-matrimony-700 hover:to-rose-700 text-white font-extrabold px-3 py-2.5 rounded-xl shadow-md text-xs transition-all flex items-center justify-center gap-1.5 w-full cursor-pointer"
                                    >
                                        <span>💬</span>
                                        <span>Connect Now</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ProfileFormModal = ({ isOpen, onClose, allowClose, currentUser, showNotification, mode = 'edit', setView, initialStep = 1 }) => {
    if (!isOpen) return null;

    const [step, setStep] = useState(initialStep);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([0, 0, 0]);
    const [imageFiles, setImageFiles] = useState([null, null, null]);
    const [imagePreviews, setImagePreviews] = useState(currentUser?.images || [null, null, null]);
    const firstInputRef = useRef(null);
    const contentRef = useRef(null); // Ref for scrollable content

    // Initialize form data
    const [formData, setFormData] = useState({
        createdBy: 'Self', creatorName: '', creatorPhone: '',
        name: currentUser?.name || '', middleName: '', lastName: '', gender: 'male', status: '',
        phone: '', phoneVisibility: 'Private', country: 'India',
        permanentState: '', permanentCity: '', permanentAddress: '', permanentPinCode: '',
        sameAddress: false,
        currentState: '', currentCity: '', currentAddress: '', currentPinCode: '',
        state: '', city: '', streetAddress: '', pinCode: '',
        dob: '', birthTime: '', birthPlace: '', height: '', religion: '', caste: '', gotra: '',
        education: '', specificDegree: '', employmentType: '', profession: '', workPlace: '', designation: '',
        income: '', motherTongue: '', hobbies: '',
        parentName: '', parentPhone: '', about: '', partnerPreference: '',
        ...currentUser,
        manualCity: '', manualReligion: '', manualCaste: '', manualEducation: '',
    });

    // --- Persistence Logic: Loading moved to isOpen effect ---

    // Save on change
    useEffect(() => {
        if (currentUser && !currentUser.profileComplete) {
            localStorage.setItem(`profile_draft_${currentUser.id}`, JSON.stringify(formData));
            localStorage.setItem(`profile_step_${currentUser.id}`, step);
        }
    }, [formData, step, currentUser]);

    const clearDraft = () => {
        if (currentUser) {
            localStorage.removeItem(`profile_draft_${currentUser.id}`);
            localStorage.removeItem(`profile_step_${currentUser.id}`);
        }
    };

    const activeQuestions = signupQuestions;

    useEffect(() => {
        if (isOpen && currentUser) {
            let finalData = {
                ...currentUser,
                country: currentUser.country || 'India',
                state: currentUser.state || '',
                city: currentUser.city || '',
                religion: currentUser.religion || '',
                caste: currentUser.caste || '',
                motherTongue: currentUser.motherTongue || '',
                education: currentUser.education || '',
                employmentType: currentUser.employmentType || '',
                manualCity: '', manualReligion: '', manualCaste: '', manualEducation: ''
            };

            let startStep = initialStep;
            let missingFieldKey = null;

            // Helper function to validate a field (copied from validateField)
            const validateInitialField = (key, value) => {
                if (value === undefined || value === null) return false;
                const strVal = String(value).trim();
                if (strVal === '' || strVal.toLowerCase().startsWith('select')) return false;
                if (key === 'birthTime') return /^\d{1,2}:\d{2}\s(AM|PM)$/i.test(strVal);
                if (strVal.match(/:\s*(AM|PM)/i) || strVal.match(/^:/)) return /^\d{1,2}:\d{2}\s(AM|PM)$/i.test(strVal);
                return true;
            };

            // 1. Merge Draft Data
            if (!currentUser.profileComplete) {
                const savedData = localStorage.getItem(`profile_draft_${currentUser.id}`);
                if (savedData) {
                    try {
                        const parsed = JSON.parse(savedData);
                        finalData = { ...finalData, ...parsed };
                    } catch (e) {
                        console.error("Error parsing draft", e);
                    }
                }
            }

            // 2. Validate strict fields and transform manual "Other" values BEFORE calculating missing fields
            const normalizeField = (key, options) => {
                if (finalData[key] && options) {
                    const matchedOption = options.find(opt => {
                        const val = typeof opt === 'object' ? opt.value : opt;
                        return String(val).toLowerCase() === String(finalData[key]).toLowerCase();
                    });
                    if (matchedOption) {
                        finalData[key] = typeof matchedOption === 'object' ? matchedOption.value : matchedOption;
                        return true;
                    }
                }
                return false;
            };

            const checkAndSetOther = (key, options) => {
                if (finalData[key] && options) {
                    const isMatched = normalizeField(key, options);

                    let isCityOption = false;
                    if (key === 'city' && indianStates[finalData.state]) {
                        isCityOption = indianStates[finalData.state].includes(finalData.city);
                    }

                    if (!isMatched && key !== 'city' && finalData[key] !== 'Other') {
                        finalData[`manual${key}`] = finalData[key];
                        finalData[key] = 'Other';
                    } else if (key === 'city' && !isCityOption && finalData.state && finalData.city && finalData.city !== 'Other') {
                        finalData.manualCity = finalData.city;
                        finalData.city = 'Other';
                    }
                }
            };

            Object.values(signupQuestions).flat().forEach(q => {
                // Handle strict fields (Status, Gender, Mother Tongue) -> Clear if invalid/legacy
                if (['status', 'gender', 'motherTongue'].includes(q.key) && finalData[q.key]) {
                    const isMatched = normalizeField(q.key, q.options);
                    if (!isMatched) {
                        // Legacy handling for single
                        if (q.key === 'status' && String(finalData.status).toLowerCase() === 'single') {
                            finalData.status = 'Unmarried';
                        } else {
                            finalData[q.key] = '';
                        }
                    }
                }

                // Handle fields that support "Other" -> Move to Manual
                if (['religion', 'caste', 'education', 'city'].includes(q.key)) {
                    checkAndSetOther(q.key, q.options);
                }
            });

            // 3. Calculate starting step based on missing data
            let foundIncompleteStep = false;
            for (let i = 1; i <= Object.keys(activeQuestions).length; i++) {
                const currentQs = activeQuestions[i];
                for (const q of currentQs) {
                    if (q.condition && !q.condition(finalData)) continue;

                    if (q.required !== false && !validateInitialField(q.key, finalData[q.key])) {
                        startStep = i;
                        missingFieldKey = q.key;
                        foundIncompleteStep = true;
                        break;
                    }

                    if (finalData[q.key] === 'Other' && !validateInitialField(`manual${q.key}`, finalData[`manual${q.key}`])) {
                        startStep = i;
                        missingFieldKey = `manual${q.key}`;
                        foundIncompleteStep = true;
                        break;
                    }
                }
                if (foundIncompleteStep) break;
            }

            setFormData(prev => ({ ...prev, ...finalData }));
            setStep(startStep);
            setImagePreviews(currentUser.images || [null, null, null]);

            // Auto-scroll to the missing field once rendering is done
            if (missingFieldKey) {
                setTimeout(() => {
                    const fieldElement = document.getElementById(`field-container-${missingFieldKey}`);
                    if (fieldElement && contentRef.current) {
                        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            }
        }
    }, [isOpen, currentUser?.id, initialStep]);

    // Auto-scroll to top on error
    useEffect(() => {
        if (error && contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error]);

    // Auto-focus first input on step change
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, [step]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            let formattedValue = value;
            if (name === 'phone' || name === 'parentPhone' || name === 'creatorPhone') {
                let phoneVal = value.replace(/[^\d+]/g, '');
                if (phoneVal.startsWith('+')) {
                    phoneVal = '+' + phoneVal.slice(1).replace(/\+/g, '');
                } else {
                    phoneVal = phoneVal.replace(/\+/g, '');
                }
                formattedValue = phoneVal.slice(0, 15);
            } else if (name === 'height') {
                formattedValue = value.replace(/[^0-9.]/g, '');
            }
            const newData = { ...prev, [name]: formattedValue };
            if (name === 'createdBy' && String(value).startsWith('Self')) { newData.creatorName = ''; newData.creatorPhone = ''; }
            if (name === 'employmentType') { newData.workPlace = ''; newData.designation = ''; newData.jobLocation = ''; newData.businessName = ''; newData.businessType = ''; newData.profession = ''; }
            
            // Sync permanent/current address fields
            if (newData.sameAddress) {
                if (name === 'permanentState') { newData.currentState = formattedValue; newData.state = formattedValue; }
                if (name === 'permanentCity') { newData.currentCity = formattedValue; newData.city = formattedValue; }
                if (name === 'permanentAddress') { newData.currentAddress = formattedValue; newData.streetAddress = formattedValue; }
                if (name === 'permanentPinCode') { newData.currentPinCode = formattedValue; newData.pinCode = formattedValue; }
            } else {
                if (name === 'currentState') newData.state = formattedValue;
                if (name === 'currentCity') newData.city = formattedValue;
                if (name === 'currentAddress') newData.streetAddress = formattedValue;
                if (name === 'currentPinCode') newData.pinCode = formattedValue;
            }
            return newData;
        });
    };

    const handleImageSelect = async (e, index) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressedFile = await compressImage(file, 0.7);
                const newFiles = [...imageFiles]; newFiles[index] = compressedFile; setImageFiles(newFiles);
                const newPreviews = [...imagePreviews]; newPreviews[index] = URL.createObjectURL(compressedFile); setImagePreviews(newPreviews);
            } catch (err) {
                console.error("Image processing error:", err);
                // Fallback for compression error
                console.warn("Compression failed, using original");
                const newFiles = [...imageFiles]; newFiles[index] = file; setImageFiles(newFiles);
                const newPreviews = [...imagePreviews]; newPreviews[index] = URL.createObjectURL(file); setImagePreviews(newPreviews);
            }
        }
    };

    const clearAllPhotos = () => {
        setImageFiles([null, null, null]);
        setImagePreviews([null, null, null]);
        setError("All photos cleared. Please upload at least one photo to continue.");
    };

    const clearIndividualPhoto = (index, e) => {
        e.preventDefault(); // Prevent label click
        e.stopPropagation();

        const newFiles = [...imageFiles];
        newFiles[index] = null;
        setImageFiles(newFiles);

        const newPreviews = [...imagePreviews];
        newPreviews[index] = null;
        setImagePreviews(newPreviews);

        showNotification("Photo removed.", "success");
    };

    const uploadImages = async () => {
        const uploadedUrls = [...(currentUser.images || [])];
        while (uploadedUrls.length < 3) uploadedUrls.push(null);
        const uploadPromises = imageFiles.map(async (file, index) => {
            if (!file) return null;
            try {
                const storageRef = ref(storage, `profile_images/${currentUser.id}/${Date.now()}_${index}`);
                const uploadTask = uploadBytesResumable(storageRef, file);
                return new Promise((resolve) => {
                    uploadTask.on('state_changed',
                        (snapshot) => { const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100; setUploadProgress(prev => { const newP = [...prev]; newP[index] = progress; return newP; }); },
                        (error) => { console.error(error); resolve(null); },
                        async () => { try { const url = await getDownloadURL(uploadTask.snapshot.ref); uploadedUrls[index] = url; resolve(url); } catch (e) { resolve(null); } }
                    );
                });
            } catch (err) { return null; }
        });
        await Promise.all(uploadPromises);
        return uploadedUrls.filter(url => url !== null);
    };

    const handleProfileComplete = async (e) => {
        if (e) e.preventDefault();
        setIsUploading(true);
        setError('');
        setSuccessMsg('');
        try {
            const images = await uploadImages();

            // STRICT VALIDATION: Must have at least one image
            if (!images || images.length === 0) {
                setError("A profile photo is required to continue.");
                setIsUploading(false);
                return;
            }

            const dataToSave = { ...formData };

            // Consolidate manual inputs
            Object.keys(dataToSave).forEach(key => {
                if (dataToSave[key] === 'Other' && dataToSave[`manual${key}`]) {
                    dataToSave[key] = dataToSave[`manual${key}`];
                }
            });

            // Clean up temporary manual fields before saving
            const finalData = Object.fromEntries(
                Object.entries(dataToSave).filter(([key]) => !key.startsWith('manual'))
            );

            finalData.images = images;
            // We no longer auto-verify. profileComplete is strictly controlled by Admins.
            if (calculateProfileCompletion(finalData) >= 100 && !currentUser.profileComplete) {
                finalData.isUnderReview = true;
            }

            await updateDoc(doc(db, "profiles", currentUser.id), finalData);
            clearDraft(); // Clear local storage draft

            const msg = finalData.profileComplete ? "Registration Successful!" : "Profile Updated Successfully!";
            setSuccessMsg(msg);

            // Delay closing so the user can see the success message
            setTimeout(() => {
                showNotification(msg, "success");
                if (finalData.profileComplete && setView) setView('matches');
                onClose();
            }, 1500);
        } catch (err) { setError("Failed to update profile"); }
        finally { setIsUploading(false); }
    };

    const validateField = (key, value) => {
        if (value === undefined || value === null) return false;
        const strVal = String(value).trim();
        if (strVal === '' || strVal.toLowerCase().startsWith('select')) return false;

        // Strict validation for Time of Birth
        if (key === 'birthTime') {
            return /^\d{1,2}:\d{2}\s(AM|PM)$/i.test(strVal);
        }

        // Specific validation for manual time entries or other time-like strings
        if (strVal.match(/:\s*(AM|PM)/i) || strVal.match(/^:/)) {
            return /^\d{1,2}:\d{2}\s(AM|PM)$/i.test(strVal);
        }

        return true;
    };

    const nextStep = () => {
        const currentQs = activeQuestions[step];
        for (const q of currentQs) {
            if (q.condition && !q.condition(formData)) continue;

            // Check main field
            if (q.required !== false && !validateField(q.key, formData[q.key])) {
                setError(`Please fill in: ${q.label.split('/')[0]}`);
                return;
            }

            // Check manual input if "Other" is selected
            if (formData[q.key] === 'Other' && !validateField(`manual${q.key}`, formData[`manual${q.key}`])) {
                setError(`Please specify your ${q.label.split('/')[0]}`);
                return;
            }
        }

        if (currentQs.some(q => q.key === 'dob')) {
            const age = calculateAge(formData.dob);
            if (formData.gender === 'male' && age < 21) { setError("Must be at least 21 years old."); return; }
            if (formData.gender === 'female' && age < 18) { setError("Must be at least 18 years old."); return; }
        }

        // --- SERVER-SIDE PERSISTENCE (Partial Save) ---
        // Save the fields from the current step to Firestore immediately
        const stepData = {};
        currentQs.forEach(q => {
            // Save main field
            if (formData[q.key] !== undefined) stepData[q.key] = formData[q.key];
            // Save Manual field if applicable
            if (formData[q.key] === 'Other' && formData[`manual${q.key}`]) {
                stepData[`manual${q.key}`] = formData[`manual${q.key}`];
            }
        });

        // Always update last active step
        stepData.lastStep = step + 1;

        // Fire and forget update (don't block UI)
        if (currentUser && currentUser.id) {
            updateDoc(doc(db, "profiles", currentUser.id), stepData).catch(err => console.error("Partial save failed", err));
        }
        // ----------------------------------------------

        setError('');
        setStep(s => s + 1);
        if (contentRef.current) contentRef.current.scrollTo(0, 0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && e.target.type !== 'textarea') {
            e.preventDefault();
            nextStep();
        }
    };

    const renderField = (q, index) => {
        const isMissing = q.required !== false && !validateField(q.key, formData[q.key]);
        const highlightClass = isMissing ? "border-red-400 bg-red-50/50" : "border-stone-200 focus:border-matrimony-500 focus:bg-white";
        const commonClasses = `w-full bg-stone-50 border px-4 py-3 text-lg md:text-xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-matrimony-500/20 transition-all font-medium rounded-xl ${highlightClass}`;

        const isValid = q.required !== false && validateField(q.key, formData[q.key]);
        const validIcon = isValid ? <span className="absolute right-4 top-4 text-emerald-500 text-xl animate-scale-in">✓</span> : null;
        const missingIcon = isMissing ? <span className="absolute right-4 top-4 text-red-500 text-xl animate-bounce">!</span> : null;

        const inputProps = {
            name: q.key,
            value: formData[q.key] || '',
            onChange: handleInputChange,
            onKeyDown: handleKeyDown,
            className: commonClasses,
            required: q.required !== false,
            ref: index === 0 ? firstInputRef : null // Auto-focus first field
        };

        let fieldInput;
        if ((q.key === 'state' || q.key === 'permanentState' || q.key === 'currentState') && formData.country === 'India') {
            fieldInput = <select {...inputProps} className={`${commonClasses} appearance-none cursor-pointer`}><option value="" className="text-stone-400">Select State</option>{Object.keys(indianStates).map(s => <option key={s} value={s} className="bg-white">{s}</option>)}</select>;
        }
        else if ((q.key === 'city' || q.key === 'permanentCity' || q.key === 'currentCity') && formData.country === 'India') {
            const stateKey = q.key === 'permanentCity' ? 'permanentState' : (q.key === 'currentCity' ? 'currentState' : 'state');
            const selectedState = formData[stateKey];
            if (selectedState && indianStates[selectedState]) {
                fieldInput = <select {...inputProps} className={`${commonClasses} appearance-none cursor-pointer`}><option value="" className="text-stone-400">Select City</option>{indianStates[selectedState].map(c => <option key={c} value={c} className="bg-white">{c}</option>)}<option value="Other" className="text-matrimony-600 font-bold bg-stone-50">Other / Not Listed</option></select>;
            } else {
                fieldInput = <select {...inputProps} disabled className={`${commonClasses} appearance-none bg-stone-150 cursor-not-allowed`}><option value="">Select State First</option></select>;
            }
        }
        else if (q.type === 'checkbox') {
            fieldInput = (
                <div className="flex items-center gap-3 py-2">
                    <input
                        type="checkbox"
                        id={q.key}
                        name={q.key}
                        checked={!!formData[q.key]}
                        onChange={(e) => {
                            const val = e.target.checked;
                            setFormData(prev => {
                                const newData = { ...prev, [q.key]: val };
                                if (val) {
                                    newData.currentState = prev.permanentState || '';
                                    newData.currentCity = prev.permanentCity || '';
                                    newData.currentAddress = prev.permanentAddress || '';
                                    newData.currentPinCode = prev.permanentPinCode || '';
                                    newData.state = prev.permanentState || '';
                                    newData.city = prev.permanentCity || '';
                                    newData.pinCode = prev.permanentPinCode || '';
                                    newData.streetAddress = prev.permanentAddress || '';
                                }
                                return newData;
                            });
                        }}
                        className="w-6 h-6 text-matrimony-600 border-stone-300 rounded focus:ring-matrimony-500 cursor-pointer"
                    />
                    <label htmlFor={q.key} className="text-stone-700 font-semibold cursor-pointer text-lg">{q.label.split('/')[0]}</label>
                </div>
            );
        }
        else if (q.type === 'select') {
            fieldInput = <select {...inputProps} className={`${commonClasses} appearance-none cursor-pointer`}><option value="" className="text-stone-400">Select...</option>{q.options.map((opt, i) => { const val = typeof opt === 'object' ? opt.value : opt; const label = typeof opt === 'object' ? opt.label : opt; return <option key={i} value={val} className="bg-white">{label}</option>; })}</select>;
        }
        else if (q.type === 'custom_time') {
            // Parse existing value or default to empty
            const timeVal = formData[q.key] || "";
            // Regex to parse "hh:mm AM/PM" - LENIENT for partials
            // Matches "5:30 AM", "5: AM", ":30 PM", ": AM"
            const match = timeVal.match(/^(\d{0,2}):(\d{0,2})\s?(AM|PM)$/i);
            const currentHH = match ? match[1] : "";
            const currentMM = match ? match[2] : "";
            const currentMeridian = match ? match[3].toUpperCase() : "AM";

            const handleTimePartChange = (part, val) => {
                let h = currentHH;
                let m = currentMM;
                let mer = currentMeridian;

                if (part === 'hh') h = val;
                if (part === 'mm') m = val;
                if (part === 'meridian') mer = val;

                // Update formData with partial or full string. 
                // We use space between time and meridian.
                const newVal = `${h}:${m} ${mer}`;
                handleInputChange({ target: { name: q.key, value: newVal } });
            };

            fieldInput = (
                <div className="flex gap-2 w-full">
                    <select value={currentHH} onChange={(e) => handleTimePartChange('hh', e.target.value)} className={`${commonClasses} w-1/3 appearance-none cursor-pointer text-center`}>
                        <option value="" disabled>HH</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={String(h).padStart(2, '0')} className="bg-white">{String(h).padStart(2, '0')}</option>)}
                    </select>
                    <span className="text-stone-400 self-center text-xl font-bold">:</span>
                    <select value={currentMM} onChange={(e) => handleTimePartChange('mm', e.target.value)} className={`${commonClasses} w-1/3 appearance-none cursor-pointer text-center`}>
                        <option value="" disabled>MM</option>
                        {Array.from({ length: 60 }, (_, i) => i).map(m => <option key={m} value={String(m).padStart(2, '0')} className="bg-white">{String(m).padStart(2, '0')}</option>)}
                    </select>
                    <select value={currentMeridian} onChange={(e) => handleTimePartChange('meridian', e.target.value)} className={`${commonClasses} w-1/3 appearance-none cursor-pointer text-center md:px-1`}>
                        <option value="AM" className="bg-white">AM</option>
                        <option value="PM" className="bg-white">PM</option>
                    </select>
                </div>
            );
        }
        else if (q.type === 'textarea') {
            fieldInput = <textarea {...inputProps} rows="3" placeholder={q.placeholder} className={`${commonClasses} resize-none`} />;
        } else {
            fieldInput = <input type={q.inputType} {...inputProps} placeholder={q.placeholder} autoComplete="off" />;
        }

        return (
            <div className="relative group">
                {fieldInput}
                {isValid ? validIcon : missingIcon}
            </div>
        );
    };

    const currentQuestions = activeQuestions[step];
    const totalSteps = Object.keys(activeQuestions).length + 1;
    const progress = ((step - 1) / totalSteps) * 100;

    return (
        <div className="fixed inset-0 z-[200] bg-stone-900/60 backdrop-blur-sm text-stone-900 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-stone-200/50">
                {/* Top Bar */}
                <div className="flex justify-between items-center p-5 md:p-6 bg-white border-b border-stone-100 shrink-0 z-50">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center">
                            <span className="text-[10px] md:text-xs font-bold tracking-widest uppercase text-matrimony-500 block">Step {step} of {Object.keys(activeQuestions).length}</span>
                            <h2 className="text-lg md:text-xl font-bold text-stone-800 leading-none ml-2 pt-1">{
                                ['Basic Details', 'Contact Info', 'Personal Info', 'Career & Family', 'About & Preferences'][step - 1] || 'Verification'
                            }</h2>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={async () => {
                                if (window.confirm("Are you sure you want to logout?")) {
                                    try {
                                        await signOut(auth);
                                        window.location.reload();
                                    } catch (e) { console.error("Logout failed", e); }
                                }
                            }}
                            className="px-4 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600 hover:text-stone-900 text-xs font-bold uppercase tracking-wider transition-colors focus:ring-2 focus:ring-stone-200"
                        >
                            Logout
                        </button>
                        {allowClose && (
                            <button onClick={onClose} className="rounded-full bg-stone-100 p-2 md:p-3 hover:bg-stone-200 text-stone-500 hover:text-stone-800 transition-all group focus:ring-2 focus:ring-stone-200">
                                <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Progress Line */}
                <div className="w-full h-1 bg-stone-100">
                    <div className="h-full bg-matrimony-500 transition-all duration-700 ease-out" style={{ width: `${progress}%` }}></div>
                </div>

                {/* Main Content - SMART GRID */}
                <div ref={contentRef} className="flex-1 overflow-y-auto custom-scrollbar bg-stone-50/50">
                    <div className="max-w-4xl mx-auto px-6 py-12">
                        {/* ERROR DISPLAY AT TOP */}
                        {error && (
                            <div className="mb-6 bg-red-50 text-red-600 px-6 py-4 rounded-xl shadow-sm animate-shake flex items-center gap-3 font-medium border border-red-200">
                                <svg className="w-6 h-6 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span>{error}</span>
                            </div>
                        )}
                        {/* SUCCESS DISPLAY AT TOP */}
                        {successMsg && (
                            <div className="mb-6 bg-emerald-50 text-emerald-600 px-6 py-4 rounded-xl shadow-sm animate-fade-in flex items-center gap-3 font-medium border border-emerald-200">
                                <svg className="w-6 h-6 shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                <span>{successMsg}</span>
                            </div>
                        )}

                        {step <= Object.keys(activeQuestions).length ? (
                            <div className="animate-slide-in-up">
                                {/* Missing Info Alert */}
                                {currentQuestions.some(q => q.required !== false && !formData[q.key]) && (
                                    <div className="mb-8 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg flex items-center gap-3 animate-pulse">
                                        <span className="text-2xl">⚠️</span>
                                        <div>
                                            <h4 className="font-bold text-amber-800">Missing Information</h4>
                                            <p className="text-amber-700/80 text-sm">Please fill in the highlighted fields to continue.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                                    {currentQuestions.map((q, idx) => {
                                        if (q.condition && !q.condition(formData)) return null;

                                        if (q.type === 'section_header') {
                                            return (
                                                <div key={q.key} id={`field-container-${q.key}`} className="col-span-1 md:col-span-2 pt-6 pb-2 border-b border-stone-200/50 flex items-center gap-3">
                                                    <div className="w-1.5 h-6 bg-matrimony-500 rounded-full"></div>
                                                    <h3 className="text-lg md:text-xl font-bold text-stone-800 font-serif">{q.label}</h3>
                                                </div>
                                            );
                                        }

                                        const isFullWidth = q.fullWidth || q.type === 'textarea';

                                        return (
                                            <div key={q.key} id={`field-container-${q.key}`} className={`space-y-2 ${isFullWidth ? 'col-span-1 md:col-span-2' : 'col-span-1'}`}>
                                                <label className="block text-sm font-bold text-stone-500 uppercase tracking-wider ml-1">
                                                    {q.label.split('/')[0]}
                                                    {q.required === false && <span className="text-stone-400 text-xs ml-2 normal-case font-normal">(Optional)</span>}
                                                </label>

                                                {renderField(q, idx)}

                                                {/* Manual Inputs - Dynamic for ALL 'Other' selections */}
                                                {formData[q.key] === 'Other' && (
                                                    <div id={`field-container-manual${q.key}`} className="mt-3 animate-fade-in-up">
                                                        <input
                                                            type="text"
                                                            name={`manual${q.key}`}
                                                            value={formData[`manual${q.key}`] || ''}
                                                            onChange={handleInputChange}
                                                            placeholder={`Please specify your ${q.label.split('/')[0]}...`}
                                                            className="w-full bg-transparent border-b-2 border-matrimony-500/50 px-3 py-3 text-lg text-stone-900 placeholder-matrimony-500/50 focus:outline-none focus:border-matrimony-500 transition-all font-medium"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="animate-fade-in text-center py-10">
                                <div className="inline-block p-4 rounded-full bg-matrimony-50 mb-6 animate-bounce-slow">
                                    <svg className="w-16 h-16 text-matrimony-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </div>
                                <h2 className="text-4xl md:text-5xl font-black mb-4 text-stone-900 font-serif">Make it Memorable</h2>
                                <p className="text-xl text-stone-500 mb-2 max-w-xl mx-auto">Profiles with clear photos receive <span className="text-matrimony-600 font-bold">500% more interest</span>. Upload your best moments.</p>
<p className="text-sm text-stone-400 font-medium mb-6">Max size: 5MB per photo</p>

                                <div className="flex justify-center mb-8">
                                    <button type="button" onClick={clearAllPhotos} disabled={!imagePreviews.some(img => img !== null)} className="text-sm font-bold text-matrimony-600 hover:text-matrimony-800 disabled:text-stone-300 disabled:cursor-not-allowed transition-colors bg-matrimony-50 hover:bg-matrimony-100 px-4 py-2 rounded-lg flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        Clear All Photos
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-3xl mx-auto">
                                    {[0, 1, 2].map(index => (
                                        <div key={index} className="relative aspect-square rounded-3xl bg-white border-2 border-dashed border-stone-200 hover:border-matrimony-500 hover:bg-matrimony-50/50 transition-all flex flex-col items-center justify-center overflow-hidden group shadow-sm hover:shadow-md duration-300">
                                            <label className="w-full h-full cursor-pointer flex flex-col items-center justify-center absolute inset-0 z-10">
                                                {imagePreviews[index] ? (
                                                    <img src={imagePreviews[index]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                                ) : (
                                                    <>
                                                        <svg className="w-12 h-12 text-stone-300 mb-3 group-hover:scale-110 group-hover:text-matrimony-400 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                                                        <span className="text-stone-400 font-medium group-hover:text-matrimony-600">Upload Photo</span>
                                                    </>
                                                )}
                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageSelect(e, index)} />
                                                {imagePreviews[index] && <div className="absolute inset-0 bg-white/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity backdrop-blur-sm"><span className="text-matrimony-700 font-bold bg-white px-4 py-2 rounded-full border border-stone-200 shadow-sm">Change</span></div>}
                                            </label>
                                            {imagePreviews[index] && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => clearIndividualPhoto(index, e)}
                                                    className="absolute top-3 right-3 z-20 bg-red-500/90 hover:bg-red-600 text-white p-2 rounded-full shadow-md backdrop-blur-sm transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-500/50 opacity-0 group-hover:opacity-100"
                                                    title="Remove Photo"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            )}
                                            {imagePreviews[index] && index > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const newFiles = [...imageFiles];
                                                        const tempFile = newFiles[0];
                                                        newFiles[0] = newFiles[index];
                                                        newFiles[index] = tempFile;
                                                        setImageFiles(newFiles);

                                                        const newPreviews = [...imagePreviews];
                                                        const tempPreview = newPreviews[0];
                                                        newPreviews[0] = newPreviews[index];
                                                        newPreviews[index] = tempPreview;
                                                        setImagePreviews(newPreviews);
                                                    }}
                                                    className="absolute bottom-3 right-3 z-20 bg-stone-900/70 hover:bg-matrimony-600 text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide rounded-lg shadow-md backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                                                    title="Set as Default Photo"
                                                >
                                                    Set Default
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Bottom Actions */}
                <div className="p-5 md:p-6 border-t border-stone-100 bg-white shrink-0 flex justify-between items-center z-50">
                    <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="text-stone-400 hover:text-stone-700 font-bold uppercase tracking-wider text-sm transition-colors disabled:opacity-0 flex items-center gap-2 group focus:outline-none focus:ring-2 focus:ring-stone-200 rounded-lg px-2 py-1">
                        <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        Back
                    </button>

                    {step <= Object.keys(activeQuestions).length ? (
                        <button onClick={nextStep} className="bg-matrimony-600 text-white px-8 md:px-10 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold text-base md:text-lg hover:-translate-y-1 hover:shadow-lg transition-all flex items-center gap-2 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-matrimony-500">
                            Next Step
                            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </button>
                    ) : (
                        <button onClick={handleProfileComplete} disabled={isUploading || !imagePreviews.some(img => img !== null)} className="bg-matrimony-600 text-white px-8 md:px-12 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold text-base md:text-lg shadow-md hover:shadow-lg hover:-translate-y-1 transition-all disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-matrimony-500">
                            {isUploading ? <><span className="animate-spin text-2xl">⏳</span> Creating...</> : <>Finish & Launch 🚀</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const PROFILE_FIELD_ORDER = [
    'createdBy', 'creatorName', 'creatorPhone', 'name', 'middleName', 'lastName', 'gender', 'status', 'dob', 'birthTime', 'birthPlace',
    'phone', 'country', 'permanentState', 'permanentCity', 'permanentAddress', 'permanentPinCode', 'sameAddress', 'currentState', 'currentCity', 'currentAddress', 'currentPinCode', 'education', 'specificDegree', 'employmentType', 'workPlace',
    'designation', 'jobLocation', 'businessName', 'businessType', 'businessLocation', 'profession', 'income',
    'height', 'religion', 'caste', 'gotra', 'motherTongue', 'hobbies', 'parentName', 'parentPhone', 'about', 'partnerPreference', 'email'
];
const MyProfilePage = ({ currentUser, showNotification, setView, onEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editableUser, setEditableUser] = useState(currentUser);
    const [uploadProgress, setUploadProgress] = useState([0, 0, 0]);
    const [isUploading, setIsUploading] = useState(false);
    const completionPercentage = calculateProfileCompletion(currentUser);

    useEffect(() => { // This useEffect is fine as it only runs when currentUser changes
        setEditableUser(currentUser);
    }, [currentUser]);

    const handleInputChange = (e) => { 
        const { name, value } = e.target; 
        let formattedValue = value;
        if (name === 'phone' || name === 'parentPhone' || name === 'creatorPhone') {
            let phoneVal = value.replace(/[^\d+]/g, '');
            if (phoneVal.startsWith('+')) {
                phoneVal = '+' + phoneVal.slice(1).replace(/\+/g, '');
            } else {
                phoneVal = phoneVal.replace(/\+/g, '');
            }
            formattedValue = phoneVal.slice(0, 15);
        } else if (name === 'height') {
            formattedValue = value.replace(/[^0-9.]/g, '');
        }
        setEditableUser(prev => {
            const newData = { ...prev, [name]: formattedValue };
            
            // Sync permanent/current address fields
            if (newData.sameAddress) {
                if (name === 'permanentState') { newData.currentState = formattedValue; newData.state = formattedValue; }
                if (name === 'permanentCity') { newData.currentCity = formattedValue; newData.city = formattedValue; }
                if (name === 'permanentAddress') { newData.currentAddress = formattedValue; newData.streetAddress = formattedValue; }
                if (name === 'permanentPinCode') { newData.currentPinCode = formattedValue; newData.pinCode = formattedValue; }
            } else {
                if (name === 'currentState') newData.state = formattedValue;
                if (name === 'currentCity') newData.city = formattedValue;
                if (name === 'currentAddress') newData.streetAddress = formattedValue;
                if (name === 'currentPinCode') newData.pinCode = formattedValue;
            }
            return newData;
        }); 
    };

    const handleImageChange = async (e, index) => {
        const file = e.target.files[0];
        if (file && currentUser) {
            setIsUploading(true);
            setUploadProgress(prev => { const newProgress = [...prev]; newProgress[index] = 0; return newProgress; });

            let fileToUpload = file;
            try {
                fileToUpload = await compressImage(file, 0.7);
            } catch (err) {
                console.error("Compression failed, using original", err);
            }

            const storageRef = ref(storage, `profile_pictures/${currentUser.id}/image_${index + 1}_${Date.now()}`);
            const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(prev => {
                        const newProgress = [...prev];
                        newProgress[index] = progress;
                        return newProgress;
                    });
                },
                (error) => {
                    console.error("Upload failed:", error);
                    showNotification("Error uploading photo. Please try again.", "error");
                    setIsUploading(false);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const newImages = [...editableUser.images];
                    newImages[index] = downloadURL;

                    const userRef = doc(db, "profiles", currentUser.id);
                    await updateDoc(userRef, {
                        images: newImages,
                        isProfilePictureValid: true // Mark valid if they successfully upload one here too
                    });

                    setEditableUser(prev => ({ ...prev, images: newImages }));
                    setCurrentUser(prev => ({ ...prev, images: newImages, isProfilePictureValid: true }));
                    showNotification("Photo updated successfully!", "success");
                    setIsUploading(false);
                }
            );
        }
    };

    const handleSave = async () => {
        try {
            const userRef = doc(db, "profiles", currentUser.id);
            const pct = calculateProfileCompletion({ ...currentUser, ...editableUser });
            await updateDoc(userRef, { ...editableUser, isUnderReview: pct >= 100 && !currentUser.profileComplete });
            setIsEditing(false);
            showNotification("Profile saved successfully!", "success");
        } catch (err) {
            console.error("Error updating profile: ", err);
            showNotification("Error saving profile.", "error");
        }
    };
    const [zoomedImage, setZoomedImage] = useState(null);

    return (
        <div className="max-w-5xl mx-auto p-4 md:p-8">
            <div className="bg-white/80 backdrop-blur-md border border-stone-200 rounded-3xl shadow-soft p-6 md:p-8">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-stone-900 font-serif">My Profile</h2>
                    {isEditing ? (
                        <div className="flex items-center gap-4">
                            <button onClick={() => { setIsEditing(false); setEditableUser(currentUser); }} className="px-4 py-2 text-stone-600 bg-stone-100/50 hover:bg-stone-200 rounded-xl font-semibold border border-stone-200 transition-colors">Cancel</button>
                            <button onClick={handleSave} disabled={isUploading} className="px-6 py-2 text-white bg-matrimony-600 rounded-xl hover:bg-matrimony-700 hover:shadow-lg font-semibold disabled:opacity-50 transition-all">Save Changes</button>
                        </div>
                    ) : (
                        <button onClick={onEdit} className="px-6 py-2 text-white bg-matrimony-600 rounded-xl hover:bg-matrimony-700 hover:shadow-lg hover:-translate-y-0.5 font-semibold transition-all">Edit Profile</button>
                    )}
                </div>
                {completionPercentage < 100 && !isEditing && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-5 rounded-r-xl mb-8 shadow-sm" role="alert">
                        <p className="font-bold text-yellow-900">Your profile is {completionPercentage}% complete!</p>
                        <p className="text-yellow-700 text-sm mt-1">Complete your profile to get better matches and increase your visibility.</p>
                        <button onClick={onEdit} className="mt-3 px-5 py-2 bg-yellow-400 text-yellow-900 font-bold rounded-lg hover:bg-yellow-500 hover:shadow-md transition-all text-sm">Complete Now</button>
                    </div>
                )}
                <div className="mb-10">
                    <h3 className="text-2xl font-bold text-stone-900 mb-6 font-serif">My Photos</h3>
                    {isUploading && <p className="text-center text-matrimony-600 mb-4 font-medium">Uploading...</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                        {(editableUser.images || [null, null, null]).slice(0, 3).map((img, index) => (
                            isEditing ? (
                                <label key={index} className="cursor-pointer relative overflow-hidden rounded-2xl shadow-sm border border-stone-200 group">
                                    <img src={img || 'https://placehold.co/400x400/f5f5f4/a8a29e?text=Upload+Photo'} alt={`Profile ${index + 1}`} className="w-full h-40 md:h-56 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity backdrop-blur-[2px]">
                                        <span className="text-white font-bold bg-white/20 px-4 py-2 rounded-full border border-white/30 backdrop-blur-md text-sm">Change Photo</span>
                                    </div>
                                    {uploadProgress[index] > 0 && uploadProgress[index] < 100 && (
                                        <div className="absolute bottom-0 left-0 w-full h-1 bg-stone-200">
                                            <div className="bg-matrimony-500 h-1" style={{ width: `${uploadProgress[index]}%` }}></div>
                                        </div>
                                    )}
                                    <input type="file" className="hidden" accept="image/*" disabled={isUploading} onChange={(e) => handleImageChange(e, index)} />
                                    {img && index > 0 && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const newImages = [...editableUser.images];
                                                const temp = newImages[0];
                                                newImages[0] = newImages[index];
                                                newImages[index] = temp;
                                                setEditableUser({ ...editableUser, images: newImages });
                                            }}
                                            className="absolute bottom-3 right-3 z-10 bg-stone-900/70 hover:bg-matrimony-600 text-white px-3 py-1.5 text-[10px] font-bold uppercase rounded-lg shadow-sm backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            Set Default
                                        </button>
                                    )}
                                </label>
                            ) : (
                                <div key={index} className="relative overflow-hidden rounded-2xl shadow-sm border border-stone-200 cursor-zoom-in group" onClick={() => img && setZoomedImage(img)}>
                                    <img src={img || 'https://placehold.co/400x400/f5f5f4/a8a29e?text=No+Photo'} alt={`Profile ${index + 1}`} className="w-full h-40 md:h-56 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                                </div>
                            )
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    {PROFILE_FIELD_ORDER.map(key => {
                        if (!(key in editableUser)) return null;

                        // CONDITIONAL VISIBILITY: Hide Creator fields if "Self"
                        const isSelf = (editableUser.createdBy || 'Self').startsWith('Self');
                        if ((key === 'creatorName' || key === 'creatorPhone') && isSelf) return null;

                        // CONDITIONAL VISIBILITY: Hide current address fields if sameAddress is true
                        if (['currentState', 'currentCity', 'currentAddress', 'currentPinCode'].includes(key) && editableUser.sameAddress) return null;

                        const value = editableUser[key];
                        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

                        if (key === 'sameAddress') {
                            if (!isEditing) return null; // Hide sameAddress checkbox in view mode
                            return (
                                <div key={key} className="md:col-span-2 py-2 flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        name={key}
                                        checked={!!value}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setEditableUser(prev => {
                                                const newData = { ...prev, sameAddress: val };
                                                if (val) {
                                                    newData.currentState = prev.permanentState || '';
                                                    newData.currentCity = prev.permanentCity || '';
                                                    newData.currentAddress = prev.permanentAddress || '';
                                                    newData.currentPinCode = prev.permanentPinCode || '';
                                                    newData.state = prev.permanentState || '';
                                                    newData.city = prev.permanentCity || '';
                                                    newData.pinCode = prev.permanentPinCode || '';
                                                    newData.streetAddress = prev.permanentAddress || '';
                                                }
                                                return newData;
                                            });
                                        }}
                                        className="w-5 h-5 text-matrimony-600 border-stone-300 rounded focus:ring-matrimony-500 cursor-pointer"
                                    />
                                    <label className="text-stone-700 font-semibold cursor-pointer text-base">My permanent and current address is same / सध्याचा आणि कायमचा पत्ता एकच आहे</label>
                                </div>
                            );
                        }
                        if (['permanentState', 'currentState'].includes(key)) {
                            return (
                                <div key={key}>
                                    <label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>
                                    {isEditing ? (
                                        <select
                                            name={key}
                                            value={value}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm cursor-pointer"
                                        >
                                            <option value="">Select State</option>
                                            {Object.keys(indianStates).map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    ) : (
                                        <p className="text-stone-800 font-medium p-3 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">{String(value)}</p>
                                    )}
                                </div>
                            );
                        }
                        if (['permanentCity', 'currentCity'].includes(key)) {
                            const stateKey = key === 'permanentCity' ? 'permanentState' : 'currentState';
                            const selectedState = editableUser[stateKey];
                            return (
                                <div key={key}>
                                    <label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>
                                    {isEditing ? (
                                        selectedState && indianStates[selectedState] ? (
                                            <select
                                                name={key}
                                                value={value}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm cursor-pointer"
                                            >
                                                <option value="">Select City</option>
                                                {indianStates[selectedState].map(c => <option key={c} value={c}>{c}</option>)}
                                                <option value="Other">Other / Not Listed</option>
                                            </select>
                                        ) : (
                                            <select disabled className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-500 rounded-xl cursor-not-allowed"><option>Select State First</option></select>
                                        )
                                    ) : (
                                        <p className="text-stone-800 font-medium p-3 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">{String(value)}</p>
                                    )}
                                </div>
                            );
                        }
                        if (['createdBy', 'gender', 'status', 'phoneVisibility'].includes(key)) {
                            return (<div key={key}><label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>{isEditing ? (<select name={key} value={value} onChange={handleInputChange} className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm">
                                {key === 'createdBy' && ['Self', 'Relative', 'Mother', 'Father', 'Sister', 'Brother'].map(opt => <option key={opt} value={opt} className="bg-white">{opt}</option>)}
                                {key === 'gender' && [{ v: 'male', l: 'Male' }, { v: 'female', l: 'Female' }].map(opt => <option key={opt.v} value={opt.v} className="bg-white">{opt.l}</option>)}
                                {key === 'status' && ['Unmarried', 'Divorced', 'Widowed'].map(opt => <option key={opt} value={opt} className="bg-white">{opt}</option>)}
                                {key === 'phoneVisibility' && ['Private', 'Public'].map(opt => <option key={opt} value={opt} className="bg-white">{opt}</option>)}</select>) : (<p className="text-stone-800 font-medium p-3 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">{String(value)}</p>)}</div>);
                        } if (key === 'email') {
                            return (<div key={key}><label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label><p className="pt-2 text-stone-500 bg-stone-50 p-3 rounded-xl border border-stone-100 shadow-sm opacity-80">{String(value)}</p></div>)
                        } if (['about', 'partnerPreference'].includes(key)) {
                            return (<div key={key} className="md:col-span-2"><label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>{isEditing ? (<textarea name={key} value={String(value)} onChange={handleInputChange} rows="4" className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm resize-none" />) : (<p className="text-stone-800 font-medium p-4 bg-stone-50 rounded-xl border border-stone-100 shadow-sm whitespace-pre-wrap leading-relaxed">{String(value)}</p>)}</div>)
                        } if (key === 'dob') {
                            return (<div key={key}><label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>{isEditing ? (<input type="date" name={key} value={String(value)} onChange={handleInputChange} className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm" />) : (<p className="text-stone-800 font-medium p-3 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">{String(value)} ({calculateAge(value)} years old)</p>)}</div>)
                        } return (<div key={key}><label className="block text-sm font-bold text-stone-500/80 uppercase tracking-wider mb-1">{label}</label>{isEditing ? (<input type="text" name={key} value={String(value)} onChange={handleInputChange} className="mt-1 block w-full p-3 bg-stone-50 border border-stone-200 text-stone-900 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all shadow-sm" />) : (<p className="text-stone-800 font-medium p-3 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">{String(value)}</p>)}</div>);
                    })}
                </div>
            </div>
            <ImageZoomModal imageUrl={zoomedImage} onClose={() => setZoomedImage(null)} />
        </div>
    );
};

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const ImageZoomModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex justify-center items-center z-[99999] animate-fade-in-up backdrop-blur-sm" onClick={onClose}>
            <div className="relative w-full h-full flex items-center justify-center p-4">
                <TransformWrapper
                    initialScale={1}
                    minScale={0.5}
                    maxScale={4}
                    centerOnInit={true}
                >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <React.Fragment>
                            <div className="fixed top-28 right-4 z-[9999] flex gap-4" onClick={e => e.stopPropagation()}>
                                <button onClick={() => resetTransform()} className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors backdrop-blur-md border border-white/20 shadow-lg" title="Reset">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                </button>
                                <button onClick={onClose} className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors backdrop-blur-md border border-white/20 shadow-lg" title="Close">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
                                <img
                                    src={imageUrl}
                                    alt="Zoomed Profile"
                                    className="max-w-full max-h-full object-contain"
                                    onClick={e => e.stopPropagation()}
                                />
                            </TransformComponent>
                        </React.Fragment>
                    )}
                </TransformWrapper>
            </div>
        </div>
    );
};

const DetailedProfilePage = ({ profile, setView, openChat, backView = 'matches', currentUser, showNotification, chatRequests = [], handleSendChatRequest, handleAcceptChatRequest, handleBlockUser, handleUnblockUser, handleDisconnectUser, onLike }) => {
    const [mainImage, setMainImage] = useState(profile.images?.[0] || 'https://placehold.co/400x400');
    const [activeThumbIndex, setActiveThumbIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('details');
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null);

    // Scroll to top on mount
    // Scroll to top whenever profile changes or component mounts
    React.useLayoutEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        // Comprehensive backup timers for mobile, slow renders, and browser scroll-restoration attempts
        const timer1 = setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }, 10);

        const timer2 = setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }, 50);

        const timer3 = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 150);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [profile?.id]);

    const handleShare = () => {
        const url = `${window.location.origin}?profileId=${profile.id}`;
        navigator.clipboard.writeText(url).then(() => {
            showNotification("Profile Link Copied!", "success");
        }).catch(() => {
            showNotification("Failed to copy link.", "error");
        });
    };

    const generatePdf = async () => {
        if (!window.jspdf) {
            alert("PDF generation library is not loaded yet. Please try again in a moment.");
            return;
        }
        if (currentUser?.isEditor) {
            showNotification("Editors are not allowed to export profiles.", "error");
            return;
        }
        setIsGeneratingPdf(true);

        try {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 15;
            let y = 20;
            const lineHeight = 7;

            // Helper to add text with auto-wrapping
            const addWrappedText = (text, fontSize = 10, fontStyle = 'normal', color = [0, 0, 0]) => {
                doc.setFontSize(fontSize);
                doc.setFont("helvetica", fontStyle);
                doc.setTextColor(...color);
                const splitText = doc.splitTextToSize(text || 'N/A', pageWidth - (margin * 2));

                // Check for page break
                if (y + (splitText.length * lineHeight) > doc.internal.pageSize.getHeight() - margin) {
                    doc.addPage();
                    y = 20;
                }

                doc.text(splitText, margin, y);
                y += (splitText.length * lineHeight) + 2;
            };

            // Helper to add a section title
            const addSectionTitle = (title) => {
                y += 5;
                if (y > doc.internal.pageSize.getHeight() - margin) { doc.addPage(); y = 20; }
                doc.setFontSize(14);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(225, 29, 72); // Rose-600
                doc.text(title, margin, y);
                y += 10;
                doc.setDrawColor(200);
                doc.line(margin, y - 5, pageWidth - margin, y - 5);
            };

            // Helper to add a field-value pair
            const addField = (label, value, inlineWithPrevious = false) => {
                doc.setFontSize(10);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(100);

                const labelWidth = 50;
                if (y > doc.internal.pageSize.getHeight() - margin) { doc.addPage(); y = 20; }

                doc.text(label + ":", margin, y);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0);
                doc.text(String(value || 'N/A'), margin + labelWidth, y);

                y += lineHeight;
            };

            // --- PAGE 1: Personal & Professional Details ---
            doc.setFontSize(22);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(225, 29, 72);
            doc.text("MyLuckySpouse Profile", pageWidth / 2, y, { align: 'center' });
            y += 15;

            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.text(`${profile.name} ${profile.lastName}`, pageWidth / 2, y, { align: 'center' });
            y += 15;

            addSectionTitle("Personal Details");
            addField("Full Name", `${profile.name} ${profile.middleName ? profile.middleName + ' ' : ''}${profile.lastName || ''}`);
            addField("Profile ID", profile.id);
            addField("Created By", profile.createdBy);
            if (profile.createdBy !== 'Self') {
                addField("Creator Name", profile.creatorName);
                if (profile.creatorPhone) addField("Creator Phone", profile.creatorPhone);
            }
            addField("Age / DOB", `${calculateAge(profile.dob)} years (${profile.dob})`);
            if (profile.birthTime) addField("Birth Time", profile.birthTime);
            if (profile.birthPlace) addField("Birth Place", profile.birthPlace);
            addField("Gender", profile.gender);
            addField("Marital Status", profile.status);
            addField("Height", profile.height);
            if (profile.middleName) addField("Middle Name", profile.middleName);

            addSectionTitle("Religious Details");
            addField("Religion", profile.religion);
            addField("Caste", profile.caste);
            addField("Gotra", profile.gotra);
            addField("Mother Tongue", profile.motherTongue);

            addSectionTitle("Professional Details");
            addField("Education", profile.education);
            if (profile.specificDegree) addField("Specific Degree", profile.specificDegree);
            addField("Employment Type", profile.employmentType);
            if (profile.profession) addField("Profession", profile.profession);
            if (profile.designation) addField("Designation", profile.designation);
            if (profile.workPlace) addField("Company", profile.workPlace);
            if (profile.businessName) addField("Business Name", profile.businessName);
            if (profile.jobLocation) addField("Job Location", profile.jobLocation);
            addField("Annual Income", profile.income);

            addSectionTitle("Location & Contact");
            addField("Location", `${profile.city || profile.currentCity || ''}, ${profile.state || profile.currentState || ''}, ${profile.country || 'India'}`);
            addField("Permanent Location", `${profile.permanentCity || profile.city || ''}, ${profile.permanentState || profile.state || ''} - ${profile.permanentPinCode || profile.pinCode || ''}`);
            addField("Current Location", profile.sameAddress ? "Same as Permanent Location" : `${profile.currentCity || profile.city || ''}, ${profile.currentState || profile.state || ''} - ${profile.currentPinCode || profile.pinCode || ''}`);
            addField("Phone", profile.phone);
            addField("Email", profile.email);
            addField("Parent Name", profile.parentName);
            addField("Parent Phone", profile.parentPhone);

            addSectionTitle("More About Candidate");
            if (profile.hobbies) addField("Hobbies", profile.hobbies);
            if (profile.about) addField("About", profile.about);
            if (profile.partnerPreference) addField("Partner Preference", profile.partnerPreference);

            // --- PAGE 2: About & Preferences ---
            doc.addPage();
            y = 20;

            addSectionTitle("About Me");
            addWrappedText(profile.about, 11);

            y += 10;
            addSectionTitle("Partner Preferences");
            addWrappedText(profile.partnerPreference, 11);

            // --- PAGES 3+: Images ---
            if (profile.images && profile.images.length > 0) {
                const getBase64FromUrl = async (url) => {
                    try {
                        const res = await fetch(url);
                        const blob = await res.blob();
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                    } catch (e) {
                        console.error("Error fetching image:", e);
                        return null;
                    }
                };

                for (let i = 0; i < profile.images.length; i++) {
                    doc.addPage();
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                    doc.text(`Image ${i + 1} of ${profile.images.length}`, margin, 15);

                    const imgUrl = profile.images[i];
                    const base64 = await getBase64FromUrl(imgUrl);

                    if (base64) {
                        const imgProps = doc.getImageProperties(base64);
                        const pdfWidth = doc.internal.pageSize.getWidth() - (margin * 2);
                        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                        // Center vertically if possible, or just top
                        doc.addImage(base64, 'JPEG', margin, 20, pdfWidth, pdfHeight);
                    } else {
                        doc.text("Failed to load image.", margin, 30);
                    }
                }
            }

            doc.save(`${profile.name}-${profile.lastName}-full-profile.pdf`);

        } catch (err) {
            console.error("PDF generation failed:", err);
            alert("Failed to generate PDF. Please check console for details.");
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const isProfileComplete = calculateProfileCompletion(currentUser) >= 100;
    const handleChatAction = (profile) => {
        if (!isProfileComplete) {
            showNotification("Please complete your profile to connect with users.", "error");
        } else {
            openChat(profile);
        }
    };

    return (<div className="w-full bg-stone-50 pb-20 min-h-screen">
        <ImageZoomModal imageUrl={zoomedImage} onClose={() => setZoomedImage(null)} />

        {/* --- Cinematic Hero Section --- */}
        <div className="relative w-full h-[60vh] md:h-[75vh] bg-stone-900 overflow-hidden border-b border-stone-800 flex items-center justify-center">
            {/* Blurred Background to fill empty space nicely */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <img src={mainImage} alt="" className="w-full h-full object-cover opacity-40 blur-3xl scale-125" />
            </div>
            {/* Main Image properly fitted - increased to z-20 to ensure it is stacked above the background blurred image */}
            <div className="absolute inset-0 z-20 p-2 md:p-8 flex items-center justify-center pointer-events-none">
                <img src={mainImage} onClick={() => setZoomedImage(mainImage)} alt="Profile Hero" className="max-w-full max-h-full object-contain opacity-95 cursor-zoom-in transition-transform duration-1000 hover:scale-[1.02] drop-shadow-2xl rounded-xl md:rounded-3xl pointer-events-auto" />
            </div>
            {/* Dark gradient overlay - added pointer-events-none so it doesn't block clicks on the profile picture */}
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent pointer-events-none z-10"></div>

            {/* Top Bar - added pointer-events-none to container and pointer-events-auto to buttons so it doesn't intercept other clicks */}
            <div className="absolute top-0 left-0 right-0 p-4 md:p-8 flex justify-between items-start z-30 w-full max-w-7xl mx-auto animate-fade-in-down pointer-events-none">
                <button onClick={() => setView(backView)} className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-xl border border-white/20 text-sm font-bold rounded-full text-white hover:bg-white/20 shadow-cinematic transition-all focus:outline-none hover:scale-105 pointer-events-auto">
                    &larr; Back
                </button>
                <div className="flex gap-4 pointer-events-auto">
                    {backView === 'admin' && (currentUser?.isAdmin || currentUser?.isViewer) && (
                        <button onClick={generatePdf} disabled={isGeneratingPdf} className="inline-flex items-center px-5 py-2.5 bg-blue-600/80 backdrop-blur-md border border-blue-400/30 text-sm font-bold rounded-full text-white hover:bg-blue-600 shadow-md transition-all disabled:opacity-50">
                            {isGeneratingPdf ? 'Exporting...' : 'Export PDF'}
                        </button>
                    )}
                    {currentUser && currentUser.id !== profile.id && (
                        <>
                            {/* Like Toggle Button */}
                            {(() => {
                                const isLiked = currentUser.likedUsers?.includes(profile.id);
                                return (
                                    <button 
                                        onClick={() => onLike(profile)} 
                                        className={`inline-flex items-center gap-2 px-6 py-3 backdrop-blur-xl border text-sm font-bold rounded-full shadow-cinematic transition-all focus:outline-none hover:scale-105 pointer-events-auto ${isLiked ? 'bg-rose-600/90 text-white border-rose-500/30 hover:bg-rose-600' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'}`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill={isLiked ? "white" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                        </svg>
                                        {isLiked ? "Liked" : "Like Profile"}
                                    </button>
                                );
                            })()}

                            {currentUser.blockedUsers?.includes(profile.id) ? (
                                <button onClick={() => handleUnblockUser(profile.id)} className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600/80 backdrop-blur-md border border-emerald-500/30 text-sm font-bold rounded-full text-white hover:bg-emerald-600 shadow-cinematic transition-all focus:outline-none hover:scale-105">
                                    Unblock User
                                </button>
                            ) : (
                                <button onClick={() => { if (window.confirm("Are you sure you want to block this user?")) handleBlockUser(profile.id); }} className="inline-flex items-center gap-2 px-6 py-3 bg-red-600/80 backdrop-blur-md border border-red-500/30 text-sm font-bold rounded-full text-white hover:bg-red-600 shadow-cinematic transition-all focus:outline-none hover:scale-105">
                                    Block User
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {/* Bottom Hero Info - added pointer-events-none so the user can click through the empty spaces of the text overlay onto the image */}
            <div className="absolute bottom-0 left-0 right-0 p-6 pb-20 md:p-12 md:pb-28 z-30 w-full max-w-7xl mx-auto animate-slide-up pointer-events-none">
                <div className="flex items-center gap-4 flex-wrap mb-3">
                    <h2 className="text-4xl md:text-7xl font-black text-white font-serif tracking-tight drop-shadow-2xl">{`${profile.name} ${profile.lastName}`}</h2>
                    {profile.isPreferred && <span className="bg-amber-500/80 backdrop-blur-md text-amber-50 border border-amber-400 text-xs font-bold px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.5)]">Preferred</span>}
                </div>
                <div className="flex items-center gap-3 text-stone-200 font-medium text-lg flex-wrap">
                    <span>{calculateAge(profile.dob)} yrs</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400"></span>
                    <span>{profile.height || "Height N/A"}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400"></span>
                    <span>{profile.city}, {profile.state}</span>
                </div>
                {isProfileOnline(profile) ? (
                    <div className="flex items-center gap-2 mt-4 text-emerald-300 font-semibold text-sm"> <span className="relative flex h-3 w-3"><span className="animate-ping bg-emerald-400 absolute inline-flex h-full w-full rounded-full opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span></span> Online </div>
                ) : (
                    <div className="flex items-center gap-2 mt-4 text-stone-300 font-medium text-sm"> <span className="relative flex h-3 w-3"><span className="relative inline-flex rounded-full h-3 w-3 bg-stone-400"></span></span> Offline </div>
                )}
            </div>
        </div>

        {/* --- Main Overlapping Content Area --- */}
        <div id="profile-to-export" className="max-w-7xl mx-auto px-4 md:px-8 -mt-12 md:-mt-24 relative z-30">
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Content Area */}
                <div className="flex-grow bg-white/95 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_-10px_40px_-5px_rgba(0,0,0,0.3)] p-6 md:p-12 border border-white animate-slide-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                    <div className="space-y-12 text-stone-700">
                        {/* Tags at Top */}
                        <div className="flex flex-wrap gap-2 pb-6 border-b border-stone-100">
                            <span className="bg-stone-50 border border-stone-200 text-stone-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">ID: {profile.id}</span>
                            {profile.createdBy && <span className="bg-stone-50 border border-stone-200 text-stone-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">Added By: {profile.createdBy}</span>}
                            {profile.hobbies && <span className="bg-stone-50 border border-stone-200 text-stone-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">Hobbies: {profile.hobbies}</span>}
                        </div>

                        {/* Premium Payment Verification Card (Admins/Editors only) */}
                        {backView === 'admin' && (currentUser?.isAdmin || currentUser?.isEditor) && !profile.profileComplete && (
                            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 p-6 md:p-8 rounded-3xl border border-amber-200 shadow-sm animate-fade-in-up">
                                <h4 className="font-bold text-amber-900 text-xl font-serif mb-3 flex items-center gap-2">
                                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                                    Matrimony Premium Payment Review
                                </h4>
                                
                                {profile.paymentDetails && profile.paymentDetails.paymentId ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-[10px] text-amber-700 uppercase tracking-widest font-extrabold">Razorpay Payment ID</p>
                                                <p className="font-mono font-bold text-stone-800 text-base">{profile.paymentDetails.paymentId}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-amber-700 uppercase tracking-widest font-extrabold">Submitted At</p>
                                                <p className="font-semibold text-stone-800 text-sm">
                                                    {profile.paymentDetails.submittedAt ? new Date(profile.paymentDetails.submittedAt).toLocaleString() : 'N/A'}
                                                </p>
                                            </div>
                                            {profile.paymentDetails.method && (
                                                <div>
                                                    <p className="text-[10px] text-amber-700 uppercase tracking-widest font-extrabold">Method</p>
                                                    <p className="font-semibold text-stone-800 text-sm">{profile.paymentDetails.method}</p>
                                                </div>
                                            )}
                                            <button 
                                                onClick={async () => {
                                                    if (!window.confirm("Approve payment and activate user profile?")) return;
                                                    try {
                                                        const userRef = doc(db, "profiles", profile.id);
                                                        await updateDoc(userRef, {
                                                            profileComplete: true,
                                                            isUnderReview: false,
                                                            isPaid: true,
                                                            'paymentDetails.status': 'approved',
                                                            'paymentDetails.approvedAt': Date.now()
                                                        });
                                                        showNotification("Profile verified and payment approved successfully!", "success");
                                                        setView('admin');
                                                    } catch (e) {
                                                        console.error("Error approving payment:", e);
                                                        showNotification("Failed to approve payment: " + e.message, "error");
                                                    }
                                                }}
                                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center gap-2 uppercase tracking-wider mt-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                                                Approve & Verify Profile
                                            </button>
                                        </div>
                                        
                                        {profile.paymentDetails.screenshotUrl && (
                                            <div className="text-left">
                                                <p className="text-[10px] text-amber-700 uppercase tracking-widest font-extrabold mb-1">Receipt Screenshot</p>
                                                <div className="relative group overflow-hidden rounded-2xl border border-amber-200 shadow-sm max-w-[240px] bg-white cursor-zoom-in">
                                                    <img 
                                                        src={profile.paymentDetails.screenshotUrl} 
                                                        alt="Receipt Proof" 
                                                        onClick={() => setZoomedImage(profile.paymentDetails.screenshotUrl)}
                                                        className="w-full h-auto object-cover max-h-[160px] group-hover:scale-105 transition-all duration-300"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center text-white text-xs font-bold">
                                                        Click to Zoom
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3">
                                            <span className="text-xl">⚠️</span>
                                            <div>
                                                <p className="font-bold text-rose-900 text-sm">No Payment Info Submitted Yet</p>
                                                <p className="text-xs text-rose-700 mt-0.5">This user has not completed their Razorpay payment or submitted a transaction reference. You can manually verify their payment and activate their account below.</p>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-3 bg-white/60 p-4 rounded-2xl border border-amber-200/60 max-w-xl">
                                            <div>
                                                <label htmlFor={`manual-payment-id-${profile.id}`} className="block text-xs font-black text-amber-900 uppercase tracking-widest mb-1.5">
                                                    Manual Razorpay ID or Transaction Reference (Optional)
                                                </label>
                                                <input 
                                                    type="text" 
                                                    id={`manual-payment-id-${profile.id}`}
                                                    placeholder="e.g. pay_manual_12345 (Leave empty to auto-generate)"
                                                    className="w-full font-mono text-sm px-4 py-2.5 rounded-xl border border-amber-200 focus:outline-none focus:ring-2 focus:ring-matrimony-500 bg-white text-stone-800"
                                                />
                                            </div>
                                            <button 
                                                onClick={async () => {
                                                    const inputVal = document.getElementById(`manual-payment-id-${profile.id}`)?.value?.trim();
                                                    const finalPaymentId = inputVal || `pay_manual_${Date.now()}`;
                                                    
                                                    if (!window.confirm(`Manually approve and verify account with reference: ${finalPaymentId}?`)) return;
                                                    
                                                    try {
                                                        const userRef = doc(db, "profiles", profile.id);
                                                        await updateDoc(userRef, {
                                                            profileComplete: true,
                                                            isUnderReview: false,
                                                            isPaid: true,
                                                            paymentDetails: {
                                                                paymentId: finalPaymentId,
                                                                method: 'Admin Manual Verification',
                                                                status: 'approved',
                                                                submittedAt: Date.now(),
                                                                approvedAt: Date.now()
                                                            }
                                                        });
                                                        showNotification("Profile verified and payment approved successfully!", "success");
                                                        setView('admin');
                                                    } catch (e) {
                                                        console.error("Error manually approving payment:", e);
                                                        showNotification("Failed to approve payment: " + e.message, "error");
                                                    }
                                                }}
                                                className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 uppercase tracking-wider"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                Verify & Approve Account
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {profile.about && (
                            <div>
                                <h4 className="font-bold text-stone-900 text-2xl font-serif mb-4 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Story</h4>
                                <p className="whitespace-pre-wrap text-stone-600 text-lg leading-relaxed">{profile.about}</p>
                            </div>
                        )}
                        <hr className="border-stone-100" />
                        <div><h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Basic Details</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                                <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Full Name</p><p className="font-semibold text-stone-800 text-lg">{`${profile.name} ${profile.middleName || ''} ${profile.lastName}`}</p></div>
                                <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Age & DOB</p><p className="font-semibold text-stone-800 text-lg">{`${calculateAge(profile.dob)} years (${profile.dob})`}</p></div>
                                <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Height</p><p className="font-semibold text-stone-800 text-lg">{profile.height}</p></div>
                                <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Marital Status</p><p className="font-semibold text-stone-800 text-lg">{profile.status}</p></div>
                                <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Gender</p><p className="font-semibold capitalize text-stone-800 text-lg">{profile.gender}</p></div>
                            </div>
                        </div>
                        <hr className="border-stone-100" />
                        <div><h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Religious Details</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Religion</p><p className="font-semibold text-stone-800 text-lg">{profile.religion}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Caste</p><p className="font-semibold text-stone-800 text-lg">{profile.caste}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Gotra</p><p className="font-semibold text-stone-800 text-lg">{profile.gotra || 'N/A'}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Mother Tongue</p><p className="font-semibold text-stone-800 text-lg">{profile.motherTongue}</p></div></div></div><hr className="border-stone-100" />
                        <div><h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Professional & Education</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Education</p><p className="font-semibold text-stone-800 text-lg">{profile.education}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Specific Degree</p><p className="font-semibold text-stone-800 text-lg">{profile.specificDegree || '-'}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Profession</p><p className="font-semibold text-stone-800 text-lg">{profile.profession}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Company Name</p><p className="font-semibold text-stone-800 text-lg">{profile.workPlace}</p></div><div className="col-span-1 sm:col-span-2"><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Designation</p><p className="font-semibold text-stone-800 text-lg">{profile.designation}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Annual Income</p><p className="font-semibold text-stone-800 text-lg">{profile.income}</p></div></div></div><hr className="border-stone-100" />
                        <div><h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Location & Family</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Current Location</p><p className="font-semibold text-stone-800 text-lg">{`${profile.city}, ${profile.state}, ${profile.country}`}</p></div><div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Parent Name</p><p className="font-semibold text-stone-800 text-lg">{profile.parentName || 'N/A'}</p></div></div></div>
                        <hr className="border-stone-100" />

                        <div>
                            <h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Partner Preferences</h4>
                            <div className="bg-rose-50/50 p-8 rounded-3xl border border-rose-100 text-stone-700">
                                <p className="whitespace-pre-wrap text-lg leading-relaxed">{profile.partnerPreference || "No specific preferences mentioned."}</p>
                            </div>
                        </div>

                        {/* Contact Details moved to the very end */}
                        <hr className="border-stone-100" />
                        <div>
                            <h4 className="font-bold text-stone-900 text-2xl font-serif mb-6 flex items-center gap-3"><div className="w-1.5 h-8 bg-matrimony-500 rounded-full"></div>Contact Details</h4>
                            {(currentUser?.isAdmin || currentUser?.id === profile.id || currentUser?.isViewer || currentUser?.isEditor) ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 bg-stone-50 p-8 rounded-3xl border border-stone-100">
                                    <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Phone</p><p className="font-semibold text-stone-900 text-lg">{profile.phone || 'N/A'}</p></div>
                                    <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Email</p><p className="font-semibold text-stone-900 text-lg">{profile.email || 'N/A'}</p></div>
                                    {profile.parentPhone && <div><p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Parent Phone</p><p className="font-semibold text-stone-900 text-lg">{profile.parentPhone}</p></div>}
                                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-stone-100">
                                        <div>
                                            <p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Permanent Location</p>
                                            <p className="font-semibold text-stone-900 text-lg">{`${profile.permanentCity || profile.city || ''}, ${profile.permanentState || profile.state || ''}, ${profile.permanentPinCode || profile.pinCode || ''}`}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Current Location</p>
                                            <p className="font-semibold text-stone-900 text-lg">{profile.sameAddress ? "Same as Permanent Location" : `${profile.currentCity || profile.city || ''}, ${profile.currentState || profile.state || ''}, ${profile.currentPinCode || profile.pinCode || ''}`}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gradient-to-br from-stone-50 to-stone-100 p-8 rounded-3xl border border-stone-200 text-center">
                                    <p className="text-stone-600 font-medium mb-4 text-lg">Contact details are private.</p>
                                    <a href="tel:+919511891850" className="inline-block bg-matrimony-600 text-white font-bold py-3.5 px-8 rounded-full hover:bg-matrimony-700 shadow-xl transition-all hover:-translate-y-1">
                                        Call us to connect: +91 95118 91850
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Floating Panel */}
                <div className="w-full lg:w-96 flex-shrink-0 space-y-8 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>

                    {/* Gallery Section Moved Above Interested */}
                    {profile.images && profile.images.length > 1 && (
                        <div className="bg-white rounded-[2.5rem] p-8 border border-stone-100 shadow-soft">
                            <h4 className="font-bold text-stone-900 text-xl font-serif mb-6 flex items-center gap-2"><div className="w-1.5 h-6 bg-matrimony-400 rounded-full"></div>Gallery</h4>
                            <div className="grid grid-cols-2 gap-4">
                                {profile.images.map((img, index) => (
                                    <div key={index} className={`rounded-2xl overflow-hidden aspect-square border-2 transition-all cursor-pointer ${activeThumbIndex === index ? 'border-matrimony-500 shadow-md ring-4 ring-matrimony-100 ring-offset-2 scale-105' : 'border-transparent hover:border-stone-200 hover:scale-105'}`} onClick={() => { setMainImage(img); setActiveThumbIndex(index); setZoomedImage(img); }}>
                                        <img src={img} alt={`Gallery ${index}`} className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Interested Section */}
                    {backView !== 'admin' && !currentUser?.isViewer && !currentUser?.isEditor && (
                        <div className="bg-gradient-to-b from-white to-rose-50/50 rounded-[2.5rem] p-10 text-center border border-white shadow-[0_-10px_40px_-5px_rgba(0,0,0,0.1)] sticky top-32">
                            <h3 className="font-bold text-3xl text-matrimony-800 font-serif mb-3">Interested?</h3>
                            <p className="text-stone-500 mb-8 font-medium">Take the first step towards a beautiful journey.</p>
                            <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center shadow-soft mb-8 border border-stone-100">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-matrimony-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                            </div>
                            {(() => {
                                const request = (chatRequests || []).find(r => r.participants?.includes(profile.id));
                                const isLiked = currentUser?.likedUsers?.includes(profile.id);
                                
                                return (
                                    <div className="flex flex-col gap-4">
                                        {/* Like Toggle Button */}
                                        <button 
                                            onClick={() => onLike(profile)} 
                                            className={`w-full font-bold py-3.5 px-6 rounded-2xl transition-all text-base flex items-center justify-center gap-2 border hover:-translate-y-0.5 transform shadow-sm ${isLiked ? 'bg-rose-600 text-white border-rose-500 hover:bg-rose-700' : 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'}`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill={isLiked ? "white" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                            </svg>
                                            {isLiked ? "Liked Profile" : "Like Profile"}
                                        </button>

                                        {/* Existing connection request actions */}
                                        {(() => {
                                            if (!request) {
                                                return (
                                                    <button onClick={() => handleSendChatRequest(profile.id)} disabled={!isProfileComplete} className="w-full bg-gradient-to-r from-matrimony-600 to-rose-500 text-white font-bold py-4 px-6 rounded-2xl hover:from-matrimony-700 hover:to-rose-600 transition-all shadow-premium hover:-translate-y-1 transform disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center justify-center gap-3">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                        Connect / Send Request
                                                    </button>
                                                );
                                            }
                                            
                                            if (request.status === 'pending') {
                                                if (request.senderId === currentUser?.id) {
                                                    return (
                                                        <div className="space-y-3">
                                                            <button disabled className="w-full bg-stone-100 text-stone-400 font-bold py-4 px-6 rounded-2xl border border-stone-200 text-lg flex items-center justify-center gap-3 cursor-not-allowed">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                                Connection Pending
                                                            </button>
                                                            <button 
                                                                onClick={() => {
                                                                    if (window.confirm("Are you sure you want to cancel this connection request?")) {
                                                                        handleDisconnectUser(profile.id);
                                                                    }
                                                                }}
                                                                className="w-full bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 font-bold py-3 px-6 rounded-2xl transition-all text-sm flex items-center justify-center gap-2"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                                Cancel Request
                                                            </button>
                                                        </div>
                                                    );
                                                } else {
                                                    const requestId = request.id;
                                                    return (
                                                        <div className="space-y-3">
                                                            <button onClick={() => handleAcceptChatRequest(requestId)} className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-bold py-4 px-6 rounded-2xl hover:from-emerald-700 hover:to-teal-600 transition-all shadow-premium hover:-translate-y-1 transform text-lg flex items-center justify-center gap-3">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                                Accept Connection Request
                                                            </button>
                                                            <button 
                                                                onClick={() => {
                                                                    if (window.confirm("Are you sure you want to decline this connection request?")) {
                                                                        handleDisconnectUser(profile.id);
                                                                    }
                                                                }}
                                                                className="w-full bg-stone-100 hover:bg-stone-200 text-stone-600 border border-stone-200 font-bold py-3 px-6 rounded-2xl transition-all text-sm flex items-center justify-center gap-2"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                                Decline Request
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                            }
                                            
                                            return (
                                                <div className="space-y-3">
                                                    <button onClick={() => handleChatAction(profile)} disabled={!isProfileComplete} className="w-full bg-gradient-to-r from-matrimony-600 to-rose-500 text-white font-bold py-4 px-6 rounded-2xl hover:from-matrimony-700 hover:to-rose-600 transition-all shadow-premium hover:-translate-y-1 transform disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center justify-center gap-3">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                                                        Send Message
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            if (window.confirm(`Are you sure you want to disconnect from ${profile.name || 'this user'}? This will delete the chat history and connection.`)) {
                                                                handleDisconnectUser(profile.id);
                                                            }
                                                        }}
                                                        className="w-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-bold py-3 px-6 rounded-2xl transition-all text-sm flex items-center justify-center gap-2"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                        Disconnect Profile
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>);
};

// By defining NavLink outside the Header, we ensure it's not re-created on every render.
// This improves performance and prevents potential issues with event handlers.
const NavLink = ({ viewName, children, setView, setIsMenuOpen }) => {
    const handleClick = () => {
        // The NavLink's only job is to set the desired view.
        // All routing and access control is handled by renderMainContent.
        setView(viewName);
        setIsMenuOpen(false);
    };
    return <button onClick={handleClick} className="w-full text-left md:w-auto px-4 py-2 md:p-0 font-semibold text-gray-600 hover:text-rose-600">{children}</button>;
};

const VerificationPendingPage = ({ currentUser, showNotification }) => (
    <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 p-8 rounded-2xl max-w-md text-center shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Verification Pending</h2>
            <p className="text-gray-300 mb-8 leading-relaxed">
                Your profile is complete!
                <br /><br />
                Our team is currently reviewing your details to ensure the safety and quality of our community. You will receive a notification once verified.
            </p>
            <button onClick={() => window.location.reload()} className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/10">
                Check Status
            </button>
            <p className="mt-6 text-xs text-gray-500">
                Logged in as <span className="text-gray-400">{currentUser?.email}</span>
            </p>
        </div>
    </div>
);

const IncompleteProfileBanner = ({ currentUser, onUpdate }) => {
    // Only show for "Legacy Complete" users (profileComplete=true) who are missing calculated fields.
    // New users are forced into the modal anyway, so this is specifically for old users who might have dismissed the popup.
    const isLegacyComplete = currentUser?.profileComplete === true;
    const completionPercentage = calculateProfileCompletion(currentUser);
    const isIncomplete = completionPercentage < 100;
    const isViewer = currentUser?.isViewer;

    const hasImages = currentUser?.images && currentUser.images.length > 0;
    const textCompletion = calculateProfileCompletion(currentUser, true);
    const isTextComplete = textCompletion >= 100;

    // Logic:
    // 1. If Text is incomplete -> "Missing Details"
    // 2. If Text complete, but Image missing -> "Upload Photo"

    if (isViewer) return null;

    // Legacy user who is missing text? OR anyone who is missing fields but somehow on a page where banner shows
    if (isIncomplete || !hasImages) {
        let message = "Your profile is incomplete.";
        let btnText = "Complete Now";

        if (isTextComplete && !hasImages) {
            message = "⚠️ Please upload a profile photo to be visible to matches!";
            btnText = "Upload Photo";
        } else if (!isTextComplete) {
            message = "⚠️ Your profile is missing some details. Complete it for better matches!";
        } else {
            // If both complete (pct=100 and hasImages), then isIncomplete should be false.
            return null;
        }

        return (
            <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white text-sm font-semibold p-2 text-center shadow-md relative z-[60] flex items-center justify-center gap-4 animate-fade-in-down">
                <span>{message}</span>
                <button onClick={onUpdate} className="bg-white text-orange-600 px-3 py-1 rounded-full text-xs font-bold hover:bg-gray-100 transition-colors shadow-sm">
                    {btnText}
                </button>
            </div>
        );
    }
    return null;
};
const Header = ({ currentUser, setView, onInboxClick, totalUnreadCount, onNotificationClick, unreadNotificationsCount, handleLogout, setShowProfileModal, showNotification }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const completionPercentage = currentUser ? calculateProfileCompletion(currentUser) : 0;
    const isProfileComplete = currentUser ? completionPercentage >= 100 && (currentUser.images && currentUser.images.length > 0) : false;
    const canBypassCompletion = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor;

    const handleInboxClick = () => {
        if (!canBypassCompletion && !isProfileComplete) {
            showNotification("Please complete your profile to access your inbox.", "error");
            setShowProfileModal(true);
            return;
        }
        onInboxClick();
        setIsMenuOpen(false);
    };

    const handleNotificationClick = () => {
        if (!canBypassCompletion && !isProfileComplete) {
            showNotification("Please complete your profile to access notifications.", "error");
            setShowProfileModal(true);
            return;
        }
        onNotificationClick();
        setIsMenuOpen(false);
    };

    const ProfileCompletionIndicator = () => (
        <button onClick={() => { setView('complete-profile'); setIsMenuOpen(false); }} className="w-full text-left md:w-auto px-4 py-2 md:p-0 font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-orange-500 hover:to-orange-400 flex items-center gap-2 group transition-all">
            <div className="relative w-7 h-7">
                <svg className="w-full h-full rotate-[-90deg]" viewBox="0 0 36 36">
                    <path className="text-white/10" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="4" />
                    <path className="text-yellow-500 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke="currentColor" strokeWidth="4" strokeDasharray={`${completionPercentage}, 100`} strokeLinecap="round"
                    />
                </svg>
            </div>
            <span className="group-hover:scale-105 transition-transform">Complete ({completionPercentage}%)</span>
        </button>
    );

    return (
        <header className="fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-7xl z-50 transition-all duration-300">
            <div className="absolute inset-0 bg-white/80 backdrop-blur-2xl border border-white/50 shadow-cinematic rounded-full"></div>

            <nav className={`container mx-auto px-4 md:px-6 py-2 md:py-3 flex justify-between items-center relative z-10`}>
                <button onClick={() => setView('matches')} className="group flex items-center gap-3 relative mr-4">
                    <img src="/logo-v3.png" alt="MyLuckySpouse" className="h-8 md:h-10 w-auto relative drop-shadow-sm group-hover:scale-110 transition-transform duration-500" />
                    <div className="flex flex-col items-start relative hidden sm:flex">
                        <span className="text-lg md:text-xl font-black tracking-tight text-matrimony-700 group-hover:text-matrimony-600 transition-colors font-serif">
                            MyLuckySpouse
                        </span>
                        <span className="text-[9px] font-bold text-matrimony-500 tracking-widest uppercase -mt-1 ml-0.5">
                            Matchmaking AI
                        </span>
                    </div>
                </button>

                {/* Mobile Icons + Hamburger Container (Mobile Only) */}
                <div className="flex items-center gap-2 md:hidden relative z-10">
                    <button onClick={handleInboxClick} className="relative p-2 text-stone-600 hover:text-matrimony-600 transition-colors bg-white/50 rounded-full border border-stone-200" aria-label="Open Inbox">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {totalUnreadCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-matrimony-500 text-[9px] font-bold text-white shadow-sm animate-scale-up">
                                {totalUnreadCount}
                            </span>
                        )}
                    </button>

                    <button onClick={handleNotificationClick} className="relative p-2 text-stone-600 hover:text-matrimony-600 transition-colors bg-white/50 rounded-full border border-stone-200" aria-label="Open Notifications">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        {unreadNotificationsCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-matrimony-500 text-[9px] font-bold text-white shadow-sm animate-pulse">
                                {unreadNotificationsCount}
                            </span>
                        )}
                    </button>

                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="relative p-2 text-stone-600 hover:text-matrimony-600 transition-colors bg-white/50 rounded-full border border-stone-200">
                        <svg className="h-5 w-5 relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"} />
                        </svg>
                    </button>
                </div>

                {/* Navigation Links */}
                <div className={`
                    ${isMenuOpen ? 'flex' : 'hidden'} 
                    md:flex flex-col md:flex-row md:items-center 
                    absolute md:static top-full left-0 w-full md:w-auto 
                    bg-white/95 md:bg-transparent backdrop-blur-3xl md:backdrop-blur-none
                    border border-stone-100 md:border-none shadow-premium md:shadow-none
                    rounded-3xl md:rounded-none mt-4 md:mt-0
                    p-6 md:p-0 
                    md:space-x-6 space-y-4 md:space-y-0
                    transition-all ease-in-out z-50
                `}>

                    {(completionPercentage < 100) && !currentUser.isAdmin && !currentUser.isViewer && !currentUser.isEditor ? (
                        <>
                            <button onClick={() => { setView('complete-profile'); setIsMenuOpen(false); }} className="opacity-70 cursor-pointer text-stone-500 hover:text-matrimony-600 font-medium flex items-center gap-2 transition-colors">
                                <span>All Matches</span> <span className="text-[10px] font-bold bg-stone-100 px-2 py-0.5 rounded border border-stone-200 text-stone-500">LOCKED</span>
                            </button>
                            <button onClick={() => { setView('complete-profile'); setIsMenuOpen(false); }} className="opacity-70 cursor-pointer text-stone-500 hover:text-matrimony-600 font-medium flex items-center gap-2 transition-colors">
                                <span>AI Matches ✨</span> <span className="text-[10px] font-bold bg-stone-100 px-2 py-0.5 rounded border border-stone-200 text-stone-500">LOCKED</span>
                            </button>
                            <button onClick={() => { setView('complete-profile'); setIsMenuOpen(false); }} className="hidden md:flex opacity-70 cursor-pointer text-stone-500 hover:text-matrimony-600 font-medium items-center gap-2 transition-colors">
                                <span>Inbox</span> <span className="text-[10px] font-bold bg-stone-100 px-2 py-0.5 rounded border border-stone-200 text-stone-500">LOCKED</span>
                            </button>
                            <button onClick={() => { showNotification("Please complete your profile to access notifications.", "error"); setShowProfileModal(true); }} className="hidden md:flex opacity-70 cursor-pointer text-stone-500 hover:text-matrimony-600 font-medium items-center gap-2 transition-colors">
                                <span>Notifications</span> <span className="text-[10px] font-bold bg-stone-100 px-2 py-0.5 rounded border border-stone-200 text-stone-500">LOCKED</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <NavLink viewName="matches" setView={setView} setIsMenuOpen={setIsMenuOpen}>
                                <span className="relative group/link text-stone-600 hover:text-matrimony-600 font-medium transition-colors">
                                    All Matches
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-matrimony-600 transition-all duration-300 group-hover/link:w-full"></span>
                                </span>
                            </NavLink>
                            <NavLink viewName="ai-matches" setView={setView} setIsMenuOpen={setIsMenuOpen}>
                                <span className="font-bold text-matrimony-600 hover:text-matrimony-500 transition-all flex items-center gap-1 group/ai">
                                    AI Matches
                                    <span className="text-xl group-hover/ai:animate-bounce">✨</span>
                                </span>
                            </NavLink>
                            <button onClick={handleInboxClick} className="hidden md:flex relative group/inbox text-stone-600 hover:text-matrimony-600 font-medium transition-colors items-center gap-2" aria-label="Open Inbox">
                                <div className="relative">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                    {totalUnreadCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-matrimony-500 text-[10px] font-bold text-white shadow-sm">{totalUnreadCount}</span>
                                    )}
                                </div>
                                <span className="md:hidden">Inbox</span>
                            </button>
                            <button onClick={handleNotificationClick} className="hidden md:flex relative group/notification text-stone-600 hover:text-matrimony-600 font-medium transition-colors items-center gap-2" aria-label="Open Notifications">
                                <div className="relative">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {unreadNotificationsCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-matrimony-500 text-[10px] font-bold text-white shadow-sm animate-pulse">{unreadNotificationsCount}</span>
                                    )}
                                </div>
                                <span className="md:hidden">Notifications</span>
                            </button>
                        </>
                    )}

                    {(completionPercentage < 100) && !currentUser.isAdmin && !currentUser.isViewer && !currentUser.isEditor ? (
                        <div className="md:bg-stone-50 md:px-4 md:py-2 rounded-xl md:border md:border-stone-100">
                            <ProfileCompletionIndicator />
                        </div>
                    ) : (
                        <NavLink viewName="my-profile" setView={setView} setIsMenuOpen={setIsMenuOpen}>
                            <div className="flex items-center gap-2 group/profile">
                                <div className="w-9 h-9 rounded-full bg-white p-[2px] shadow-sm border border-stone-200 group-hover/profile:border-matrimony-300 transition-all">
                                    <div className="w-full h-full rounded-full bg-stone-100 flex items-center justify-center overflow-hidden">
                                        {currentUser.images?.[0] ? (
                                            <img src={currentUser.images[0]} className="w-full h-full object-cover" alt="Me" />
                                        ) : (
                                            <span className="text-xs font-bold text-stone-500">{currentUser?.name?.[0] || 'U'}</span>
                                        )}
                                    </div>
                                </div>
                                <span className="text-stone-700 hover:text-matrimony-600 font-medium transition-colors hidden md:inline-block max-w-[100px] truncate">{currentUser?.name || 'My Profile'}</span>
                                <span className="text-stone-700 hover:text-matrimony-600 font-medium transition-colors md:hidden">My Profile</span>
                            </div>
                        </NavLink>
                    )}

                    {(currentUser?.isAdmin || currentUser?.isEditor) && (
                        <NavLink viewName="admin" setView={setView} setIsMenuOpen={setIsMenuOpen}>
                            <span className="font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-full transition-all flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                Admin Panel
                            </span>
                        </NavLink>
                    )}

                    {!Capacitor.isNativePlatform() && (
                        <a href="/myluckyspouse.apk" download className="group relative w-full md:w-auto overflow-hidden rounded-full bg-gradient-to-r from-matrimony-50 to-rose-50 px-4 py-2 text-matrimony-600 font-bold border border-matrimony-100 transition-all hover:shadow-premium hover:-translate-y-0.5 transform">
                            <div className="flex items-center justify-center gap-2 relative z-10 text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>Get App</span>
                            </div>
                        </a>
                    )}

                    <button onClick={handleLogout} className="mt-4 md:mt-0 w-full md:w-auto px-4 py-2 text-stone-500 bg-white/50 border border-stone-200 rounded-full hover:bg-white hover:text-stone-800 font-semibold transition-all flex items-center justify-center gap-2 shadow-sm text-sm hover:-translate-y-0.5 transform">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Logout
                    </button>
                </div>
            </nav>
        </header>
    );
};


// --- REFACTORED CHAT WIDGET FOR MULTIPLE WINDOWS ---
const ChatWidget = ({ chatUser, currentUser, onClose, showNotification, handleBlockUser, handleUnblockUser, handleDisconnectUser }) => {
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null); // Ref for the input field
    const getChatId = (uid1, uid2) => uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const hasBlockedMe = chatUser?.blockedUsers?.includes(currentUser.id);
    const haveIBlocked = currentUser?.blockedUsers?.includes(chatUser.id);
    const isBlocked = hasBlockedMe || haveIBlocked;

    useEffect(() => {
        if (!currentUser || !chatUser) { setMessages([]); return; };
        const chatId = getChatId(currentUser.id, chatUser.id);
        if (!chatId) return;
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            // Optimistic UI: Removed hasPendingWrites check to show messages instantly
            // if (querySnapshot.hasPendingWrites) return;

            const fetchedMessages = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(msg => !(msg.blocked === true && msg.senderId !== currentUser.id));
            setMessages(fetchedMessages);

            // Mark messages as read
            fetchedMessages.forEach(async (msg) => {
                try {
                    if (msg.senderId === chatUser.id && (msg.status === 'sent' || msg.status === 'delivered')) {
                        const msgRef = doc(db, 'chats', chatId, 'messages', msg.id);
                        await updateDoc(msgRef, { status: 'read' });
                    }
                } catch (error) {
                    // Silently fail status updates
                }
            });

        });
        return () => unsubscribe();
    }, [chatUser, currentUser]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (isBlocked) return;
        const textToSend = messageText.trim();
        if (textToSend === '' || !currentUser || !chatUser) return;

        // --- SMART PHONE NUMBER BLOCKING ---
        const normalizeAndAnalyze = (str) => {
            if (!str) return { normalized: '', digitIndices: [] };

            const numberWords = {
                'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
                'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9'
            };

            let normalized = str.toLowerCase();
            // Replace number words with digits (word boundary aware)
            for (const word in numberWords) {
                normalized = normalized.replace(new RegExp(`\\b${word}\\b`, 'g'), numberWords[word]);
            }

            // Find indices of all digits
            const digitIndices = [];
            for (let i = 0; i < normalized.length; i++) {
                if (/\d/.test(normalized[i])) {
                    digitIndices.push(i);
                }
            }

            return { normalized, digitIndices };
        };

        // Combine history (limit to last 15 to catch slow counts) + current
        const historyText = messages
            .filter(m => m.senderId === currentUser.id)
            .slice(-15)
            .map(m => m.text)
            .join(' ');

        const fullText = historyText + ' ' + textToSend;
        // Analyze History and Full Text Separately to find boundary
        const { normalized: normHistory } = normalizeAndAnalyze(historyText);
        const { normalized: normFull, digitIndices } = normalizeAndAnalyze(fullText);

        if (digitIndices.length >= 10) {
            // Check the span of the last 10 digits
            const last10Indices = digitIndices.slice(-10);
            const endIndex = last10Indices[9]; // 10th digit

            // BOUNDARY CHECK: If the 10th digit is entirely within the history part,
            // then the user isn't adding a new digit to complete the sequence.
            // Logic: If endIndex < length of normalized history, it's an old pattern.
            if (endIndex >= normHistory.length) {
                const startIndex = last10Indices[0];
                const suspectSpan = normFull.substring(startIndex, endIndex + 1);

                // Count alpha characters (a-z) in this span
                const alphaCount = (suspectSpan.match(/[a-z]/g) || []).length;

                // THRESHOLD: If fewer than 5 letters appear between the start and end of the 10-digit sequence, it's a number.
                if (alphaCount < 5) {
                    showNotification("Strict Policy: Sharing contact numbers is prohibited.", "error");
                    return;
                }
            }
        }

        setMessageText('');
        setShowEmojiPicker(false);
        // Keep focus on input to prevent keyboard from closing
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 10);

        const chatId = getChatId(currentUser.id, chatUser.id);
        if (!chatId) return;

        try {
            const chatDocRef = doc(db, 'chats', chatId);
            await setDoc(chatDocRef, {
                participants: [currentUser.id, chatUser.id],
                lastMessage: textToSend,
                lastMessageTime: serverTimestamp()
            }, { merge: true });
        } catch (e) {
            console.error('Error creating chat doc:', e);
            showNotification('Failed to create chat doc. ' + e.message, 'error');
            return;
        }

        try {
            const isReceiverBlockingMe = chatUser?.blockedUsers?.includes(currentUser.id);
            await addDoc(collection(db, 'chats', chatId, 'messages'), {
                text: textToSend,
                senderId: currentUser.id,
                timestamp: serverTimestamp(),
                status: 'sent',
                ...(isReceiverBlockingMe ? { blocked: true } : {})
            });
        } catch (error) {
            console.error('Error sending message doc:', error);
            showNotification('Failed to send message. ' + error.message, 'error');
            setMessageText(textToSend);
        }
    };

    const onEmojiClick = (emojiObject) => {
        setMessageText(prevInput => prevInput + emojiObject.emoji);
    };

    if (!chatUser) { return null; }
    return (
        <div className="fixed inset-0 md:static md:w-80 md:h-[450px] lg:w-96 lg:h-[500px] bg-stone-50/95 md:bg-white/95 backdrop-blur-xl rounded-none md:rounded-2xl shadow-premium flex flex-col z-[100] animate-fade-in-up border border-stone-200 overflow-hidden">
            <div className="bg-white/80 backdrop-blur-md text-stone-800 border-b border-stone-100 p-4 pt-14 md:pt-4 rounded-t-none md:rounded-t-2xl flex justify-between items-center shadow-sm z-10 sticky top-0">
                <div className="flex items-center flex-1 min-w-0">
                    <button onClick={onClose} className="mr-3 md:hidden text-stone-400 hover:text-stone-700 flex-shrink-0 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-lg leading-tight text-stone-900 break-words font-serif">{chatUser.name || 'Chat'}</h3>
                        {isProfileOnline(chatUser) ? (
                            <p className="text-xs text-emerald-500 font-medium flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                                Active now
                            </p>
                        ) : (
                            <p className="text-xs text-stone-500 font-medium flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 inline-block"></span>
                                Active: {getLastActive(chatUser)}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 ml-2">
                    {currentUser && currentUser.id !== chatUser.id && (
                        <>
                            <button 
                                type="button"
                                onClick={() => { if (window.confirm(`Are you sure you want to disconnect from ${chatUser.name || 'this user'}? This will delete the chat history and connection.`)) handleDisconnectUser(chatUser.id); }}
                                className="p-1.5 text-stone-400 hover:text-red-500 bg-white hover:bg-red-50 rounded-full transition-all border border-stone-200 hover:border-red-200" 
                                title="Disconnect Chat"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6" />
                                </svg>
                            </button>
                            {currentUser.blockedUsers?.includes(chatUser.id) ? (
                                <button 
                                    type="button"
                                    onClick={() => handleUnblockUser(chatUser.id)}
                                    className="p-1.5 text-emerald-600 hover:text-emerald-700 bg-white hover:bg-emerald-50 rounded-full transition-all border border-emerald-200" 
                                    title="Unblock User"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </button>
                            ) : (
                                <button 
                                    type="button"
                                    onClick={() => { if (window.confirm(`Are you sure you want to block ${chatUser.name || 'this user'}?`)) handleBlockUser(chatUser.id); }}
                                    className="p-1.5 text-stone-400 hover:text-red-500 bg-white hover:bg-red-50 rounded-full transition-all border border-stone-200 hover:border-red-200" 
                                    title="Block User"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                </button>
                            )}
                        </>
                    )}
                    <button onClick={onClose} className="text-2xl font-bold text-stone-300 hover:text-stone-700 flex items-center justify-center w-8 h-8 rounded-full hover:bg-stone-100 transition-colors" aria-label="Close Chat">&times;</button>
                </div>
            </div>
            <div className="flex-1 p-4 overflow-y-auto bg-transparent space-y-3 scrollbar-hide">
                {messages.map(msg => (
                    <ChatMessage key={msg.id} message={msg} currentUserId={currentUser.id} />
                ))}
                <div ref={messagesEndRef} />
            </div>
            {showEmojiPicker && !isBlocked && (
                <div className="absolute bottom-20 right-0 z-10 w-full">
                    <EmojiPicker onEmojiClick={onEmojiClick} width="100%" height={350} theme="dark" />
                </div>
            )}
            <div className="p-3 md:p-4 bg-white/90 backdrop-blur-md rounded-b-none md:rounded-b-2xl relative border-t border-stone-100 pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:pb-4 shadow-[0_-4px_10px_-5px_rgba(0,0,0,0.05)]">
                <form onSubmit={handleSendMessage} className={`flex gap-2 w-full items-center bg-stone-50 rounded-full border border-stone-200 p-1 pr-2 shadow-inner focus-within:border-matrimony-500 focus-within:ring-1 focus-within:ring-matrimony-200 transition-all ${isBlocked ? 'opacity-60 bg-stone-100/50 cursor-not-allowed border-stone-200' : ''}`}>
                    <button type="button" disabled={isBlocked} onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 text-stone-400 hover:text-matrimony-500 disabled:pointer-events-none disabled:opacity-30 flex-shrink-0 transition-colors focus:outline-none rounded-full hover:bg-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>
                    <input 
                        ref={inputRef} 
                        type="text" 
                        value={messageText} 
                        onChange={(e) => setMessageText(e.target.value)} 
                        placeholder={
                            haveIBlocked 
                                ? "You have blocked this user. Unblock to chat." 
                                : hasBlockedMe 
                                    ? "You cannot send messages to this user." 
                                    : "Type a message..."
                        } 
                        disabled={isBlocked}
                        className="flex-1 min-w-0 py-2 px-2 bg-transparent border-none focus:outline-none focus:ring-0 placeholder-stone-400 disabled:placeholder-stone-500/70 disabled:cursor-not-allowed text-stone-800 text-sm" 
                    />
                    <button type="submit" disabled={isBlocked || !messageText.trim()} className="p-2 text-white bg-matrimony-600 hover:bg-matrimony-700 rounded-full disabled:opacity-50 disabled:bg-stone-300 disabled:text-stone-500 transition-all flex-shrink-0 shadow-sm transform active:scale-95 flex justify-center items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px] transform rotate-0 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                    </button>
                </form>
            </div>
        </div>
    );
};


const VerifyEmailPage = ({ setView }) => (<div className="min-h-screen flex items-center justify-center p-4 bg-stone-50"><div className="max-w-md w-full bg-white border border-stone-100 rounded-3xl shadow-soft p-10 text-center space-y-6"><div className="w-20 h-20 bg-matrimony-50 text-matrimony-600 rounded-full flex items-center justify-center mx-auto mb-4"><svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div><h2 className="text-3xl font-bold text-stone-900 font-serif">Verify Your Email</h2><p className="text-stone-500 leading-relaxed">A verification link has been sent to your email address. Please check your inbox (and spam folder) and click the link to activate your account.</p><button onClick={() => setView('auth')} className="w-full px-4 py-4 mt-2 text-white font-bold bg-matrimony-600 rounded-2xl hover:bg-matrimony-700 shadow-md transition-all">Back to Login</button></div></div>);
const Footer = ({ onTermsClick, onAdminClick, currentUser, setView }) => {
    const handlePrivacyClick = (e) => {
        e.preventDefault();
        window.history.pushState({}, '', '/privacy');
        setView('privacy-policy');
    };
    return (
        <footer className="bg-white border-t border-stone-100 py-12">
            <div className="container mx-auto px-6 text-center text-stone-500 relative z-10">
                <p className="font-medium">&copy; {new Date().getFullYear()} MyLuckySpouse. All Rights Reserved. | Support: <a href="tel:+919175654627" className="text-matrimony-600 font-bold hover:text-matrimony-700 transition-colors">+91 91756 54627</a></p>
                <div className='flex justify-center items-center divide-x divide-stone-300 mt-6'>
                    <button onClick={onTermsClick} className="text-sm px-4 hover:text-matrimony-600 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-matrimony-200 rounded">Terms and Conditions</button>
                    <a href="/privacy" onClick={handlePrivacyClick} className="text-sm px-4 hover:text-matrimony-600 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-matrimony-200 rounded">Privacy Policy</a>
                </div>
            </div>
        </footer>
    );
};

const TermsAndConditionsModal = ({ onClose }) => (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex justify-center items-center z-[100] p-4 animate-fade-in-up">
        <div className="bg-white border border-stone-100 p-6 md:p-8 rounded-3xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white/95 backdrop-blur-md py-4 border-b border-stone-100 z-10">
                <h2 className="text-3xl font-bold text-stone-900 font-serif">Terms and Conditions</h2>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-3xl font-bold transition-colors">&times;</button>
            </div>
            <div className="prose max-w-none text-stone-600 pr-4 prose-headings:text-stone-900 prose-headings:font-serif prose-strong:text-stone-800 prose-a:text-matrimony-600 prose-a:font-medium">
                <p className="text-sm text-gray-500">Effective Date: 1st January 2025</p>
                <p>Welcome to MyluckySpouse.com (“Website”, “We”, “Our”, “Us”). By accessing or using our website, you agree to comply with and be legally bound by these Terms & Conditions (“T&C”). If you do not agree, please discontinue use immediately.</p>

                <h3>1. Eligibility</h3>
                <ul>
                    <li>You must be at least 18 years of age (for females) and 21 years of age (for males) as per Indian marriage laws.</li>
                    <li>By registering, you represent that you have the legal capacity to enter into a contract of marriage.</li>
                    <li>Membership is strictly for individuals seeking marriage and not for casual dating or other purposes.</li>
                </ul>

                <h3>2. Account Registration & Responsibilities</h3>
                <ul>
                    <li>You agree to provide accurate and truthful information during registration.</li>
                    <li>You are solely responsible for maintaining the confidentiality of your login credentials.</li>
                    <li>You agree not to:
                        <ul>
                            <li>Create multiple accounts.</li>
                            <li>Impersonate another person.</li>
                            <li>Post false or obscene information.</li>
                        </ul>
                    </li>
                    <li>You will be held responsible for all activities under your account.</li>
                </ul>

                <h3>3. User Obligations</h3>
                <ul>
                    <li>You shall not use the website for harassment, abuse, illegal activities, or sharing obscene content.</li>
                    <li>The website reserves the right to delete, suspend, or terminate accounts violating these rules.</li>
                </ul>

                <h3>4. Membership & Payments (if applicable)</h3>
                <ul>
                    <li>Fees for paid services are non-refundable.</li>
                    <li>We reserve the right to modify, discontinue, or change pricing without notice.</li>
                </ul>

                <h3>5. Content & Intellectual Property</h3>
                <ul>
                    <li>All content, design, logos, and intellectual property of MyluckySpouse.com are owned by us.</li>
                    <li>Users may not copy or distribute content from the site without written permission.</li>
                </ul>

                <h3>6. Privacy & Data Protection</h3>
                <ul>
                    <li>We value your privacy and handle personal information as per our <a href="https://myluckyspouse.web.app/privacy" target="_blank">Privacy Policy</a>.</li>
                    <li>We do not guarantee the authenticity of members. Users are advised to verify details independently.</li>
                </ul>

                <h3>7. Limitation of Liability</h3>
                <ul>
                    <li>We are not liable for any loss, fraud, or disputes arising from the use of this site.</li>
                    <li>Users are advised to exercise caution, verify details, and seek legal/parental guidance before marriage.</li>
                </ul>

                <h3>8. Termination</h3>
                <ul>
                    <li>We may suspend or terminate your account for violation of T&C, misuse, or complaints.</li>
                    <li>You may terminate your account anytime by contacting us.</li>
                </ul>

                <h3>9. Indemnification</h3>
                <p>You agree to indemnify and hold harmless MyluckySpouse.com, its owners, employees, and affiliates from any claims, damages, or expenses arising out of your use of the website.</p>

                <h3>10. Governing Law & Jurisdiction</h3>
                <ul>
                    <li>These Terms shall be governed by the laws of India.</li>
                    <li>Any disputes shall be subject to the exclusive jurisdiction of the courts of Pune, Maharashtra, India.</li>
                </ul>

                <h3>11. Changes to Terms</h3>
                <p>We may update or modify these Terms at any time without prior notice. Continued use of the website implies acceptance of the revised Terms.</p>

                <h3>12. Contact Us</h3>
                <p>For any queries or complaints, please contact: 📧 Email: support@myluckyspouse.com</p>
            </div>
        </div>
    </div>
);
const Notification = ({ message, type }) => { if (!message) return null; const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'; return (<div className={`fixed top-5 right-5 ${bgColor} text-white py-2 px-4 rounded-lg shadow-lg animate-fade-in-down z-[100]`}>{message}</div>); };

const ChatListPopup = ({ unreadChats, profiles, onChatItemClick, onClose, currentUserId, chatRequests = [], onAcceptRequest, onDeclineRequest, onUnblockRequest, currentUser }) => {
    const chatsWithDetails = Object.entries(unreadChats).map(([chatId, data]) => {
        const otherUserId = chatId.replace(currentUserId, '').replace('_', '');
        const profile = profiles.find(p => p.id === otherUserId);
        return { ...data, profile, chatId, otherUserId };
    }).filter(chat => {
        if (!chat.profile || !chat.lastMessage) return false;
        return true;
    }).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    const incomingRequests = (chatRequests || []).filter(r => 
        r.status === 'pending' && 
        r.receiverId === currentUserId
    ).map(r => {
        const senderProfile = profiles.find(p => p.id === r.senderId);
        return { ...r, senderProfile };
    }).filter(r => {
        if (!r.senderProfile) return false;
        return true;
    });

    const currentUserProfile = currentUser || profiles.find(p => p.id === currentUserId);
    const blockedProfiles = profiles.filter(p => currentUserProfile?.blockedUsers?.includes(p.id));

    return (
        <div className="fixed inset-0 md:inset-auto md:absolute md:top-20 md:right-4 bg-stone-50/95 md:bg-white/95 backdrop-blur-xl z-[90] md:z-[60] w-full h-full md:w-96 md:h-auto md:rounded-2xl md:shadow-premium border border-stone-200 flex flex-col pt-16 md:pt-0 overflow-hidden">
            <div className="px-4 py-4 pt-14 md:pt-4 flex justify-between items-center bg-white/80 backdrop-blur-md sticky top-0 z-10 border-b border-stone-100">
                <h3 className="font-bold text-2xl text-stone-900 font-serif">Chats</h3>
                <div className="flex gap-4">
                    <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-700 bg-white hover:bg-stone-100 rounded-full transition-colors shadow-sm border border-stone-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M13.414 12l4.293 4.293a1 1 0 01-1.414 1.414L12 13.414l-4.293 4.293a1 1 0 01-1.414-1.414L10.586 12 6.293 7.707a1 1 0 011.414-1.414L12 10.586l4.293-4.293a1 1 0 011.414 1.414L13.414 12z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto md:max-h-[500px] flex flex-col">
                {/* Connection Requests Section */}
                {incomingRequests.length > 0 && (
                    <div className="border-b border-stone-200 bg-rose-50/30 p-4">
                        <h4 className="font-bold text-xs text-rose-700 uppercase tracking-widest mb-3">Connection Requests</h4>
                        <div className="space-y-3">
                            {incomingRequests.map(req => (
                                <div key={req.id} className="flex items-center justify-between bg-white border border-rose-100 rounded-2xl p-3 shadow-sm animate-fade-in-up">
                                    <div className="flex items-center min-w-0 mr-2">
                                        <img src={req.senderProfile.images?.[0] || PLACEHOLDER_IMAGE} alt={req.senderProfile.name} className="w-10 h-10 rounded-full object-cover mr-2.5 shadow-sm border border-stone-100" />
                                        <div className="min-w-0">
                                            <p className="font-bold text-stone-900 text-sm truncate">{req.senderProfile.name}</p>
                                            <p className="text-xs text-stone-500 truncate">{calculateAge(req.senderProfile.dob)} yrs, {req.senderProfile.city}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5 flex-shrink-0">
                                        <button 
                                            onClick={() => onAcceptRequest(req.id)} 
                                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all focus:outline-none"
                                        >
                                            Accept
                                        </button>
                                        <button 
                                            onClick={() => onDeclineRequest(req.id)} 
                                            className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold text-xs rounded-xl transition-all focus:outline-none"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Active Chats Section */}
                <div className="flex-grow">
                    {chatsWithDetails.length > 0 ? (chatsWithDetails.map(chat => (
                        <div key={chat.chatId} onClick={() => onChatItemClick(chat.profile)} className="flex items-center p-3 hover:bg-stone-50 cursor-pointer border-b border-stone-100 last:border-b-0 transition-colors bg-white">
                            <img src={chat.profile.images?.[0] || PLACEHOLDER_IMAGE} alt={chat.profile.name} className="w-12 h-12 rounded-full object-cover mr-3 shadow-sm border border-stone-100" />
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-stone-900">{chat.profile.name}</p>
                                <p className="text-sm text-stone-500 truncate">{chat.lastMessage}</p>
                            </div>
                            {chat.count > 0 && (<span className="ml-2 bg-matrimony-500 text-white text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 shadow-sm">{chat.count}</span>)}
                        </div>
                    ))) : (<p className="p-8 text-center text-stone-500 font-medium">No active chats.</p>)}
                </div>

                {/* Blocked Users Section */}
                {blockedProfiles.length > 0 && (
                    <div className="border-t border-stone-200 bg-stone-100/50 p-4 mt-auto">
                        <h4 className="font-bold text-xs text-stone-500 uppercase tracking-widest mb-3">Blocked Users</h4>
                        <div className="space-y-2">
                            {blockedProfiles.map(blockedUser => (
                                <div key={blockedUser.id} className="flex items-center justify-between bg-white border border-stone-200 rounded-2xl p-3 shadow-sm animate-fade-in-up">
                                    <div className="flex items-center min-w-0 mr-2">
                                        <img src={blockedUser.images?.[0] || PLACEHOLDER_IMAGE} alt={blockedUser.name} className="w-10 h-10 rounded-full object-cover mr-2.5 shadow-sm border border-stone-100" />
                                        <div className="min-w-0">
                                            <p className="font-bold text-stone-900 text-sm truncate">{blockedUser.name}</p>
                                        </div>
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => onUnblockRequest(blockedUser.id)} 
                                        className="px-3 py-1.5 bg-stone-50 hover:bg-stone-100 text-stone-700 font-bold text-xs rounded-xl transition-all focus:outline-none border border-stone-250 hover:border-stone-300"
                                    >
                                        Unblock
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const NotificationListPopup = ({ notifications, onClose, onNotificationItemClick, profiles, onAcceptRequest, onDeclineRequest, onClearNotification, onClearAllNotifications }) => {
    return (
        <div className="fixed inset-0 md:inset-auto md:absolute md:top-20 md:right-4 bg-stone-50/95 md:bg-white/95 backdrop-blur-xl z-[90] md:z-[60] w-full h-full md:w-96 md:h-auto md:rounded-2xl md:shadow-premium border border-stone-200 flex flex-col pt-16 md:pt-0 overflow-hidden animate-fade-in-up">
            <div className="px-4 py-4 pt-14 md:pt-4 flex justify-between items-center bg-white/80 backdrop-blur-md sticky top-0 z-10 border-b border-stone-100">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-2xl text-stone-900 font-serif">Notifications</h3>
                    {notifications.length > 0 && (
                        <button 
                            onClick={onClearAllNotifications}
                            className="text-[10px] text-red-655 hover:text-red-700 bg-red-50 hover:bg-red-105 px-2 py-0.5 rounded transition-colors font-bold uppercase tracking-wider border border-red-200"
                            title="Clear All Notifications"
                        >
                            Clear All
                        </button>
                    )}
                </div>
                <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-700 bg-white hover:bg-stone-100 rounded-full transition-colors shadow-sm border border-stone-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M13.414 12l4.293 4.293a1 1 0 01-1.414 1.414L12 13.414l-4.293 4.293a1 1 0 01-1.414-1.414L10.586 12 6.293 7.707a1 1 0 011.414-1.414L12 10.586l4.293-4.293a1 1 0 011.414 1.414L13.414 12z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
            
            <div className="flex-1 overflow-y-auto md:max-h-[500px] flex flex-col">
                {notifications.length > 0 ? (
                    <div className="divide-y divide-stone-100">
                        {notifications.map(n => {
                            const senderProfile = profiles.find(p => p.id === n.fromUserId || p.id === n.senderId);
                            const name = n.fromUserName || n.senderName || 'Someone';
                            const profilePic = n.fromUserProfilePic || senderProfile?.images?.[0];
                            const timeString = n.timestamp ? timeAgo(n.timestamp.toDate ? n.timestamp.toDate() : new Date(n.timestamp)) : 'Just now';

                            // Determine action message based on type
                            let message = n.message || 'sent a notification';
                            if (n.type === 'chat_request') {
                                message = 'sent you a connection request';
                            } else if (n.type === 'chat_accept') {
                                message = 'accepted your connection request';
                            } else if (n.type === 'like') {
                                message = 'liked your profile';
                            }

                            return (
                                <div 
                                    key={n.id} 
                                    onClick={() => senderProfile && onNotificationItemClick(senderProfile, n.type)}
                                    className={`p-4 flex items-start gap-3 hover:bg-stone-50 cursor-pointer transition-colors relative ${!n.read ? 'bg-rose-50/20' : 'bg-white'}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm border border-stone-100">
                                        {profilePic ? (
                                            <img src={profilePic} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                            <span className="text-sm font-bold text-stone-500">{name[0]}</span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-stone-850 leading-snug">
                                            <span className="font-bold text-stone-900">{name}</span> {message}
                                        </p>
                                        <p className="text-xs text-stone-400 mt-1 font-medium">{timeString}</p>
                                        
                                        {/* Action buttons directly in notification item for connection requests */}
                                        {n.type === 'chat_request' && (
                                            <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
                                                <button 
                                                    onClick={() => onAcceptRequest(n.id, n.fromUserId)} 
                                                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg shadow-sm transition-all focus:outline-none"
                                                >
                                                    Accept
                                                </button>
                                                <button 
                                                    onClick={() => onDeclineRequest(n.id, n.fromUserId)} 
                                                    className="px-3 py-1 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold text-xs rounded-lg transition-all focus:outline-none border border-stone-200"
                                                >
                                                    Decline
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                        {!n.read && (
                                            <span className="w-2.5 h-2.5 rounded-full bg-matrimony-500"></span>
                                        )}
                                        <button 
                                            onClick={(e) => onClearNotification(n.id, e)}
                                            className="p-1 text-stone-400 hover:text-red-500 rounded-full hover:bg-stone-100 transition-colors"
                                            title="Clear Notification"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center my-auto min-h-[250px]">
                        <div className="w-16 h-16 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-400 mb-4 animate-bounce">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-stone-800 text-lg">No Notifications Yet</h4>
                        <p className="text-xs text-stone-400 max-w-[200px] mt-1 mx-auto leading-relaxed">
                            When you receive connection requests or acceptance alerts, they will appear here.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};




// --- Admin Dashboard Components ---
const AdminDashboardHeader = ({ currentUser }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [sessionDuration, setSessionDuration] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
            setSessionDuration(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatDuration = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs > 0 ? `${hrs}h ` : ''}${mins}m ${secs}s`;
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 18) return "Good Afternoon";
        return "Good Evening";
    };

    return (
        <div className="mb-8 p-6 rounded-3xl bg-white border border-stone-100 shadow-sm relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <p className="text-stone-500 text-sm font-medium tracking-wider uppercase mb-1">{currentTime.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <h1 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
                        {getGreeting()}, <span className="text-matrimony-600">{currentUser.name || 'Admin'}</span>
                    </h1>
                </div>

                <div className="flex gap-4 md:gap-8 bg-stone-50 p-3 rounded-2xl border border-stone-100">
                    <div className="text-center px-2">
                        <p className="text-xs text-stone-400 uppercase tracking-widest font-bold">Current Time</p>
                        <p className="text-xl font-mono font-medium text-stone-800">{currentTime.toLocaleTimeString()}</p>
                    </div>
                    <div className="w-px bg-stone-200"></div>
                    <div className="text-center px-2">
                        <p className="text-xs text-stone-400 uppercase tracking-widest font-bold">Session</p>
                        <p className="text-xl font-mono font-medium text-matrimony-600">{formatDuration(sessionDuration)}</p>
                    </div>
                </div>
            </div>
            {currentUser.metadata?.lastSignInTime && (
                <div className="relative z-10 mt-6 flex items-center text-xs text-stone-500 font-medium">
                    <svg className="w-4 h-4 mr-1 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                    Last Login: {new Date(currentUser.metadata.lastSignInTime).toLocaleString()}
                </div>
            )}
        </div>
    );
};

const AdminStatsGrid = ({ stats, activeFilter, onFilterClick }) => {
    const StatCard = ({ id, title, value, color, iconPath, bgClass, iconColorClass }) => {
        const isActive = activeFilter === id;
        return (
            <div
                onClick={() => onFilterClick(id)}
                className={`relative overflow-hidden rounded-2xl p-5 bg-white border transition-all duration-300 cursor-pointer group ${isActive ? 'ring-2 ring-matrimony-500 border-matrimony-200 shadow-md transform -translate-y-1' : 'border-stone-100 hover:border-stone-200 hover:shadow-sm'}`}
            >
                <div className="flex justify-between items-start">
                    <div className="z-10">
                        <p className={`text-xs font-bold uppercase tracking-widest mb-1 transition-colors ${isActive ? 'text-matrimony-600' : 'text-stone-500 group-hover:text-stone-700'}`}>{title}</p>
                        <h3 className="text-3xl font-black text-stone-900">{value}</h3>
                    </div>
                    <div className={`p-3 rounded-xl ${bgClass} ${iconColorClass} transition-transform ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                        </svg>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <StatCard id="all" title="Total Users" value={stats.totalUsers} bgClass="bg-indigo-50" iconColorClass="text-indigo-600"
                iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            <StatCard id="online" title="Online" value={stats.onlineUsers} bgClass="bg-emerald-50" iconColorClass="text-emerald-600"
                iconPath="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
            <StatCard id="verified" title="Verified" value={stats.verifiedUsers} bgClass="bg-violet-50" iconColorClass="text-violet-600"
                iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            <StatCard id="male" title="Male" value={stats.maleUsers} bgClass="bg-sky-50" iconColorClass="text-sky-600"
                iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            <StatCard id="female" title="Female" value={stats.femaleUsers} bgClass="bg-rose-50" iconColorClass="text-rose-600"
                iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            <StatCard id="App" title="App Users" value={stats.appUsers} bgClass="bg-amber-50" iconColorClass="text-amber-600"
                iconPath="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            <StatCard id="Web" title="Web Users" value={stats.webUsers} bgClass="bg-fuchsia-50" iconColorClass="text-fuchsia-600"
                iconPath="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </div>
    );
};

const AdminNotesModal = ({ isOpen, onClose, user, currentUser, showNotification }) => {
    const [notes, setNotes] = useState(user.adminNotes || []);
    const [newNote, setNewNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        setIsSaving(true);
        try {
            const note = {
                id: Date.now().toString(),
                content: newNote.trim(),
                authorName: currentUser.name || 'Admin',
                authorId: currentUser.id,
                timestamp: Date.now()
            };
            const updatedNotes = [note, ...notes];
            await updateDoc(doc(db, "profiles", user.id), {
                adminNotes: updatedNotes
            });
            setNotes(updatedNotes);
            setNewNote('');
            showNotification("Note added successfully!", "success");
        } catch (err) {
            console.error("Error adding note:", err);
            showNotification(`Failed to add note: ${err.message}`, "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex justify-center items-center z-[150] p-4 animate-fade-in">
            <div className="bg-white border border-stone-100 rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[80vh]">
                <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-stone-50">
                    <h3 className="font-bold text-lg text-stone-900 font-serif">Review Notes for {user.name}</h3>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700 transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="p-5 flex-1 overflow-y-auto space-y-4 bg-transparent">
                    <div className="flex gap-2">
                        <textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add a private note regarding this candidate..."
                            className="w-full p-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:ring-2 focus:ring-matrimony-500 focus:border-transparent outline-none resize-none placeholder-stone-400"
                            rows="3"
                        ></textarea>
                        <button
                            onClick={handleAddNote}
                            disabled={isSaving || !newNote.trim()}
                            className="bg-matrimony-600 text-white px-4 rounded-xl font-bold text-sm shadow-sm hover:bg-matrimony-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isSaving ? '...' : 'Add'}
                        </button>
                    </div>

                    <div className="space-y-3 pt-2">
                        {notes.length === 0 ? (
                            <p className="text-center text-stone-500 text-sm italic py-4">No notes added yet.</p>
                        ) : (
                            notes.map(note => (
                                <div key={note.id} className="bg-stone-50 p-4 rounded-xl shadow-sm border border-stone-100 relative group animate-slide-in">
                                    <p className="text-stone-700 text-sm leading-relaxed whitespace-pre-wrap">{note.content}</p>
                                    <div className="mt-3 flex justify-between items-center text-[10px] text-stone-500 font-medium uppercase tracking-wide">
                                        <span className="text-matrimony-600">{note.authorName}</span>
                                        <span>{new Date(note.timestamp).toLocaleDateString()} {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const RecentNotesWidget = ({ profiles, onViewNote }) => {
    // Collect all admin notes
    const recentNotes = useMemo(() => {
        let allNotes = [];
        profiles.forEach(p => {
            if (p.adminNotes && Array.isArray(p.adminNotes)) {
                p.adminNotes.forEach(note => {
                    allNotes.push({
                        ...note,
                        targetUser: p
                    });
                });
            }
        });
        return allNotes.sort((a, b) => b.timestamp - a.timestamp).slice(0, 10);
    }, [profiles]);

    if (recentNotes.length === 0) return null;

    return (
        <div className="mb-8 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
            <div className="bg-stone-50 p-4 border-b border-stone-100 flex justify-between items-center">
                <h3 className="font-bold text-stone-900 font-serif flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                    Recent Activity & Notes
                </h3>
                <span className="text-xs font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">{recentNotes.length} Recent</span>
            </div>
            <div className="max-h-60 overflow-y-auto custom-scrollbar-light p-0 divide-y divide-stone-100">
                {recentNotes.map((note, idx) => (
                    <div key={idx} onClick={() => onViewNote(note.targetUser)} className="p-4 hover:bg-stone-50 transition-colors cursor-pointer group">
                        <div className="flex justify-between items-start mb-1">
                            <p className="text-xs font-bold text-stone-500 uppercase tracking-wide">
                                <span className="text-matrimony-600">{note.authorName}</span> <span className="mx-1">on</span> <span className="text-stone-800 group-hover:text-matrimony-600 transition-colors">{note.targetUser.name}</span>
                            </p>
                            <span className="text-[10px] text-stone-500 bg-stone-100 px-2 py-0.5 rounded border border-stone-200">{timeAgo(note.timestamp)}</span>
                        </div>
                        <p className="text-sm text-stone-600 line-clamp-2 italic">"{note.content}"</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Admin Import Modal Component ---
const AdminImportModal = ({ isOpen, onClose, functions, showNotification }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'mapping', 'importing', 'summary'
    const [file, setFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importLogs, setImportLogs] = useState([]);
    const [summary, setSummary] = useState({ success: 0, failed: 0, warnings: 0, details: [] });

    // Handle Drag & Drop
    const [dragActive, setDragActive] = useState(false);

    if (!isOpen) return null;

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileParsing(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileParsing(e.target.files[0]);
        }
    };

    const handleFileParsing = async (selectedFile) => {
        const name = selectedFile.name;
        const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
        const isCsv = name.endsWith('.csv');
        if (!isExcel && !isCsv) {
            showNotification("Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file.", "error");
            return;
        }

        setFile(selectedFile);
        setIsParsing(true);

        try {
            const XLSX = await import('xlsx');
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const bstr = event.target.result;
                    const wb = XLSX.read(bstr, { type: 'binary' });
                    const wsname = wb.SheetNames[0];
                    const ws = wb.Sheets[wsname];
                    const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

                    if (data.length < 2) {
                        showNotification("The uploaded file does not contain enough data (header row + at least 1 user row required).", "error");
                        setIsParsing(false);
                        return;
                    }

                    const rawHeaders = data[0].map(h => (h || '').toString().trim());
                    // Filter out empty headers
                    const fileHeaders = rawHeaders.filter(h => h !== '');
                    const rows = data.slice(1).map(row => {
                        const rowObj = {};
                        rawHeaders.forEach((header, idx) => {
                            if (header) {
                                rowObj[header] = row[idx] !== undefined ? row[idx] : '';
                            }
                        });
                        return rowObj;
                    });

                    setHeaders(fileHeaders);
                    setParsedRows(rows);

                    // Smart auto-mapping
                    const initialMapping = {
                        email: '',
                        name: '',
                        phone: '',
                        gender: '',
                        // Optional
                        middleName: '',
                        lastName: '',
                        religion: '',
                        caste: '',
                        dob: '',
                        status: '',
                        height: '',
                        motherTongue: '',
                        education: '',
                        employmentType: '',
                        workPlace: '',
                        designation: '',
                        income: ''
                    };

                    const findBestMatch = (fieldKey, fieldLabel, aliases) => {
                        for (const header of fileHeaders) {
                            const hLower = header.toLowerCase();
                            if (hLower === fieldKey.toLowerCase() || hLower === fieldLabel.toLowerCase()) {
                                return header;
                            }
                            for (const alias of aliases) {
                                if (hLower.includes(alias.toLowerCase())) {
                                    return header;
                                }
                            }
                        }
                        return '';
                    };

                    initialMapping.email = findBestMatch('email', 'email', ['e-mail', 'mail', 'email address', 'username']);
                    initialMapping.name = findBestMatch('name', 'name', ['full name', 'candidate name', 'first name', 'candidate_name', 'client name']);
                    initialMapping.phone = findBestMatch('phone', 'phone', ['mobile', 'contact', 'phone number', 'mobile number', 'whatsapp']);
                    initialMapping.gender = findBestMatch('gender', 'gender', ['sex']);
                    initialMapping.lastName = findBestMatch('lastName', 'last name', ['surname', 'family name']);
                    initialMapping.middleName = findBestMatch('middleName', 'middle name', ['father name', 'husband name']);
                    initialMapping.dob = findBestMatch('dob', 'dob', ['date of birth', 'birth date', 'birthdate']);
                    initialMapping.religion = findBestMatch('religion', 'religion', ['faith']);
                    initialMapping.caste = findBestMatch('caste', 'caste', ['community', 'subcaste']);
                    initialMapping.status = findBestMatch('status', 'marital status', ['marital']);
                    initialMapping.education = findBestMatch('education', 'education', ['degree', 'qualification']);
                    initialMapping.income = findBestMatch('income', 'income', ['salary', 'earnings']);

                    setMapping(initialMapping);
                    setStep('mapping');
                } catch (err) {
                    console.error("Error parsing file contents:", err);
                    showNotification("Failed to parse file: " + err.message, "error");
                } finally {
                    setIsParsing(false);
                }
            };
            reader.readAsBinaryString(selectedFile);
        } catch (err) {
            console.error("Error importing xlsx library:", err);
            showNotification("Failed to load Excel parsing library.", "error");
            setIsParsing(false);
        }
    };

    const handleStartImport = async () => {
        if (!mapping.email) {
            showNotification("Candidate Email column mapping is required.", "error");
            return;
        }

        setStep('importing');
        setIsImporting(true);
        setImportProgress(0);
        setImportLogs([]);

        const { httpsCallable } = await import('firebase/functions');
        const createImportedUserCallable = httpsCallable(functions, 'createImportedUser');

        let successCount = 0;
        let failedCount = 0;
        let warningCount = 0;
        const detailsLog = [];

        const logMessage = (msg, type = 'info') => {
            const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            setImportLogs(prev => [...prev, { time, msg, type }]);
        };

        logMessage(`Starting import of ${parsedRows.length} users...`, 'info');

        for (let i = 0; i < parsedRows.length; i++) {
            const row = parsedRows[i];
            const payload = {};

            // Map sheet columns to standard fields
            Object.keys(mapping).forEach(field => {
                const sheetCol = mapping[field];
                if (sheetCol) {
                    payload[field] = row[sheetCol];
                }
            });

            const userEmail = payload.email;
            if (!userEmail) {
                logMessage(`⚠️ Skipping row ${i + 2}: Missing Email.`, 'warn');
                warningCount++;
                continue;
            }

            logMessage(`⏳ [${i + 1}/${parsedRows.length}] Importing ${userEmail}...`, 'info');

            try {
                const response = await createImportedUserCallable(payload);
                const result = response.data;

                if (result.success) {
                    successCount++;
                    if (result.warning) {
                        logMessage(`⚠️ Imported ${userEmail} successfully, but email failed to send: ${result.warning}`, 'warn');
                        warningCount++;
                        detailsLog.push({ email: userEmail, status: 'Success with Warning', details: result.warning });
                    } else if (result.updatedExisting) {
                        logMessage(`✅ Marked existing user ${userEmail} as Imported.`, 'success');
                        detailsLog.push({ email: userEmail, status: 'Success', details: 'Marked existing user as Imported' });
                    } else if (result.createdProfileOnly) {
                        logMessage(`✅ Marked existing Auth user ${userEmail} as Imported.`, 'success');
                        detailsLog.push({ email: userEmail, status: 'Success', details: 'Created profile only' });
                    } else {
                        logMessage(`✅ Imported ${userEmail} successfully. Credentials generated!`, 'success');
                        detailsLog.push({ email: userEmail, status: 'Success', details: `UID: ${result.uid}` });
                    }
                } else {
                    failedCount++;
                    logMessage(`❌ Failed to import ${userEmail}: ${result.error}`, 'error');
                    detailsLog.push({ email: userEmail, status: 'Failed', details: result.error });
                }
            } catch (error) {
                failedCount++;
                logMessage(`❌ Failed to import ${userEmail}: ${error.message}`, 'error');
                detailsLog.push({ email: userEmail, status: 'Failed', details: error.message });
            }

            setImportProgress(Math.round(((i + 1) / parsedRows.length) * 100));
        }

        setIsImporting(false);
        setSummary({ success: successCount, failed: failedCount, warnings: warningCount, details: detailsLog });
        setStep('summary');
    };

    const handleClose = () => {
        // Reset state
        setFile(null);
        setHeaders([]);
        setParsedRows([]);
        setMapping({});
        setImportLogs([]);
        setImportProgress(0);
        setStep('upload');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex justify-center items-center z-[150] p-4 animate-fade-in">
            <div className="bg-white border border-stone-100 rounded-3xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh] animate-slide-in">
                {/* Header */}
                <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-stone-50">
                    <div>
                        <h3 className="font-bold text-xl text-stone-900 font-serif">Import Users from Excel/CSV</h3>
                        <p className="text-xs text-stone-500 font-medium mt-0.5">Generate passwords and email credentials automatically</p>
                    </div>
                    {!isImporting && (
                        <button onClick={handleClose} className="text-stone-400 hover:text-stone-700 transition-colors p-1 hover:bg-stone-100 rounded-full">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="p-6 flex-1 overflow-y-auto bg-white min-h-[300px]">
                    {step === 'upload' && (
                        <div className="space-y-6">
                            <div
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                                className={`border-2 border-dashed rounded-3xl p-12 text-center transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[220px] ${
                                    dragActive ? 'border-matrimony-500 bg-rose-50/20' : 'border-stone-200 hover:border-matrimony-400 bg-stone-50/50 hover:bg-stone-50'
                                }`}
                            >
                                <input
                                    type="file"
                                    className="hidden"
                                    id="excel-file-upload"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    disabled={isParsing}
                                />
                                <label htmlFor="excel-file-upload" className="cursor-pointer block w-full">
                                    {isParsing ? (
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-12 h-12 border-4 border-stone-200 border-t-matrimony-600 rounded-full animate-spin mb-4"></div>
                                            <p className="text-sm font-bold text-stone-700">Parsing sheet data...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="w-16 h-16 bg-white shadow-sm border border-stone-100 rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 group-hover:border-matrimony-200 transition-all text-stone-400 group-hover:text-matrimony-500">
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                            </div>
                                            <p className="text-base font-bold text-stone-700">Drag & Drop your file here</p>
                                            <p className="text-sm text-stone-400 mt-1">or <span className="text-matrimony-600 font-bold underline hover:text-matrimony-700">browse local files</span></p>
                                            <p className="text-[10px] text-stone-400 font-semibold uppercase tracking-wider mt-4">Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                                        </>
                                    )}
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 'mapping' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 text-xs font-semibold flex items-start gap-2.5 shadow-sm">
                                <span className="text-base">💡</span>
                                <p className="leading-relaxed">
                                    We auto-detected column mappings by matching header names. Please verify that <strong>Candidate Email</strong> is mapped correctly, as it is the primary login username for credentials.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-bold text-sm text-stone-800 border-b border-stone-100 pb-2">Column Mapping Config</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[35vh] overflow-y-auto pr-2 custom-scrollbar-light">
                                    {/* Candidate Email - Required */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Candidate Email <span className="text-rose-500">*</span></label>
                                        <select
                                            value={mapping.email}
                                            onChange={(e) => setMapping({ ...mapping, email: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>

                                    {/* Candidate Name */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Candidate Name</label>
                                        <select
                                            value={mapping.name}
                                            onChange={(e) => setMapping({ ...mapping, name: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column (Optional) --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>

                                    {/* Candidate Phone */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Candidate Phone</label>
                                        <select
                                            value={mapping.phone}
                                            onChange={(e) => setMapping({ ...mapping, phone: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column (Optional) --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>

                                    {/* Gender */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Gender</label>
                                        <select
                                            value={mapping.gender}
                                            onChange={(e) => setMapping({ ...mapping, gender: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column (Optional) --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Date of Birth</label>
                                        <select
                                            value={mapping.dob}
                                            onChange={(e) => setMapping({ ...mapping, dob: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column (Optional) --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>

                                    {/* Religion */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">Religion</label>
                                        <select
                                            value={mapping.religion}
                                            onChange={(e) => setMapping({ ...mapping, religion: e.target.value })}
                                            className="w-full p-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-700 font-medium"
                                        >
                                            <option value="">-- Choose Column (Optional) --</option>
                                            {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="border border-stone-100 bg-stone-50/50 rounded-2xl p-4 space-y-2">
                                <p className="text-xs font-bold text-stone-500 uppercase tracking-wide">Data Preview (First 2 Rows)</p>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs text-stone-600 border-collapse">
                                        <thead>
                                            <tr className="border-b border-stone-200">
                                                <th className="py-2 px-2 font-bold uppercase text-stone-500">Email</th>
                                                <th className="py-2 px-2 font-bold uppercase text-stone-500">Name</th>
                                                <th className="py-2 px-2 font-bold uppercase text-stone-500">Phone</th>
                                                <th className="py-2 px-2 font-bold uppercase text-stone-500">Gender</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedRows.slice(0, 2).map((row, idx) => (
                                                <tr key={idx} className="border-b border-stone-100 last:border-none">
                                                    <td className="py-2 px-2 font-mono">{mapping.email ? row[mapping.email] : 'N/A'}</td>
                                                    <td className="py-2 px-2 font-semibold">{mapping.name ? row[mapping.name] : 'N/A'}</td>
                                                    <td className="py-2 px-2 font-medium">{mapping.phone ? row[mapping.phone] : 'N/A'}</td>
                                                    <td className="py-2 px-2 capitalize">{mapping.gender ? row[mapping.gender] : 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 'importing' && (
                        <div className="space-y-6 animate-fade-in flex flex-col items-stretch h-[45vh]">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-bold text-stone-800">Processing Users...</span>
                                    <span className="font-bold text-matrimony-600">{importProgress}%</span>
                                </div>
                                <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden shadow-inner border border-stone-200">
                                    <div
                                        className="bg-gradient-to-r from-matrimony-500 to-rose-600 h-full rounded-full transition-all duration-300 relative overflow-hidden"
                                        style={{ width: `${importProgress}%` }}
                                    >
                                        <div className="absolute inset-0 bg-white/20 animate-[pulse_1.5s_infinite]"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Live Console */}
                            <div className="flex-1 bg-stone-900 border border-stone-950 text-stone-200 rounded-2xl p-4 font-mono text-[11px] leading-relaxed overflow-y-auto space-y-1.5 custom-scrollbar-dark select-text shadow-inner">
                                {importLogs.map((log, idx) => (
                                    <div key={idx} className="flex gap-2">
                                        <span className="text-stone-500 font-bold select-none">[{log.time}]</span>
                                        <span className={
                                            log.type === 'error' ? 'text-rose-400 font-semibold' :
                                            log.type === 'warn' ? 'text-amber-400 font-semibold' :
                                            log.type === 'success' ? 'text-emerald-400 font-bold' : 'text-stone-300'
                                        }>
                                            {log.msg}
                                        </span>
                                    </div>
                                ))}
                                <div className="h-1 bg-transparent"></div> {/* anchor */}
                            </div>
                        </div>
                    )}

                    {step === 'summary' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-up shadow-sm">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                                </div>
                                <h4 className="font-extrabold text-2xl text-stone-900 font-serif">Import Complete!</h4>
                                <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mt-1">Process executed successfully</p>
                            </div>

                            {/* Summary Metrics */}
                            <div className="grid grid-cols-3 gap-4 border-y border-stone-200 py-6">
                                <div className="text-center">
                                    <p className="text-3xl font-black text-emerald-600 font-sans">{summary.success}</p>
                                    <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mt-1">Successfully Imported</p>
                                </div>
                                <div className="text-center border-x border-stone-100">
                                    <p className="text-3xl font-black text-rose-600 font-sans">{summary.failed}</p>
                                    <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mt-1">Failed / Skipped</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl font-black text-amber-600 font-sans">{summary.warnings}</p>
                                    <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mt-1">Warnings / Skeptical</p>
                                </div>
                            </div>

                            {/* Details List */}
                            {summary.failed > 0 && (
                                <div className="space-y-2.5">
                                    <p className="text-xs font-bold text-rose-600 uppercase tracking-wide flex items-center gap-1">
                                        ⚠️ Issues List ({summary.failed} errors)
                                    </p>
                                    <div className="max-h-[20vh] overflow-y-auto border border-stone-150 rounded-xl divide-y divide-stone-100 bg-stone-50/50 text-xs custom-scrollbar-light">
                                        {summary.details.filter(d => d.status === 'Failed').map((d, i) => (
                                            <div key={i} className="p-3 flex justify-between items-start gap-4">
                                                <span className="font-bold text-stone-700">{d.email}</span>
                                                <span className="text-[10px] font-semibold bg-rose-50 border border-rose-100 text-rose-700 px-2 py-0.5 rounded-md font-mono">{d.details}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="p-6 border-t border-stone-100 bg-stone-50 flex justify-end gap-3 items-center">
                    {step === 'upload' && (
                        <button
                            onClick={handleClose}
                            className="px-5 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-700 hover:bg-stone-50 text-sm font-bold shadow-sm transition-all"
                        >
                            Cancel
                        </button>
                    )}

                    {step === 'mapping' && (
                        <>
                            <button
                                onClick={() => setStep('upload')}
                                className="px-5 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-700 hover:bg-stone-50 text-sm font-bold shadow-sm transition-all"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleStartImport}
                                disabled={!mapping.email}
                                className="px-6 py-2.5 bg-gradient-to-r from-matrimony-600 to-matrimony-700 hover:from-matrimony-700 hover:to-matrimony-800 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                Start Import ({parsedRows.length} Users)
                            </button>
                        </>
                    )}

                    {step === 'importing' && (
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest animate-pulse">
                            Processing users in background... Please do not close.
                        </span>
                    )}

                    {step === 'summary' && (
                        <button
                            onClick={handleClose}
                            className="px-6 py-2.5 bg-gradient-to-r from-matrimony-600 to-matrimony-700 hover:from-matrimony-700 hover:to-matrimony-800 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Admin Panel Component ---
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect fill='%23f3f4f6' width='150' height='150'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='20' dy='7' font-weight='bold' x='50%25' y='50%25' text-anchor='middle'%3EUser%3C/text%3E%3C/svg%3E";

const AdminPanel = ({ profiles, showNotification, onViewProfile, currentUser, openChat }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [statsFilter, setStatsFilter] = useState('all'); // New filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('newest');
    const [showImportModal, setShowImportModal] = useState(false);

    // Birthday Wishes Suite States
    const [birthdaySettings, setBirthdaySettings] = useState({
        subject: "🎉 Happy Birthday from MyLuckySpouse! ✨",
        headline: "Happy Birthday, {{name}}! 🎂",
        message: "Today is all about celebrating you! 🌟\n\nOn this beautiful day, the entire team at MyLuckySpouse wishes you a year ahead filled with laughter, boundless joy, and wonderful new beginnings.\n\nMatrimony is a beautiful journey of finding that one special soul who completes your puzzle of life. We are incredibly honored to walk beside you on this path to finding your life partner.\n\nMay this new chapter of your life open doors to lovely moments and bring you closer to finding your lucky spouse! 💕",
        buttonText: "Discover Matches Today",
        buttonUrl: "https://myluckyspouse.web.app"
    });
    const [birthdayLogs, setBirthdayLogs] = useState([]);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [testUserName, setTestUserName] = useState('');
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [birthdaySubTab, setBirthdaySubTab] = useState('logs'); // 'logs' or 'audits'
    const [auditSearchQuery, setAuditSearchQuery] = useState('');
    // Old activeTab is redundant now, but we keep it if referenced elsewhere, though we should likely remove typical tabs.

    // Statistics
    const totalUsers = profiles.length;
    const maleUsers = profiles.filter(p => p.gender === 'male').length;
    const femaleUsers = profiles.filter(p => p.gender === 'female').length;
    const verifiedUsers = profiles.filter(p => p.profileComplete).length;
    const onlineUsers = profiles.filter(p => isProfileOnline(p)).length;
    const appUsers = profiles.filter(p => p.platform === 'App').length;
    const webUsers = profiles.filter(p => p.platform === 'Web' || !p.platform).length;

    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);

    // Birthday Wishes Suite Effects and Actions
    useEffect(() => {
        if (statsFilter !== 'birthday wishes') return;
        
        // Load Settings
        const loadSettings = async () => {
            try {
                const settingsRef = doc(db, 'settings', 'birthday');
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists()) {
                    setBirthdaySettings(settingsSnap.data());
                }
            } catch (e) {
                console.error("Failed to load birthday settings:", e);
            }
        };
        loadSettings();

        // Subscribe to Logs (Real-time updates!)
        const logsQuery = query(collection(db, 'birthday_logs'), orderBy('sentAt', 'desc'), limit(50));
        const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
            const logs = [];
            snapshot.forEach(docSnap => {
                logs.push({ id: docSnap.id, ...docSnap.data() });
            });
            setBirthdayLogs(logs);
        }, (error) => {
            console.error("Failed to fetch birthday logs:", error);
        });

        return () => unsubscribe();
    }, [statsFilter]);

    const handleSaveBirthdaySettings = async () => {
        setIsSavingSettings(true);
        try {
            const settingsRef = doc(db, 'settings', 'birthday');
            await setDoc(settingsRef, birthdaySettings);
            showNotification("Birthday template settings saved successfully!", "success");
        } catch (e) {
            console.error("Failed to save settings:", e);
            showNotification("Failed to save settings: " + e.message, "error");
        } finally {
            setIsSavingSettings(false);
        }
    };

    const handleSendTestBirthdayEmail = async () => {
        if (!testEmailAddress || !testEmailAddress.includes('@')) {
            showNotification("Please enter a valid target email address.", "error");
            return;
        }
        setIsSendingTest(true);
        try {
            const sendTestBirthdayEmailManual = httpsCallable(functions, 'sendTestBirthdayEmailManual');
            const result = await sendTestBirthdayEmailManual({
                email: testEmailAddress,
                name: testUserName || 'Valued Tester',
                subject: birthdaySettings.subject,
                headline: birthdaySettings.headline,
                message: birthdaySettings.message,
                buttonText: birthdaySettings.buttonText,
                buttonUrl: birthdaySettings.buttonUrl
            });
            showNotification(result.data.message || `Test email successfully sent to ${testEmailAddress}!`, "success");
        } catch (e) {
            console.error("Failed to send test email:", e);
            showNotification("Failed to send test email: " + e.message, "error");
        } finally {
            setIsSendingTest(false);
        }
    };

    // Real-time Auditing for Birthday wishes configurations
    const birthdayAudits = useMemo(() => {
        return profiles.map(profile => {
            const name = `${profile.name || ''} ${profile.lastName || ''}`.trim() || 'User';
            const email = profile.email || '';
            const dobStr = profile.dob || '';
            const lastWishedYear = profile.lastBirthdayWishedYear || null;
            
            let status = 'Configured';
            let statusType = 'configured'; // 'configured', 'missing_dob', 'missing_email', 'excluded'
            let detail = '';

            if (!dobStr) {
                status = 'No DOB';
                statusType = 'missing_dob';
                detail = 'Enter birthdate to enable daily wishes';
            } else if (!email) {
                status = 'No Email';
                statusType = 'missing_email';
                detail = 'Enter email address to enable dispatch';
            } else if (profile.isAdmin || profile.isEditor || profile.isViewer || profile.isMarried || profile.isUnderReview) {
                status = 'Excluded';
                statusType = 'excluded';
                let reason = [];
                if (profile.isAdmin) reason.push('Admin');
                if (profile.isEditor) reason.push('Editor');
                if (profile.isViewer) reason.push('Viewer');
                if (profile.isMarried) reason.push('Married');
                if (profile.isUnderReview) reason.push('Under Review');
                detail = `Exempted from wishes (${reason.join(', ')})`;
            } else {
                // Configured. Let's calculate next birthday date
                try {
                    const today = new Date();
                    const currentYear = today.getFullYear();
                    
                    // Parse DOB month & day
                    const match = dobStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    let birthMonth, birthDay;
                    if (match) {
                        birthMonth = parseInt(match[2], 10);
                        birthDay = parseInt(match[3], 10);
                    } else {
                        const d = new Date(dobStr);
                        birthMonth = d.getMonth() + 1;
                        birthDay = d.getDate();
                    }

                    if (!isNaN(birthMonth) && !isNaN(birthDay)) {
                        const nextBday = new Date(currentYear, birthMonth - 1, birthDay);
                        if (nextBday < today) {
                            nextBday.setFullYear(currentYear + 1);
                        }
                        
                        const options = { month: 'short', day: 'numeric' };
                        const nextBdayStr = nextBday.toLocaleDateString('en-US', options);
                        
                        if (lastWishedYear === currentYear) {
                            status = 'Sent (2026)';
                            statusType = 'sent';
                            detail = `Wished on this year. Next on ${nextBdayStr}`;
                        } else {
                            status = 'Upcoming';
                            statusType = 'upcoming';
                            detail = `Scheduled for dispatch on ${nextBdayStr}`;
                        }
                    }
                } catch (e) {
                    status = 'Error';
                    statusType = 'missing_dob';
                    detail = 'Invalid DOB date format';
                }
            }

            return {
                id: profile.id,
                name,
                email,
                dob: dobStr,
                status,
                statusType,
                detail
            };
        });
    }, [profiles]);

    const exportToPDF = async () => {
        setIsExporting(true);
        setExportProgress(0);
        try {
            // Filter out current user (Admin) and Duplicates
            const uniqueProfiles = profiles.filter((profile, index, self) =>
                index === self.findIndex((p) => p.id === profile.id)
            ).filter(p => p.id !== auth.currentUser?.uid);

            // Sort Profiles: Females First, then Males
            uniqueProfiles.sort((a, b) => {
                const gA = (a.gender || '').toLowerCase();
                const gB = (b.gender || '').toLowerCase();
                if (gA === 'female' && gB !== 'female') return -1;
                if (gA !== 'female' && gB === 'female') return 1;
                return 0;
            });

            await exportProfilesToPDF(uniqueProfiles, setExportProgress, showNotification, functions, currentUser?.isAdmin || currentUser?.isViewer);

        } catch (error) {
            console.error("Error exporting PDF:", error);
            showNotification("Failed to export PDF.", "error");
        } finally {
            setIsExporting(false);
            setExportProgress(0);
        }
    };







    // Filtered Profiles
    const filteredProfiles = useMemo(() => {
        let result = profiles;

        // Apply Viewers restriction
        if (currentUser.isViewer) {
            result = result.filter(p => !p.isAdmin && p.profileComplete);
        }

        switch (statsFilter) {
            case 'online': result = result.filter(p => isProfileOnline(p)); break;
            case 'verified': result = result.filter(p => p.profileComplete && !p.isMarried); break;
            case 'pending': result = result.filter(p => !p.profileComplete && !p.isMarried && !p.isUnderReview); break;
            case 'new user': result = result.filter(p => !p.profileComplete && !p.isMarried && p.isUnderReview); break;
            case 'married': result = result.filter(p => p.isMarried); break;
            case 'imported': result = result.filter(p => p.importedByAdmin === true); break;
            case 'male': result = result.filter(p => p.gender === 'male' && p.profileComplete && !p.isMarried); break;
            case 'female': result = result.filter(p => p.gender === 'female' && p.profileComplete && !p.isMarried); break;
            case 'App': result = result.filter(p => p.platform === 'App'); break;
            case 'Web': result = result.filter(p => p.platform === 'Web' || !p.platform); break;
            default: break;
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(p => 
                (p.name?.toLowerCase().includes(query)) ||
                (p.lastName?.toLowerCase().includes(query)) ||
                (p.email?.toLowerCase().includes(query)) ||
                (String(p.phone || '').includes(query)) ||
                (p.city?.toLowerCase().includes(query))
            );
        }

        result = [...result].sort((a, b) => {
            switch (sortOption) {
                case 'newest': return (b.createdAt?.toMillis ? b.createdAt.toMillis() : b.lastActive || 0) - (a.createdAt?.toMillis ? a.createdAt.toMillis() : a.lastActive || 0);
                case 'oldest': return (a.createdAt?.toMillis ? a.createdAt.toMillis() : a.lastActive || 0) - (b.createdAt?.toMillis ? b.createdAt.toMillis() : b.lastActive || 0);
                case 'name_asc': return (a.name || '').localeCompare(b.name || '');
                case 'name_desc': return (b.name || '').localeCompare(a.name || '');
                default: return 0;
            }
        });

        return result;
    }, [profiles, statsFilter, currentUser, searchQuery, sortOption]);

    const handleCSVExport = () => {
        if (!window.confirm(`Export ${filteredProfiles.length} profiles to CSV?`)) return;
        try {
            exportToCSV(filteredProfiles, `profiles_export_${new Date().toISOString().slice(0, 10)}.csv`);
            showNotification("CSV export started!", "success");
        } catch (e) {
            console.error("CSV Export Error:", e);
            showNotification("Failed to export CSV", "error");
        }
    };

    // Callable Cloud Function for deleting user and all their data
    const deleteUserAndDataCallable = httpsCallable(functions, 'deleteUserAndData');
    const sendVerificationReminderCallable = httpsCallable(functions, 'sendVerificationReminder');
    const sendPendingCredentialsCallable = httpsCallable(functions, 'sendPendingCredentials');

    const [isDeletingAllImported, setIsDeletingAllImported] = useState(false);

    const handleDeleteAllImported = async () => {
        const importedUsers = filteredProfiles.filter(p => p.importedByAdmin === true);
        if (importedUsers.length === 0) return;

        const confirmText = `delete ${importedUsers.length}`;
        const userInput = window.prompt(
            `WARNING: You are about to permanently delete all ${importedUsers.length} imported users.\n` +
            `This will delete their Firebase Auth accounts, Firestore profiles, and all associated data.\n\n` +
            `To confirm, type "${confirmText}" in the box below:`
        );

        if (userInput !== confirmText) {
            showNotification("Delete cancelled or confirmation text mismatched.", "info");
            return;
        }

        setIsDeletingAllImported(true);
        showNotification(`Starting bulk deletion of ${importedUsers.length} users...`, "info");

        try {
            let deletedCount = 0;
            let errorCount = 0;

            for (const user of importedUsers) {
                try {
                    await deleteUserAndDataCallable({ 
                        userId: user.id, 
                        userImages: user.images || [] 
                    });
                    deletedCount++;
                } catch (err) {
                    console.error(`Failed to delete user ${user.email || user.id}:`, err);
                    errorCount++;
                }
            }

            if (errorCount === 0) {
                showNotification(`Deleted all ${deletedCount} imported users successfully!`, "success");
            } else {
                showNotification(`Bulk deletion complete: ${deletedCount} succeeded, ${errorCount} failed.`, "warn");
            }
        } catch (globalErr) {
            console.error("Bulk deletion failed:", globalErr);
            showNotification("Bulk deletion failed: " + globalErr.message, "error");
        } finally {
            setIsDeletingAllImported(false);
        }
    };

    const handleEdit = (user) => {
        setSelectedUser(JSON.parse(JSON.stringify(user))); // Deep copy to avoid direct mutation
        setIsEditing(true);
    };

    const handleSendReminders = async () => {
        if (!window.confirm("Are you sure you want to send email reminders to ALL users with PENDING VERIFICATION status?")) return;

        try {
            showNotification("Sending reminders... This may take a moment.", "info");
            const result = await sendVerificationReminderCallable();
            showNotification(result.data.message, "success");
        } catch (error) {
            console.error("Error sending reminders:", error);
            showNotification("Failed to send reminders.", "error");
        }
    };

    const handleSendCredentials = async () => {
        if (!window.confirm("Are you sure you want to generate (if not exists) and mail credentials (username & password) to ALL PENDING users?")) return;

        try {
            showNotification("Sending credentials... This may take a moment.", "info");
            const result = await sendPendingCredentialsCallable();
            showNotification(result.data.message, "success");
        } catch (error) {
            console.error("Error sending credentials:", error);
            showNotification("Failed to send credentials.", "error");
        }
    };

    const handleSendSingleReminder = async (profile) => {
        if (!window.confirm(`Send verification reminder to ${profile.name}?`)) return;
        try {
            showNotification(`Sending reminder to ${profile.name}...`, "info");
            const result = await sendVerificationReminderCallable({ email: profile.email, name: profile.name });
            showNotification(result.data.message, "success");
        } catch (error) {
            console.error("Error sending reminder:", error);
            showNotification("Failed to send reminder.", "error");
        }
    };

    const handleSendWAReminder = (profile) => {
        const text = encodeURIComponent(`Hi ${profile.name},\n\nPlease complete your profile on MyLuckySpouse to start finding your perfect match! Log in to your account and finish your registration here: https://myluckyspouse.web.app\n\nBest,\nMyLuckySpouse Team`);
        let phone = profile.phone ? String(profile.phone).replace(/[^\d+]/g, '') : '';
        if (phone) {
            // Add +91 if it's exactly 10 digits (standard Indian number)
            if (phone.length === 10 && !phone.startsWith('+')) {
                phone = '+91' + phone;
            }
            window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
        } else {
            showNotification("User has no valid phone number.", "error");
        }
    };

    const handleBulkWAReminders = () => {
        const pendingUsers = filteredProfiles.filter(p => !p.profileComplete && !p.isMarried);
        if (pendingUsers.length === 0) {
            showNotification("No pending users found.", "info");
            return;
        }
        
        if (!window.confirm(`You are about to open WhatsApp for ${pendingUsers.length} users. PLEASE MAKE SURE YOU HAVE ALLOWED POP-UPS FOR THIS SITE in your browser settings. Continue?`)) return;

        showNotification(`Opening WhatsApp for ${pendingUsers.length} users...`, "info");
        
        pendingUsers.forEach((profile, index) => {
            setTimeout(() => {
                const text = encodeURIComponent(`Hi ${profile.name},\n\nPlease complete your profile on MyLuckySpouse to start finding your perfect match! Log in to your account and finish your registration here: https://myluckyspouse.web.app\n\nBest,\nMyLuckySpouse Team`);
                let phone = profile.phone ? String(profile.phone).replace(/[^\d+]/g, '') : '';
                if (phone) {
                    if (phone.length === 10 && !phone.startsWith('+')) {
                        phone = '+91' + phone;
                    }
                    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
                }
            }, index * 500); // 500ms delay to give browser time
        });
    };

    const handleSingleExport = async (user) => {
        setIsExporting(true);
        try {
            await exportProfilesToPDF([user], setExportProgress, showNotification, functions, currentUser?.isAdmin || currentUser?.isViewer);
        } catch (e) {
            console.error(e);
            showNotification("Export failed", "error");
        } finally {
            setIsExporting(false);
            setExportProgress(0);
        }
    };

    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesUser, setNotesUser] = useState(null);

    const handleOpenNotes = (user) => {
        setNotesUser(user);
        setShowNotesModal(true);
    };



    const [userToDelete, setUserToDelete] = useState(null);

    const handleDeleteClick = (profile) => {
        if (profile.id === auth.currentUser.uid) {
            showNotification("You cannot delete your own account.", "error");
            return;
        }
        if (profile.isAdmin) {
            showNotification("You cannot delete an Admin account.", "error");
            return;
        }
        setUserToDelete(profile);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        const userId = userToDelete.id;
        const userImages = userToDelete.images || [];
        setUserToDelete(null); // Close modal immediately

        try {
            await deleteUserAndDataCallable({ userId, userImages });
            showNotification("User and all associated data deleted successfully!", "success");
        } catch (error) {
            console.error("Error deleting user:", error);
            showNotification("Failed to delete user: " + error.message, "error");
        }
    };

    const handleUpdate = async () => {
        if (!selectedUser) return;
        if (!window.confirm("Are you sure you want to save these changes?")) return;
        try {
            const { id, ...dataToUpdate } = selectedUser;
            const userRef = doc(db, "profiles", id);
            await updateDoc(userRef, dataToUpdate);
            showNotification("Profile updated successfully!", "success");
            setIsEditing(false);
            setSelectedUser(null);
        } catch (error) {
            console.error("Error updating profile:", error);
            showNotification("Failed to update profile: " + error.message, "error");
        }
    };





    const getSessionDuration = (profile) => {
        if (!profile.sessionStartTime) return 'N/A';
        const now = Date.now();
        const diff = now - profile.sessionStartTime;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return 'Less than a minute';
        if (minutes < 60) return `${minutes} minutes`;
        if (hours < 24) return `${hours} hours`;
        return `${days} days`;
    };

    // --- Render ---

    const handleVerificationChange = async (profileId, newStatus) => {
        if (!window.confirm(`Are you sure you want to change this user's status to ${newStatus.toUpperCase()}?`)) return;
        try {
            const userRef = doc(db, "profiles", profileId);
            const updates = {};
            if (newStatus === 'married') {
                updates.isMarried = true;
            } else if (newStatus === 'verified') {
                updates.isMarried = false;
                updates.profileComplete = true;
                updates.isUnderReview = false;
            } else if (newStatus === 'under review') {
                updates.isMarried = false;
                updates.profileComplete = false;
                updates.isUnderReview = true;
            } else if (newStatus === 'pending') {
                updates.isMarried = false;
                updates.profileComplete = false;
                updates.isUnderReview = false;
            }
            await updateDoc(userRef, updates);
            const statusName = newStatus === 'under review' ? 'Under Review' : newStatus;
            showNotification(`Status updated to ${statusName} successfully!`, "success");
        } catch (error) {
            console.error("Error updating status:", error);
            showNotification("Failed to update status: " + error.message, "error");
        }
    };

    const handleRoleChange = async (profileId, newRole) => {
        if (!window.confirm(`Are you sure you want to change this user's role to ${newRole.toUpperCase()}?`)) return;
        try {
            const userRef = doc(db, "profiles", profileId);
            const updates = {};
            if (newRole === 'admin') {
                updates.isAdmin = true;
                updates.isViewer = false;
                updates.isEditor = false;
            } else if (newRole === 'editor') {
                updates.isAdmin = false;
                updates.isViewer = false;
                updates.isEditor = true;
            } else if (newRole === 'viewer') {
                updates.isAdmin = false;
                updates.isViewer = true;
                updates.isEditor = false;
            } else {
                updates.isAdmin = false;
                updates.isViewer = false;
                updates.isEditor = false;
            }
            await updateDoc(userRef, updates);
            showNotification(`Role updated to ${newRole} successfully!`, "success");
        } catch (error) {
            console.error("Error updating role:", error);
            showNotification("Failed to update role: " + error.message, "error");
        }
    };

    return (
        <div className="p-4 md:p-8 bg-stone-50 min-h-screen font-sans pb-24 md:pb-8">

            <AdminDashboardHeader currentUser={currentUser} />

            <div className="flex flex-col md:flex-row justify-end items-start md:items-center mb-6 gap-3">
                {/* Stats Filter Feedback */}
                {statsFilter !== 'all' && (
                    <div className="flex items-center gap-2 bg-matrimony-50 px-3 py-1.5 rounded-lg border border-matrimony-100 animate-fade-in">
                        <span className="text-xs font-bold text-matrimony-600 uppercase tracking-wider">Filtered by: {statsFilter}</span>
                        <button onClick={() => setStatsFilter('all')} className="text-matrimony-600 hover:text-matrimony-700">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                        </button>
                    </div>
                )}

                {(currentUser?.isAdmin || currentUser?.isEditor) && (
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={handleSendReminders}
                            className="bg-white hover:bg-stone-50 text-indigo-600 border border-indigo-200 font-bold py-2.5 px-5 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            Email Reminders
                        </button>
                        <button
                            onClick={handleBulkWAReminders}
                            className="bg-white hover:bg-green-50 text-green-600 border border-green-200 font-bold py-2.5 px-5 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                            WA Reminders
                        </button>
                        <button
                            onClick={handleSendCredentials}
                            className="bg-white hover:bg-violet-50 text-violet-600 border border-violet-200 font-bold py-2.5 px-5 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v-2l2-2v-1.257A6 6 0 1118 8zm-6-4a1 1 0 100 2h.01a1 1 0 100-2H12z" clipRule="evenodd" />
                            </svg>
                            Mail Credentials
                        </button>
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="bg-white hover:bg-rose-50 text-matrimony-600 border border-rose-200 font-bold py-2.5 px-5 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            Import Users
                        </button>
                    </div>
                )}
                {(currentUser?.isAdmin || currentUser?.isViewer) && (
                    <button
                        onClick={exportToPDF}
                        disabled={isExporting}
                        className={`bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 font-bold py-2.5 px-5 rounded-xl shadow-sm flex items-center gap-2 text-sm transition-all ${isExporting ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
                    >
                        {isExporting ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Exporting... {exportProgress}%</span>
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                <span>Export Verified</span>
                            </>
                        )}
                    </button>
                )}
            </div>

            <RecentNotesWidget profiles={profiles} onViewNote={handleOpenNotes} />

            <AdminStatsGrid
                stats={{ totalUsers, onlineUsers, verifiedUsers, maleUsers, femaleUsers, appUsers, webUsers }}
                activeFilter={statsFilter}
                onFilterClick={setStatsFilter}
            />

            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden mt-8">
                <div className="flex flex-col md:flex-row justify-between items-center p-6 border-b border-stone-200 bg-stone-50 gap-4">
                    <h2 className="text-xl font-bold text-stone-900 font-serif flex items-center gap-2 flex-wrap">
                        Manage Users
                        <span className="text-xs font-medium text-stone-600 bg-white px-2 py-1 rounded border border-stone-200">
                            {filteredProfiles.length}
                        </span>
                        {statsFilter === 'imported' && filteredProfiles.length > 0 && (
                            <button
                                onClick={handleDeleteAllImported}
                                disabled={isDeletingAllImported}
                                className="ml-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-bold py-1.5 px-3.5 rounded-xl flex items-center gap-1.5 text-xs transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                                🗑️ {isDeletingAllImported ? 'Deleting...' : 'Delete All Imported'}
                            </button>
                        )}
                    </h2>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="relative w-full sm:w-64">
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input 
                                type="text" 
                                placeholder="Search by name, email, phone or city..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 outline-none transition-all text-sm"
                            />
                        </div>
                        <select 
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value)}
                            className="w-full sm:w-auto py-2 px-3 bg-white border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 transition-all text-sm text-stone-600 font-medium"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="name_asc">Name (A-Z)</option>
                            <option value="name_desc">Name (Z-A)</option>
                        </select>
                    </div>
                </div>

                {/* Tabs Row */}
                <div className="flex border-b border-stone-200 px-6 gap-6 overflow-x-auto custom-scrollbar-light bg-stone-50/50">
                    {['all', 'male', 'female', 'new user', 'pending', 'verified', 'married', 'imported', 'birthday wishes'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setStatsFilter(tab)}
                            className={`py-3 font-bold text-sm whitespace-nowrap capitalize transition-colors border-b-2 -mb-px ${statsFilter === tab ? 'border-matrimony-600 text-matrimony-600' : 'border-transparent text-stone-500 hover:text-stone-800'}`}
                        >
                            {tab === 'birthday wishes' ? '🎂 Birthday Wishes' : tab}
                        </button>
                    ))}
                </div>
                {statsFilter === 'birthday wishes' ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 animate-fade-in bg-stone-50">
                        {/* Left Column: Template Editor */}
                        <div className="bg-white rounded-3xl p-6 shadow-sm border border-stone-200 space-y-6 flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-center border-b border-stone-100 pb-4 mb-4">
                                    <div>
                                        <h3 className="font-extrabold text-xl text-stone-900 font-serif">🎂 Birthday Template</h3>
                                        <p className="text-xs text-stone-500 font-medium mt-0.5">Customize email contents and preview in real-time</p>
                                    </div>
                                    <button
                                        onClick={handleSaveBirthdaySettings}
                                        disabled={isSavingSettings}
                                        className="bg-gradient-to-r from-matrimony-600 to-matrimony-700 hover:from-matrimony-700 hover:to-matrimony-800 text-white font-extrabold py-2.5 px-6 rounded-xl shadow-md flex items-center gap-1.5 transition-all text-xs disabled:opacity-50"
                                    >
                                        {isSavingSettings ? (
                                            <>
                                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>💾</span>
                                                <span>Save Template</span>
                                            </>
                                        )}
                                    </button>
                                </div>

                                <div className="space-y-4 text-xs font-semibold text-stone-600">
                                    {/* Subject Field */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase tracking-wider text-stone-500">Email Subject</label>
                                        <input
                                            type="text"
                                            value={birthdaySettings.subject}
                                            onChange={(e) => setBirthdaySettings({ ...birthdaySettings, subject: e.target.value })}
                                            placeholder="🎉 Happy Birthday from MyLuckySpouse! ✨"
                                            className="p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-800 text-sm font-medium transition-all"
                                        />
                                    </div>

                                    {/* Headline Field */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase tracking-wider text-stone-500">Email Headline (Supports {"{{name}}"})</label>
                                        <input
                                            type="text"
                                            value={birthdaySettings.headline}
                                            onChange={(e) => setBirthdaySettings({ ...birthdaySettings, headline: e.target.value })}
                                            placeholder="Happy Birthday, {{name}}! 🎂"
                                            className="p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-800 text-sm font-medium transition-all"
                                        />
                                    </div>

                                    {/* Message Field */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase tracking-wider text-stone-500">Message Body (Supports {"{{name}}"})</label>
                                        <textarea
                                            value={birthdaySettings.message}
                                            onChange={(e) => setBirthdaySettings({ ...birthdaySettings, message: e.target.value })}
                                            rows={8}
                                            placeholder="Write a sweet birthday wish here..."
                                            className="p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-800 text-sm font-medium transition-all resize-none leading-relaxed font-sans"
                                        />
                                    </div>

                                    {/* Button Settings */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] uppercase tracking-wider text-stone-500">Button Text</label>
                                            <input
                                                type="text"
                                                value={birthdaySettings.buttonText}
                                                onChange={(e) => setBirthdaySettings({ ...birthdaySettings, buttonText: e.target.value })}
                                                placeholder="Discover Matches Today"
                                                className="p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-800 text-xs font-medium transition-all"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] uppercase tracking-wider text-stone-500">Button Destination URL</label>
                                            <input
                                                type="text"
                                                value={birthdaySettings.buttonUrl}
                                                onChange={(e) => setBirthdaySettings({ ...birthdaySettings, buttonUrl: e.target.value })}
                                                placeholder="https://myluckyspouse.web.app"
                                                className="p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 outline-none text-stone-800 text-xs font-medium transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Test Email Dispatcher */}
                            <div className="bg-rose-50/20 border border-rose-100 rounded-2xl p-4 mt-6 space-y-3">
                                <h4 className="font-extrabold text-xs text-rose-800 flex items-center gap-1">
                                    <span>🚀</span> Test Send Custom Template
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                    <input
                                        type="text"
                                        value={testUserName}
                                        onChange={(e) => setTestUserName(e.target.value)}
                                        placeholder="Test User Name (e.g. John)"
                                        className="p-2.5 bg-white border border-stone-200 rounded-xl outline-none focus:border-matrimony-400 font-medium"
                                    />
                                    <input
                                        type="email"
                                        value={testEmailAddress}
                                        onChange={(e) => setTestEmailAddress(e.target.value)}
                                        placeholder="Target Email Address"
                                        className="p-2.5 bg-white border border-stone-200 rounded-xl outline-none focus:border-matrimony-400 font-medium"
                                    />
                                </div>
                                <button
                                    onClick={handleSendTestBirthdayEmail}
                                    disabled={isSendingTest || !testEmailAddress}
                                    className="w-full bg-rose-600 hover:bg-rose-700 text-white font-extrabold py-2.5 px-4 rounded-xl shadow-md transition-all text-xs disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isSendingTest ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Sending Test Email...</span>
                                        </>
                                    ) : (
                                        <span>✨ Send Test Email Now</span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Right Column: Visual Preview & Reports */}
                        <div className="space-y-6 flex flex-col">
                            {/* Visual HTML Preview Mockup */}
                            <div className="bg-stone-50 rounded-3xl p-6 border border-stone-200 shadow-inner flex flex-col flex-1 max-h-[480px] overflow-y-auto">
                                <h4 className="font-extrabold text-[10px] text-stone-400 uppercase tracking-widest mb-4">📧 Real-time Live Preview Mockup</h4>
                                
                                <div className="w-full bg-[#fff1f2] border border-stone-200 rounded-2xl shadow-sm overflow-hidden text-center max-w-[420px] mx-auto select-none">
                                    <div className="bg-gradient-to-r from-matrimony-600 to-rose-700 p-6 text-white">
                                        <div className="font-serif text-xl font-bold tracking-wide">MyLuckySpouse</div>
                                        <div className="text-[9px] uppercase tracking-wider opacity-90 mt-0.5">Where Love Meets Destiny</div>
                                        <div className="text-lg mt-2">✨ 🎁 ✨</div>
                                    </div>
                                    <div className="p-6 bg-white text-left">
                                        <h2 className="font-serif text-base font-bold text-stone-800 text-center mb-4 leading-snug">
                                            {(birthdaySettings.headline || "Happy Birthday, {{name}}! 🎂").replace(/\{\{name\}\}/g, testUserName || "Valued User")}
                                        </h2>
                                        <div className="text-xs text-stone-600 leading-relaxed whitespace-pre-line mb-6">
                                            {(birthdaySettings.message || "Today is all about celebrating you! 🌟").replace(/\{\{name\}\}/g, testUserName || "Valued User")}
                                        </div>
                                        <div className="text-center mb-6">
                                            <span className="inline-block bg-gradient-to-r from-matrimony-600 to-rose-700 text-white text-xs font-bold px-6 py-2.5 rounded-full shadow-md">
                                                {birthdaySettings.buttonText || "Discover Matches Today"}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-stone-500 italic text-center">
                                            Warmest wishes,<br/>
                                            <strong>The MyLuckySpouse Team</strong>
                                        </p>
                                    </div>
                                    <div className="bg-stone-50 border-t border-stone-100 p-4 text-center text-[9px] text-stone-400 leading-relaxed">
                                        You received this automated email because you are a registered member of MyLuckySpouse.<br/>
                                        &copy; {new Date().getFullYear()} MyLuckySpouse. All Reserved.
                                    </div>
                                </div>
                            </div>

                            {/* Sub-tabbed Report & Audits Board */}
                            <div className="bg-white rounded-3xl p-6 shadow-sm border border-stone-200 flex-1 flex flex-col max-h-[350px]">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-stone-100 pb-3 mb-4 gap-2">
                                    <div className="flex gap-4">
                                        <button
                                            onClick={() => setBirthdaySubTab('logs')}
                                            className={`font-serif font-extrabold text-xs pb-1 border-b-2 transition-all ${birthdaySubTab === 'logs' ? 'border-matrimony-600 text-matrimony-600' : 'border-transparent text-stone-400 hover:text-stone-700'}`}
                                        >
                                            📊 Delivery Logs
                                        </button>
                                        <button
                                            onClick={() => setBirthdaySubTab('audits')}
                                            className={`font-serif font-extrabold text-xs pb-1 border-b-2 transition-all ${birthdaySubTab === 'audits' ? 'border-matrimony-600 text-matrimony-600' : 'border-transparent text-stone-400 hover:text-stone-700'}`}
                                        >
                                            👥 Birthday Audits ({profiles.length})
                                        </button>
                                    </div>
                                    {birthdaySubTab === 'logs' ? (
                                        <span className="text-[10px] font-extrabold bg-stone-100 border border-stone-200 text-stone-500 px-2 py-0.5 rounded-md uppercase">
                                            Limit: 50
                                        </span>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search audits..."
                                                value={auditSearchQuery}
                                                onChange={(e) => setAuditSearchQuery(e.target.value)}
                                                className="pl-6 pr-2 py-1 bg-stone-50 border border-stone-200 rounded-lg outline-none text-[9px] focus:ring-1 focus:ring-matrimony-500 w-32 font-medium"
                                            />
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 overflow-y-auto text-xs pr-1">
                                    {birthdaySubTab === 'logs' ? (
                                        birthdayLogs.length === 0 ? (
                                            <div className="py-12 text-center text-stone-400 font-medium">
                                                📁 No birthday logs found in Firestore. Try sending a test message.
                                            </div>
                                        ) : (
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="border-b border-stone-200 text-stone-400 font-bold uppercase tracking-wider text-[9px] text-left">
                                                        <th className="py-2">Sent At</th>
                                                        <th className="py-2">User</th>
                                                        <th className="py-2">Email</th>
                                                        <th className="py-2 text-center">Type</th>
                                                        <th className="py-2 text-right">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {birthdayLogs.map(log => (
                                                        <tr key={log.id} className="border-b border-stone-50 hover:bg-stone-50/50">
                                                            <td className="py-2 text-stone-500 text-[10px]">
                                                                {log.sentAt?.seconds ? new Date(log.sentAt.seconds * 1000).toLocaleString() : 'Just now'}
                                                            </td>
                                                            <td className="py-2 font-bold text-stone-700">{log.userName}</td>
                                                            <td className="py-2 font-medium text-stone-500 font-mono text-[10px] select-all">{log.userEmail}</td>
                                                            <td className="py-2 text-center">
                                                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase border ${log.type === 'Test' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                                                                    {log.type || 'Auto'}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 text-right font-semibold">
                                                                {log.status === 'Success' ? (
                                                                    <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 text-[10px]">
                                                                        ✓ Success
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-100 text-[10px] cursor-help" title={log.error || 'Unknown Error'}>
                                                                        ✗ Failed
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )
                                    ) : (
                                        // Audits Sub-tab
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="border-b border-stone-200 text-stone-400 font-bold uppercase tracking-wider text-[9px] text-left">
                                                    <th className="py-2">Candidate</th>
                                                    <th className="py-2">Birthdate (DOB)</th>
                                                    <th className="py-2 text-center">Status</th>
                                                    <th className="py-2 text-right">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {birthdayAudits
                                                    .filter(audit => {
                                                        if (!auditSearchQuery) return true;
                                                        const query = auditSearchQuery.toLowerCase();
                                                        return audit.name.toLowerCase().includes(query) || audit.email.toLowerCase().includes(query);
                                                    })
                                                    .map(audit => (
                                                        <tr key={audit.id} className="border-b border-stone-50 hover:bg-stone-50/50">
                                                            <td className="py-2.5">
                                                                <div className="font-bold text-stone-700">{audit.name}</div>
                                                                <div className="text-[10px] text-stone-400 font-mono select-all">{audit.email || 'No email'}</div>
                                                            </td>
                                                            <td className="py-2.5 text-stone-600 font-medium">{audit.dob || 'Not Set'}</td>
                                                            <td className="py-2.5 text-center">
                                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase border ${
                                                                    audit.statusType === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                                    audit.statusType === 'upcoming' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                                                    audit.statusType === 'excluded' ? 'bg-stone-50 text-stone-500 border-stone-200' :
                                                                    'bg-rose-50 text-rose-700 border-rose-200'
                                                                }`}>
                                                                    {audit.status}
                                                                </span>
                                                            </td>
                                                            <td className="py-2.5 text-right text-[10px] font-medium text-stone-500 leading-snug">
                                                                {audit.detail}
                                                            </td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">User Profile</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Last Active</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Platform</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-stone-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100 bg-white">
                            {filteredProfiles.map((profile) => (
                                <tr key={profile.id} className="hover:bg-stone-50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="relative">
                                                <img
                                                    src={profile.images?.[0] || PLACEHOLDER_IMAGE}
                                                    alt={profile.name}
                                                    className="w-12 h-12 rounded-xl object-cover shadow-sm ring-2 ring-stone-100 group-hover:ring-matrimony-200 transition-all cursor-pointer"
                                                    onClick={() => onViewProfile(profile)}
                                                />
                                                {isProfileOnline(profile) && (
                                                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full shadow-sm animate-pulse"></span>
                                                )}
                                            </div>
                                            <div>
                                                <h3 onClick={() => onViewProfile(profile)} className="font-bold text-stone-900 hover:text-matrimony-600 transition-colors cursor-pointer">{profile.name} {profile.lastName}</h3>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-xs text-stone-500 capitalize">{profile.gender}</span>
                                                    <span className="text-stone-300">•</span>
                                                    <span className="text-xs text-stone-500">{calculateAge(profile.birthDate)} yrs</span>
                                                </div>
                                                {profile.password && (
                                                    <div className="mt-1 text-[11px] text-stone-500 font-semibold bg-stone-100/80 px-2 py-0.5 rounded border border-stone-200 w-fit font-mono select-all flex items-center gap-1 shadow-sm">
                                                        <span>🔑</span><span>Pwd:</span><span className="text-rose-600 font-bold">{profile.password}</span>
                                                    </div>
                                                )}
                                                {/* Razorpay Payment Information Block */}
                                                {profile.paymentDetails && profile.paymentDetails.paymentId ? (
                                                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-2 text-xs flex flex-col gap-1 w-fit max-w-[250px] shadow-sm animate-fade-in-up">
                                                        <p className="text-amber-800 font-bold flex items-center gap-1">
                                                            💳 RZP ID: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-amber-200 select-all font-semibold">{profile.paymentDetails.paymentId}</span>
                                                        </p>
                                                        {profile.paymentDetails.screenshotUrl && (
                                                            <a href={profile.paymentDetails.screenshotUrl} target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:text-rose-700 font-extrabold flex items-center gap-1 hover:underline mt-0.5">
                                                                🖼️ View Receipt Screenshot
                                                            </a>
                                                        )}
                                                        {profile.paymentDetails.method && (
                                                            <p className="text-[10px] text-stone-500 font-semibold">Method: {profile.paymentDetails.method}</p>
                                                        )}
                                                        <p className="text-[10px] text-stone-400">
                                                            Submitted: {new Date(profile.paymentDetails.submittedAt || profile.paymentDetails.activatedAt || Date.now()).toLocaleString()}
                                                        </p>
                                                    </div>
                                                ) : profile.isUnderReview ? (
                                                    <div className="mt-2 bg-rose-50 border border-rose-100 text-rose-700 rounded-xl px-2.5 py-1.5 text-[10px] font-extrabold w-fit flex items-center gap-1.5 shadow-sm animate-pulse">
                                                        <span>⚠️</span><span>No Payment Info Submitted Yet</span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col items-start gap-1">
                                            {(currentUser.isAdmin || currentUser.isEditor) ? (
                                                <div className="flex items-center gap-1">
                                                    <select
                                                        value={profile.isMarried ? 'married' : (profile.profileComplete ? 'verified' : (profile.isUnderReview ? 'under review' : 'pending'))}
                                                        onChange={(e) => handleVerificationChange(profile.id, e.target.value)}
                                                        className={`text-[10px] font-bold border rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-matrimony-500 cursor-pointer ${profile.isMarried ? 'bg-purple-50 text-purple-700 border-purple-200' : (profile.profileComplete ? 'bg-green-50 text-green-700 border-green-200' : (profile.isUnderReview ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'))}`}
                                                    >
                                                        <option value="verified">Verified</option>
                                                        <option value="under review">Under Review</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="married">Married</option>
                                                    </select>
                                                    {!profile.profileComplete && !profile.isMarried && (
                                                        <div className="flex items-center">
                                                            <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-stone-100 rounded-full transition-colors" title="Send Email Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-stone-500" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                            </button>
                                                            <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors ml-1" title="Send WhatsApp Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                profile.isMarried ? (
                                                    <span className="px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 text-[10px] font-bold uppercase tracking-wide border border-purple-200">Married</span>
                                                ) : profile.profileComplete ? (
                                                    <span className="px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-[10px] font-bold uppercase tracking-wide border border-green-200">Verified</span>
                                                ) : profile.isUnderReview ? (
                                                    <span className="px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 text-[10px] font-bold uppercase tracking-wide border border-orange-200 flex items-center gap-1">
                                                        Under Review
                                                        <div className="flex items-center ml-1 border-l border-orange-200 pl-1">
                                                            <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-orange-100 rounded-full transition-colors" title="Send Email Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                            </button>
                                                            <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors text-green-600" title="Send WhatsApp Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                            </button>
                                                        </div>
                                                    </span>
                                                ) : (
                                                    <span className="px-2.5 py-1 rounded-md bg-yellow-50 text-yellow-700 text-[10px] font-bold uppercase tracking-wide border border-yellow-200 flex items-center gap-1">
                                                        Pending
                                                        <div className="flex items-center ml-1 border-l border-yellow-200 pl-1">
                                                            <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-yellow-100 rounded-full transition-colors" title="Send Email Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                            </button>
                                                            <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors text-green-600" title="Send WhatsApp Reminder">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                            </button>
                                                        </div>
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm">
                                            <p className="font-medium text-stone-800">{getLastActive(profile)}</p>
                                            <p className="text-xs text-stone-500 mt-0.5">{getSessionDuration(profile)}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-stone-50 w-fit border border-stone-100">
                                            <span className="text-lg">{!profile.platform || profile.platform === 'Web' ? '🌐' : '📱'}</span>
                                            <span className="text-xs font-bold text-stone-600">{!profile.platform ? 'Web' : profile.platform}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {currentUser.isAdmin && currentUser.uid !== profile.id ? (
                                            <select
                                                value={profile.isAdmin ? 'admin' : (profile.isEditor ? 'editor' : (profile.isViewer ? 'viewer' : 'user'))}
                                                onChange={(e) => handleRoleChange(profile.id, e.target.value)}
                                                className="text-xs font-bold bg-white border border-stone-200 rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-matrimony-500 cursor-pointer"
                                            >
                                                <option value="user">User</option>
                                                <option value="viewer">Viewer</option>
                                                <option value="editor">Editor</option>
                                                {currentUser.isAdmin && <option value="admin">Admin</option>}
                                            </select>
                                        ) : (
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider border ${profile.isAdmin ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : profile.isEditor ? 'bg-purple-50 text-purple-700 border-purple-200' : profile.isViewer ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-stone-50 text-stone-600 border-stone-200'}`}>
                                                {profile.isAdmin ? 'Admin' : (profile.isEditor ? 'Editor' : (profile.isViewer ? 'Viewer' : 'User'))}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => openChat(profile)}
                                                className="p-2 rounded-lg text-stone-400 hover:text-green-600 hover:bg-green-50 transition-all relative group/btn"
                                                title="Message"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                                            </button>
                                            <button
                                                onClick={() => handleOpenNotes(profile)}
                                                className="p-2 rounded-lg text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-all relative group/btn"
                                                title="Notes"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                            </button>
                                            <button
                                                onClick={() => handleEdit(profile)}
                                                className="p-2 rounded-lg text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-all relative group/btn"
                                                title="Edit"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                            </button>
                                            {currentUser.isAdmin && !profile.isAdmin && (
                                                <button
                                                    onClick={() => handleDeleteClick(profile)}
                                                    className="p-2 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-all relative group/btn"
                                                    title="Delete"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View (Enhanced) */}
                <div className="md:hidden grid grid-cols-1 gap-4 p-4">
                    {filteredProfiles.map((profile) => (
                        <div key={profile.id} className="bg-white rounded-2xl p-4 shadow-sm border border-stone-200 relative overflow-hidden">
                            {isProfileOnline(profile) && (
                                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-green-100 to-transparent rounded-bl-3xl -mr-4 -mt-4"></div>
                            )}
                            <div className="flex items-start gap-4 reltaive z-10">
                                <img
                                    src={profile.images?.[0] || 'https://via.placeholder.com/150'}
                                    alt={profile.name}
                                    className="w-16 h-16 rounded-2xl object-cover shadow-sm ring-2 ring-stone-100"
                                    onClick={() => onViewProfile(profile)}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-extrabold text-stone-900 text-lg truncate w-full" onClick={() => onViewProfile(profile)}>{profile.name}</h3>
                                            <p className="text-xs text-stone-500 font-medium">{calculateAge(profile.birthDate)} yrs • {profile.city || 'N/A'}</p>
                                            {profile.password && (
                                                <div className="mt-1 text-[11px] text-stone-500 font-semibold bg-stone-100/80 px-2 py-0.5 rounded border border-stone-200 w-fit font-mono select-all flex items-center gap-1 shadow-sm">
                                                    <span>🔑</span><span>Pwd:</span><span className="text-rose-600 font-bold">{profile.password}</span>
                                                </div>
                                            )}
                                            {/* Razorpay Payment Information Block */}
                                            {profile.paymentDetails && profile.paymentDetails.paymentId ? (
                                                <div className="mt-2.5 bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs flex flex-col gap-1 w-full shadow-sm animate-fade-in-up">
                                                    <p className="text-amber-800 font-bold flex items-center gap-1">
                                                        💳 RZP ID: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-amber-200 select-all font-semibold">{profile.paymentDetails.paymentId}</span>
                                                    </p>
                                                    {profile.paymentDetails.screenshotUrl && (
                                                        <a href={profile.paymentDetails.screenshotUrl} target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:text-rose-700 font-extrabold flex items-center gap-1 hover:underline mt-0.5">
                                                            🖼️ View Receipt Screenshot
                                                        </a>
                                                    )}
                                                    {profile.paymentDetails.method && (
                                                        <p className="text-[10px] text-stone-500 font-semibold">Method: {profile.paymentDetails.method}</p>
                                                    )}
                                                    <p className="text-[10px] text-stone-400">
                                                        Submitted: {new Date(profile.paymentDetails.submittedAt || profile.paymentDetails.activatedAt || Date.now()).toLocaleString()}
                                                    </p>
                                                </div>
                                            ) : profile.isUnderReview ? (
                                                <div className="mt-2.5 bg-rose-50 border border-rose-100 text-rose-700 rounded-xl px-2.5 py-1.5 text-[10px] font-extrabold w-fit flex items-center gap-1.5 shadow-sm animate-pulse">
                                                    <span>⚠️</span><span>No Payment Info Submitted Yet</span>
                                                </div>
                                            ) : null}
                                        </div>
                                        {isProfileOnline(profile) && <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-sm shadow-green-200"></span>}
                                    </div>

                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {(currentUser.isAdmin || currentUser.isEditor) ? (
                                            <div className="flex items-center gap-1">
                                                <select
                                                    value={profile.isMarried ? 'married' : (profile.profileComplete ? 'verified' : (profile.isUnderReview ? 'under review' : 'pending'))}
                                                    onChange={(e) => handleVerificationChange(profile.id, e.target.value)}
                                                    className={`text-[10px] font-bold border rounded-md px-2 py-0.5 outline-none focus:ring-1 focus:ring-matrimony-500 cursor-pointer ${profile.isMarried ? 'bg-purple-50 text-purple-700 border-purple-200' : (profile.profileComplete ? 'bg-green-50 text-green-700 border-green-200' : (profile.isUnderReview ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'))}`}
                                                >
                                                    <option value="verified">Verified</option>
                                                    <option value="under review">Under Review</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="married">Married</option>
                                                </select>
                                                {!profile.profileComplete && !profile.isMarried && (
                                                    <div className="flex items-center">
                                                        <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-stone-100 rounded-full transition-colors" title="Send Email Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-stone-500" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                        </button>
                                                        <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors ml-1" title="Send WhatsApp Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            profile.isMarried ? (
                                                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-bold uppercase rounded border border-purple-200 flex items-center gap-1">Married</span>
                                            ) : profile.profileComplete ? (
                                                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold uppercase rounded border border-green-200 flex items-center gap-1">Verified</span>
                                            ) : profile.isUnderReview ? (
                                                <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-[10px] font-bold uppercase rounded border border-orange-200 flex items-center gap-1">
                                                    Under Review
                                                    <div className="flex items-center ml-1 border-l border-orange-200 pl-1">
                                                        <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-orange-100 rounded-full transition-colors" title="Send Email Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                        </button>
                                                        <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors text-green-600" title="Send WhatsApp Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                        </button>
                                                    </div>
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 bg-yellow-50 text-yellow-700 text-[10px] font-bold uppercase rounded border border-yellow-200 flex items-center gap-1">
                                                    Pending
                                                    <div className="flex items-center ml-1 border-l border-yellow-200 pl-1">
                                                        <button onClick={() => handleSendSingleReminder(profile)} className="p-1 hover:bg-yellow-100 rounded-full transition-colors" title="Send Email Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                                                        </button>
                                                        <button onClick={() => handleSendWAReminder(profile)} className="p-1 hover:bg-green-100 rounded-full transition-colors text-green-600" title="Send WhatsApp Reminder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                                        </button>
                                                    </div>
                                                </span>
                                            )
                                        )}
                                        <div className="flex items-center gap-1 px-2 py-0.5 bg-stone-50 text-stone-600 text-[10px] font-bold uppercase rounded border border-stone-200">
                                            <span>{(!profile.platform || profile.platform === 'Web') ? '🌐' : '📱'}</span>
                                            <span>{!profile.platform ? 'Web' : profile.platform}</span>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        {currentUser.isAdmin && currentUser.uid !== profile.id ? (
                                            <select
                                                value={profile.isAdmin ? 'admin' : (profile.isEditor ? 'editor' : (profile.isViewer ? 'viewer' : 'user'))}
                                                onChange={(e) => handleRoleChange(profile.id, e.target.value)}
                                                className="text-[10px] font-bold bg-white border border-stone-200 rounded px-1.5 py-0.5 outline-none cursor-pointer"
                                            >
                                                <option value="user">User Role</option>
                                                <option value="viewer">Viewer Role</option>
                                                <option value="editor">Editor Role</option>
                                                <option value="admin">Admin Role</option>
                                            </select>
                                        ) : (
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase border ${profile.isAdmin ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : profile.isEditor ? 'bg-purple-50 text-purple-700 border-purple-200' : profile.isViewer ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-stone-50 text-stone-600 border-stone-200'}`}>
                                                {profile.isAdmin ? 'Admin' : (profile.isEditor ? 'Editor' : (profile.isViewer ? 'Viewer' : 'User'))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-stone-100 flex justify-between items-center">
                                <p className="text-xs font-medium text-stone-500">Active: <span className="text-stone-800">{getLastActive(profile)}</span></p>
                                <div className="flex gap-2">
                                    <button onClick={() => openChat(profile)} className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                                    </button>
                                    {(currentUser?.isAdmin || currentUser?.isViewer) && (
                                        <button onClick={() => handleOpenNotes(profile)} className="p-1.5 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                        </button>
                                    )}
                                    <button onClick={() => handleEdit(profile)} className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    </button>
                                    {currentUser.isAdmin && !profile.isAdmin && (
                                        <button onClick={() => handleDeleteClick(profile)} className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {filteredProfiles.length === 0 && (
                        <div className="p-12 text-center flex flex-col items-center justify-center text-stone-400">
                            <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mb-4 border border-stone-100">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                            </div>
                            <p className="text-lg font-medium text-stone-500">No profiles found</p>
                            <button onClick={() => setStatsFilter('all')} className="mt-4 text-matrimony-600 font-bold hover:underline">Clear Filter</button>
                        </div>
                    )}
                </div>
                    </>
                )}
            </div>

            {isEditing && selectedUser && (
                <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex justify-center items-center z-[100] p-4 animate-fade-in-up">
                    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-stone-100 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b border-stone-100 pb-4">
                            <h2 className="text-2xl font-bold text-stone-900 font-serif">Edit Profile: {selectedUser.name}</h2>
                            <button onClick={() => setIsEditing(false)} className="text-stone-400 hover:text-stone-700 text-2xl font-bold transition-colors">&times;</button>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-stone-600 mb-2">Profile Pictures</label>
                            <div className="grid grid-cols-3 gap-4">
                                {[0, 1, 2].map((index) => (
                                    <label key={index} className="cursor-pointer relative overflow-hidden rounded-xl shadow-sm border border-stone-200 group h-32 md:h-40 bg-stone-50">
                                        <img src={selectedUser.images?.[index] || 'https://placehold.co/150x150/f5f5f4/a8a29e?text=Upload'} alt={`Pic ${index+1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity backdrop-blur-[2px]">
                                            <span className="text-white font-bold bg-white/20 px-2 py-1 rounded-full border border-white/30 text-[10px] text-center">Change</span>
                                        </div>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="hidden" 
                                            disabled={isUploading}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setIsUploading(true);
                                                try {
                                                    const storage = getStorage();
                                                    const storageRef = ref(storage, `profileImages/${selectedUser.id}_${Date.now()}`);
                                                    const uploadTask = uploadBytesResumable(storageRef, file);
                                                    
                                                    uploadTask.on('state_changed', null, 
                                                        (error) => {
                                                            console.error("Upload error:", error);
                                                            showNotification("Failed to upload image.", "error");
                                                            setIsUploading(false);
                                                        }, 
                                                        async () => {
                                                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                                            const newImages = [...(selectedUser.images || [null, null, null])];
                                                            newImages[index] = downloadURL;
                                                            setSelectedUser({ ...selectedUser, images: newImages });
                                                            setIsUploading(false);
                                                            showNotification("Image uploaded! Click Save Changes to apply.", "success");
                                                        }
                                                    );
                                                } catch (err) {
                                                    setIsUploading(false);
                                                    showNotification("Error preparing image upload.", "error");
                                                }
                                            }}
                                        />
                                        {selectedUser.images?.[index] && index > 0 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const newImages = [...selectedUser.images];
                                                    const temp = newImages[0];
                                                    newImages[0] = newImages[index];
                                                    newImages[index] = temp;
                                                    setSelectedUser({ ...selectedUser, images: newImages });
                                                }}
                                                className="absolute bottom-2 right-2 z-10 bg-stone-900/70 hover:bg-matrimony-600 text-white px-2 py-1 text-[10px] font-bold rounded shadow-sm backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                Default
                                            </button>
                                        )}
                                    </label>
                                ))}
                            </div>
                            <p className="text-xs text-stone-500 mt-2">The first image is the default profile picture.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {PROFILE_FIELD_ORDER.map(key => (
                                <div key={key} className={['about', 'partnerPreference'].includes(key) ? 'md:col-span-2' : ''}>
                                    <label className="block text-sm font-medium text-stone-600 mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                                    {['about', 'partnerPreference'].includes(key) ? (
                                        <textarea
                                            value={selectedUser[key] || ''}
                                            onChange={(e) => setSelectedUser({ ...selectedUser, [key]: e.target.value })}
                                            rows="3"
                                            className="w-full p-2 bg-white border border-stone-200 rounded-md focus:ring-2 focus:ring-matrimony-500 focus:border-transparent outline-none text-stone-900 placeholder-stone-400"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={selectedUser[key] || ''}
                                            onChange={(e) => setSelectedUser({ ...selectedUser, [key]: e.target.value })}
                                            className="w-full p-2 bg-white border border-stone-200 rounded-md focus:ring-2 focus:ring-matrimony-500 focus:border-transparent outline-none text-stone-900 placeholder-stone-400"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end space-x-4 mt-8 pt-4 border-t border-stone-100">
                            <button onClick={() => setIsEditing(false)} disabled={isUploading} className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 font-semibold transition-all disabled:opacity-50">Cancel</button>
                            <button onClick={handleUpdate} disabled={isUploading} className="px-4 py-2 bg-matrimony-600 text-white rounded-lg hover:bg-matrimony-700 font-semibold shadow-sm transition-all disabled:opacity-50">Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Notes Modal */}
            {showNotesModal && notesUser && (
                <AdminNotesModal
                    isOpen={showNotesModal}
                    onClose={() => setShowNotesModal(false)}
                    user={notesUser}
                    currentUser={currentUser}
                    showNotification={showNotification}
                />
            )}

            {/* Admin User Import Modal */}
            {showImportModal && (
                <AdminImportModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    functions={functions}
                    showNotification={showNotification}
                />
            )}
            {/* Delete Confirmation Modal */}
            {userToDelete && (
                <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex justify-center items-center z-[100] p-4 animate-fade-in-up">
                    <div className="bg-white border border-stone-100 p-6 md:p-8 rounded-3xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-center w-16 h-16 bg-red-50 text-red-600 rounded-full mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-center text-stone-900 mb-2">Delete Profile?</h3>
                        <p className="text-stone-500 text-center mb-8">
                            Are you sure you want to delete <span className="font-bold text-stone-700">{userToDelete.name || 'this user'}</span>? This action cannot be undone and will permanently erase all data.
                        </p>
                        <div className="flex justify-center space-x-4">
                            <button onClick={() => setUserToDelete(null)} className="px-6 py-3 bg-stone-100 text-stone-700 rounded-xl hover:bg-stone-200 font-semibold transition-all w-1/2">
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-semibold shadow-sm transition-all w-1/2">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

// --- Splash Screen Component ---
const SplashScreen = ({ visible }) => {
    return (
        <div className={`fixed inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 transition-opacity duration-1000 ${visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
            <div className="text-center relative">
                {/* Pulse Effect Background */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-rose-500/20 rounded-full blur-3xl animate-pulse"></div>

                {/* Logo and Text */}
                <div className="relative z-10 flex flex-col items-center animate-fade-in-up">
                    <img src="/logo-v3.png" alt="MyLuckySpouse Logo" className="h-24 md:h-32 w-auto mb-6 drop-shadow-2xl animate-bounce-subtle" />
                    <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-fuchsia-400 to-indigo-400 tracking-tight">
                        MyLuckySpouse
                    </h1>
                    <p className="mt-3 text-gray-400 text-sm md:text-base font-light tracking-widest uppercase">
                        Find your perfect match
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- Privacy Policy Page Component ---
const PrivacyPolicyPage = ({ setView, currentUser }) => {
    return (
        <div className="min-h-screen bg-stone-50 text-stone-600 font-sans">
            {/* Header */}
            <div className="bg-white border-b border-stone-100 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <img src="/logo-v3.png" alt="Logo" className="h-10 w-auto" />
                        <h1 className="text-xl font-bold text-stone-900 hidden md:block font-serif">MyLuckySpouse</h1>
                    </div>
                    <button
                        onClick={() => {
                            window.history.pushState({}, '', '/');
                            setView(currentUser ? 'matches' : 'auth');
                        }}
                        className="px-5 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl transition-colors font-semibold border border-stone-100"
                    >
                        Back to Home
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-6 py-12 max-w-4xl">
                <div className="bg-white border border-stone-100 rounded-3xl p-8 md:p-12 shadow-soft">
                    <h1 className="text-3xl md:text-5xl font-bold text-stone-900 font-serif mb-8 border-b border-stone-100 pb-8">Privacy Policy</h1>

                    <div className="space-y-10 text-lg leading-relaxed text-stone-600">
                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">1. Information We Collect</h2>
                            <p className="mb-4">To provide you with the best matchmaking experience, we collect the following information:</p>
                            <ul className="list-disc pl-6 space-y-2 text-stone-500">
                                <li><strong>Personal Details:</strong> Name, age, gender, date of birth, religion, caste, and contact information.</li>
                                <li><strong>Profile Information:</strong> Education, profession, income, family details, and partner preferences.</li>
                                <li><strong>Media:</strong> Photos you upload to your profile.</li>
                                <li><strong>Device Information:</strong> Information about the device used to access our service for security and optimization.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">2. How We Use Your Information</h2>
                            <p className="mb-4">We use your data strictly for:</p>
                            <ul className="list-disc pl-6 space-y-2 text-stone-500">
                                <li>Creating and managing your account.</li>
                                <li>Showing your profile to other relevant members.</li>
                                <li>AI-based matchmaking recommendations.</li>
                                <li>Communication regarding your account or services.</li>
                                <li>Legal and security compliance.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">3. Data Sharing & Disclosure</h2>
                            <p>We do <strong>not</strong> sell your personal data. Your information is shared only with:</p>
                            <ul className="list-disc pl-6 space-y-2 text-stone-500 mt-4">
                                <li>Other registered members (limited view until you connect).</li>
                                <li>Service providers who assist in our operations (e.g., hosting, notifications).</li>
                                <li>Law enforcement agencies if required by Indian law.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">4. Data Security</h2>
                            <p>We implement industry-standard security measures to protect your data. However, no method of transmission over the internet is 100% secure.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">5. User Rights</h2>
                            <p>You have the right to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-stone-500 mt-4">
                                <li>Access and update your profile information.</li>
                                <li>Delete your account permanently (contact support).</li>
                                <li>Report any misuse or concern regarding privacy.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-stone-900 mb-4 font-serif">6. Contact Us</h2>
                            <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                            <p className="mt-2 text-matrimony-600 font-semibold text-xl md:text-2xl">support@myluckyspouse.com</p>
                        </section>

                        <div className="pt-8 border-t border-stone-100 text-sm text-stone-400 text-center uppercase tracking-wider font-semibold">
                            Last Updated: January 1, 2025
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Simple */}
            <div className="py-8 text-center text-stone-500 text-sm border-t border-stone-200 mt-12 bg-white">
                &copy; {new Date().getFullYear()} MyLuckySpouse. All rights reserved.
            </div>
        </div>
    );
};

// --- Main App Component ---
export default function App() {
    const [showSplash, setShowSplash] = useState(true);
    const [view, setView] = useState(() => {
        if (window.location.pathname === '/privacypolicy' || window.location.pathname === '/privacy') {
            return 'privacy-policy';
        }
        return 'auth';
    });
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [chatRequests, setChatRequests] = useState([]);
    const [notification, setNotification] = useState({ message: null, type: 'success' });
    const [activeChats, setActiveChats] = useState([]);
    const [isLoading, setIsLoading] = useState(() => {
        if (window.location.pathname === '/privacypolicy' || window.location.pathname === '/privacy') {
            return false;
        }
        return true;
    });
    const [unreadChats, setUnreadChats] = useState({});
    const [profileModalMode, setProfileModalMode] = useState('edit');
    const [profileModalStep, setProfileModalStep] = useState(1); // New state for initial step
    const [showUpdatePopup, setShowUpdatePopup] = useState(false); // Legacy update popup
    const [updateFormData, setUpdateFormData] = useState({}); // Form data for legacy update
    const [isChatListOpen, setIsChatListOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [isNotificationListOpen, setIsNotificationListOpen] = useState(false);
    const unreadNotificationsCount = useMemo(() => {
        return notifications.filter(n => !n.read).length;
    }, [notifications]);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [matchesScrollY, setMatchesScrollY] = useState(0); // State for scroll persistence
    const [matchesGenderFilter, setMatchesGenderFilter] = useState('all'); // State for filter persistence
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showPaywallModal, setShowPaywallModal] = useState(false);
    const [authUser, setAuthUser] = useState(() => auth.currentUser);
    const [fcmToken, setFcmToken] = useState(null); // Local state to hold the token
    const notificationSound = useRef(new Audio(notificationSoundFile)); // Ref to hold the Audio object
    const initialRedirectDone = useRef(false); // Ref to track initial redirection
    const sessionStartTime = useRef(Date.now());

    // Refs for state used in event listeners
    const viewRef = useRef(view);
    const activeChatsRef = useRef(activeChats);
    const currentUserRef = useRef(currentUser);
    const profilesRef = useRef(profiles);

    const isPopStateAction = useRef(false);

    // --- EFFECT: Auto-show Paywall Modal on app load if not paid ---
    useEffect(() => {
        if (currentUser) {
            const isPremiumOrStaff = currentUser.isAdmin || currentUser.isViewer || currentUser.isEditor || currentUser.profileComplete;
            if (!isPremiumOrStaff) {
                setShowPaywallModal(true);
            }
        }
    }, [currentUser?.id, currentUser?.profileComplete]);

    // Synchronize browser history (Back/Forward navigation support)
    useEffect(() => {
        if (!currentUser) return; // Only track history for logged-in sessions
        if (Capacitor.isNativePlatform()) return; // Skip history pushState on Capacitor apps to prevent SecurityError

        try {
            const currentState = window.history.state;
            const stateMatches = currentState && 
                                 currentState.view === view && 
                                 currentState.selectedProfileId === selectedProfile?.id;

            if (!stateMatches) {
                if (isPopStateAction.current) {
                    // Change was triggered by browser navigation (popstate), do not push new state
                    isPopStateAction.current = false;
                } else {
                    // Change was triggered by UI interaction, push new state to browser history
                    window.history.pushState(
                        { view: view, selectedProfileId: selectedProfile?.id }, 
                        '', 
                        ''
                    );
                }
            }
        } catch (error) {
            console.warn("History pushState failed:", error);
        }
    }, [view, selectedProfile, currentUser]);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) return; // Skip history sync on Capacitor apps

        const handlePopState = (event) => {
            try {
                if (event.state && event.state.view) {
                    isPopStateAction.current = true;
                    setView(event.state.view);
                    
                    if (event.state.selectedProfileId) {
                        const found = profilesRef.current.find(p => p.id === event.state.selectedProfileId);
                        if (found) {
                            setSelectedProfile(found);
                        }
                    } else {
                        setSelectedProfile(null);
                    }
                } else {
                    // If returning to initial load without state, default to matches for logged in
                    if (currentUserRef.current) {
                        isPopStateAction.current = true;
                        setView('matches');
                        setSelectedProfile(null);
                    }
                }
            } catch (error) {
                console.warn("History popstate navigation failed:", error);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSplash(false);
        }, 2500); // Show splash for 2.5 seconds
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        viewRef.current = view;
        activeChatsRef.current = activeChats;
        currentUserRef.current = currentUser;
        profilesRef.current = profiles;
    }, [view, activeChats, currentUser, profiles]);

    useEffect(() => {
        const loadScript = (src) => new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) return resolve();
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Script load error for ${src}`));
            document.head.appendChild(script);
        });

        loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js').catch(err => console.error(err));
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js').catch(err => console.error(err));
    }, []);

    // --- EFFECT: Deep Linking for Profile Sharing ---
    useEffect(() => {
        const handleDeepLink = async () => {
            const params = new URLSearchParams(window.location.search);
            const profileId = params.get('profileId');

            if (profileId && currentUser) {
                // Clear the query param to avoid re-triggering
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);

                try {
                    const docRef = doc(db, 'profiles', profileId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setSelectedProfile({ id: docSnap.id, ...docSnap.data() });
                        setView('detailed-profile');
                    } else {
                        showNotification("Profile not found or invalid link.", "error");
                    }
                } catch (e) {
                    console.error("Error fetching deep linked profile:", e);
                    showNotification("Failed to load linked profile.", "error");
                }
            }
        };

        if (!isLoading && currentUser) {
            handleDeepLink();
        }
    }, [currentUser, isLoading]);

    // Initialize Push Notifications
    useEffect(() => {
        const initPushNotifications = async () => {
            if (Capacitor.isNativePlatform()) {
                // ... (Native Logic remains same) ...
                try {
                    // Create the channel for Android 8+
                    await PushNotifications.createChannel({
                        id: 'default',
                        name: 'General Notifications',
                        description: 'General notifications for the app',
                        importance: 5,
                        visibility: 1,
                        sound: 'default',
                        vibration: true,
                    });


                    const permission = await PushNotifications.checkPermissions();
                    if (permission.receive === 'prompt') {
                        await PushNotifications.requestPermissions();
                    }
                    if (permission.receive === 'granted') {
                        await PushNotifications.register();
                    }
                } catch (e) {
                    console.error("Push Notification Error:", e);
                }
            } else {
                // --- WEB PUSH LOGIC ---
                try {
                    if ('Notification' in window && typeof Notification.requestPermission === 'function') {
                        const permission = await Notification.requestPermission();
                        if (permission === 'granted') {
                            const token = await getToken(messaging, {
                                vapidKey: 'BM_U-w7y_65F6U-example-key-replace-with-real-one' // NOTE: You need a VAPID key here.
                                // For now, I'll use a placeholder or try without if configured in firebase console
                            }).catch(e => console.error("Get Token Error", e));

                            if (token) {
                                console.log("Web Push Token:", token);
                                if (auth.currentUser) {
                                    await updateDoc(doc(db, "profiles", auth.currentUser.uid), { fcmToken: token });
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.error("Web Push Error:", e);
                }

                // Foreground Message Handling
                onMessage(messaging, (payload) => {
                    console.log('Message received. ', payload);
                    // Play sound
                    const audio = new Audio('/not.mp3');
                    audio.play().catch(e => console.log("Audio play failed", e));

                    // Show Toast
                    showNotification(`${payload.notification.title}: ${payload.notification.body}`, "success");
                });
            }
        };

        initPushNotifications();
    }, []);

    // --- EFFECT: Native Listeners & Audio Unlock ---
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            // const fcmTokenRef = useRef(null); // Moved to top level

            PushNotifications.addListener('registration', (token) => {
                console.log('Push Registration Token:', token.value);
                setFcmToken(token.value); // Store token in state
            });

            PushNotifications.addListener('registrationError', (error) => {
                console.error('Push Registration Error:', error);
            });

            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Push Received:', notification);
                // Play sound for native if not handled by system
                const audio = new Audio('/not.mp3');
                audio.play().catch(e => console.log("Audio play failed", e));
                showNotification(notification.title + ": " + notification.body, "success");
            });

            PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
                console.log('Push Action:', notification);
                const data = notification.notification.data;
                const chatId = data.chatId;



                if (chatId) {
                    // Logic to open the specific chat
                    // We need to find the other user's ID from the chatId to set activeChatUser
                    // Chat ID format is usually uid1_uid2
                    const parts = chatId.split('_');
                    const otherUserId = parts.find(id => id !== auth.currentUser?.uid);

                    if (otherUserId) {
                        // Fetch the other user's profile to set as activeChatUser
                        getDoc(doc(db, 'profiles', otherUserId)).then(docSnap => {
                            if (docSnap.exists()) {
                                // Add to active chats (handling limit is done in openChat, but here we directly set state for simplicity or need to duplicate logic)
                                setActiveChats(prev => {
                                    if (prev.find(c => c.id === otherUserId)) return prev;
                                    // Use docSnap data constructing the user object
                                    const newUser = { id: otherUserId, ...docSnap.data() };
                                    const newChats = [...prev, newUser];
                                    if (window.innerWidth >= 768) {
                                        return newChats.length > 3 ? newChats.slice(newChats.length - 3) : newChats;
                                    } else {
                                        return [newUser];
                                    }
                                });
                                // Also ensure we are on the matches view or somewhere appropriate
                                if (view !== 'matches') setView('matches');
                            }
                        }).catch(err => console.error("Error fetching chat user:", err));
                    }
                }
            });
        }

        // --- Back Button Handling ---
        if (Capacitor.isNativePlatform()) {
            CapacitorApp.addListener('backButton', ({ canGoBack }) => {
                const currentView = viewRef.current;
                const currentActiveChats = activeChatsRef.current;
                const user = currentUserRef.current;

                if (currentActiveChats.length > 0) {
                    setActiveChats(prev => prev.slice(0, -1)); // Close the top-most chat
                } else if (currentView === 'detailed-profile') {
                    setView('matches');
                } else if (currentView !== 'matches' && user && !['auth', 'complete-profile'].includes(currentView)) {
                    setView('matches');
                } else {
                    CapacitorApp.exitApp();
                }
            });

            // --- Status Bar Configuration ---
            StatusBar.setStyle({ style: Style.Dark }); // Keep icons light (white)
            StatusBar.setBackgroundColor({ color: '#000000' }); // Black for system look
            StatusBar.setOverlaysWebView({ overlay: false });
        }

        // --- AUDIO UNLOCK LOGIC ---
        // Create the Audio object once and store it in a ref.

        // This function will be called on the first user interaction to unlock audio playback.
        const unlockAudio = () => {
            notificationSound.current?.play().catch(() => { });
            notificationSound.current?.pause();
            if (notificationSound.current) {
                notificationSound.current.currentTime = 0;
            }
            // Remove the event listeners after the first interaction.
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('touchstart', unlockAudio);
        };

        document.addEventListener('click', unlockAudio);
        document.addEventListener('touchstart', unlockAudio);
    }, []);

    // Effect to request notification permission on login
    useEffect(() => {
        if (currentUser && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, [currentUser]);

    // Effect to handle Razorpay redirect URL payment auto-verification
    useEffect(() => {
        if (!currentUser?.id) return;

        const urlParams = new URLSearchParams(window.location.search);
        const razorpayPaymentId = urlParams.get('razorpay_payment_id') || urlParams.get('payment_id');
        const isSuccessRedirect = window.location.pathname.includes('/payment-success') || 
                                  urlParams.get('payment') === 'success' || 
                                  urlParams.get('razorpay_payment_link_status') === 'paid' ||
                                  !!razorpayPaymentId;

        if (isSuccessRedirect) {
            // Generate a fallback payment ID if razorpay_payment_id is missing (e.g. they paid but URL didn't have ID)
            const finalPaymentId = razorpayPaymentId || `pay_redirect_${Date.now()}`;
            
            // Check if user already has this payment ID or already completed verification
            const alreadyHasPaymentId = currentUser.paymentDetails?.paymentId === finalPaymentId;
            const alreadyVerified = currentUser.profileComplete && currentUser.isPaid;

            if (!alreadyVerified && !alreadyHasPaymentId) {
                const autoSubmitPayment = async () => {
                    try {
                        const userRef = doc(db, "profiles", currentUser.id);
                        await updateDoc(userRef, {
                            isUnderReview: true,
                            paymentDetails: {
                                paymentId: finalPaymentId,
                                method: 'Razorpay Auto-Redirect Callback',
                                submittedAt: Date.now(),
                                status: 'pending_review'
                            }
                        });
                        showNotification("Payment detected! Verification started automatically.", "success");
                    } catch (error) {
                        console.error("Error auto-submitting payment details on redirect:", error);
                    }
                };
                autoSubmitPayment();
            }

            // Always clear url parameters to make URL clean and avoid infinite checks/loop on refresh
            const cleanUrl = new URL(window.location);
            cleanUrl.searchParams.delete('razorpay_payment_id');
            cleanUrl.searchParams.delete('payment_id');
            cleanUrl.searchParams.delete('payment');
            cleanUrl.searchParams.delete('status');
            cleanUrl.searchParams.delete('razorpay_payment_link_id');
            cleanUrl.searchParams.delete('razorpay_payment_link_reference');
            cleanUrl.searchParams.delete('razorpay_payment_link_status');
            cleanUrl.searchParams.delete('razorpay_signature');
            
            // If the pathname was /payment-success, clean it to / or home
            const newPathname = cleanUrl.pathname.includes('/payment-success') ? '/' : cleanUrl.pathname;
            window.history.replaceState({}, document.title, newPathname + cleanUrl.search);
        }
    }, [currentUser, currentUser?.id]);

    useEffect(() => {
        document.title = "MyLuckySpouse";
    }, []);

    // --- Removed the old 'routing for incomplete profiles' effect to allow browsing ---
    // User interactions to chat or view details will trigger the modal instead.

    // --- Firestore Heartbeat & Presence System ---
    useEffect(() => {
        if (!currentUser?.id) return;

        const userStatusFirestoreRef = doc(db, '/profiles/' + currentUser.id);

        const updateOnlineStatus = async (onlineState) => {
            try {
                const updates = { isOnline: onlineState };
                if (onlineState) {
                    updates.lastActive = Date.now();
                }
                await updateDoc(userStatusFirestoreRef, updates);
            } catch (err) {
                console.error("Error updating online status:", err);
            }
        };

        // Set user to online and update lastActive when the app loads
        updateOnlineStatus(true);

        // Setup a periodic heartbeat every 60 seconds
        const heartbeatInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                updateOnlineStatus(true);
            }
        }, 60000);

        // Handle page/unload
        const handleBeforeUnload = () => {
            updateDoc(userStatusFirestoreRef, { isOnline: false }).catch(() => {});
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Handle tab visibility changes (e.g. backgrounding on mobile/desktop)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateOnlineStatus(true);
            } else {
                updateOnlineStatus(false);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(heartbeatInterval);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // Best effort offline update on unmount
            updateDoc(userStatusFirestoreRef, { isOnline: false }).catch(() => {});
        };

    }, [currentUser?.id]);

    const showNotification = (message, type = 'success', duration = 3000) => { setNotification({ message, type }); setTimeout(() => setNotification({ message: null, type: 'success' }), duration); };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setAuthUser(user);
            if (!user) {
                setIsLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (authUser) {
            // Check if email is verified OR if the sign-in method was Google (which is always trusted)
            const isGoogleSignIn = authUser.providerData.some(provider => provider.providerId === 'google.com');

            if (authUser.emailVerified || authUser.phoneNumber || isGoogleSignIn) {
                const profileRef = doc(db, "profiles", authUser.uid);
                const adminRef = doc(db, "admins", authUser.uid);
                const isAdmin = authUser.email === 'chiranjivimk30@gmail.com';
                const isViewer = VIEWER_EMAILS.includes(authUser.email);

                const unsubProfile = onSnapshot(profileRef, async (profileDoc) => {
                    if (profileDoc.exists()) {
                        const dbData = profileDoc.data();
                        const finalIsAdmin = dbData.isAdmin || isAdmin;
                        const finalIsEditor = dbData.isEditor || false;
                        const finalIsViewer = (dbData.isViewer || isViewer) && !finalIsAdmin && !finalIsEditor;
                        setCurrentUser({
                            id: profileDoc.id,
                            ...dbData,
                            isAdmin: finalIsAdmin,
                            isEditor: finalIsEditor,
                            isViewer: finalIsViewer
                        });

                        /* Legacy Update Popup Logic - DISABLED in favor of Main Modal
                        // Check for Legacy Update
                        const userData = profileDoc.data();
                        if (userData.profileComplete && !isViewer) {
                // ... old logic removed/disabled to prevent double popup
            }
            */

                        setIsLoading(false); // Fix: Ensure loading stops when profile is found
                    } else {
                        // --- PROFILE CREATION LOGIC ---
                        // The profile document does not exist, so let's create a basic one.
                        // This happens on the very first login for a new user.
                        console.log(`Profile for user ${authUser.uid} not found. Creating one.`);
                        try {
                            const newProfileData = {
                                id: authUser.uid,
                                email: authUser.email || '',
                                name: authUser.displayName || 'New User',
                                createdAt: serverTimestamp(),
                                profileComplete: isViewer ? true : false, // Viewers are auto-complete
                                isAdmin: isAdmin,
                                isViewer: isViewer,
                                isEditor: false,
                                isVerified: false, // Default to false for new users
                            };
                            await setDoc(profileRef, newProfileData);
                        } catch (error) {
                            console.error("Error creating new user profile:", error);
                            signOut(auth); // Log out user if profile creation fails
                        }
                    }
                });
                return () => unsubProfile();
            } else {
                // User is signed out or not completely verified/logged in properly
                setCurrentUser(null);
                setIsLoading(false); // Fix: Ensure loading is set to false
            }
        } else {
            // Check for Privacy Policy URL *before* clearing user
            if (window.location.pathname === '/privacypolicy' || window.location.pathname === '/privacy') {
                setView('privacy-policy');
                setIsLoading(false);
                return;
            }

            setCurrentUser(null);
            setIsLoading(false); // Fix: Ensure loading is set to false
        }
    }, [authUser]);

    // --- EFFECT: Sync FCM Token ---
    // This effect runs whenever currentUser or fcmToken changes.
    // It ensures that even if the token arrives before the user logs in, it gets saved.
    useEffect(() => {
        if (currentUser && fcmToken) {
            const saveToken = async () => {
                try {
                    const userRef = doc(db, "profiles", currentUser.id);
                    // Check if token is already different to avoid redundant writes
                    if (currentUser.fcmToken !== fcmToken) {
                        await updateDoc(userRef, { fcmToken: fcmToken });
                        console.log("FCM Token synced to profile:", fcmToken);
                    }
                } catch (error) {
                    console.error("Error syncing FCM token:", error);
                }
            };
            saveToken();
        }
    }, [currentUser, fcmToken]);





    useEffect(() => {
        if (!currentUser) { setProfiles([]); return; }
        const unsub = onSnapshot(collection(db, "profiles"), (snapshot) => { setProfiles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); }, (error) => console.error("Error fetching profiles:", error));
        return () => unsub();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) { setChatRequests([]); return; }
        const q = query(collection(db, 'chatRequests'), where('participants', 'array-contains', currentUser.id));
        const unsub = onSnapshot(q, (snapshot) => {
            setChatRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            console.error("Error listening to chat requests:", error);
        });
        return () => unsub();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) { setNotifications([]); return; }
        const notificationsRef = collection(db, 'profiles', currentUser.id, 'notifications');
        const q = query(notificationsRef, orderBy('timestamp', 'desc'), limit(20));
        
        let isInitial = true; // Track initial snapshot load

        const unsub = onSnapshot(q, (snapshot) => {
            const allNotifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(n => n.type !== 'profile_view');
            setNotifications(allNotifs);

            snapshot.docChanges().forEach(change => {
                if (change.type === 'added') {
                    // Skip showing alerts for pre-existing notifications on page mount/reload
                    if (isInitial) return;

                    const data = change.doc.data();
                    if (data.type === 'profile_view') return;
                    
                    notificationSound.current?.play().catch(e => console.error("Audio play failed:", e));
                    
                    let alertText = "New notification!";
                    if (data.type === 'chat_request') {
                        alertText = `New connection request from ${data.fromUserName || 'Someone'}!`;
                    } else if (data.type === 'chat_accept') {
                        alertText = `${data.fromUserName || 'Someone'} accepted your connection request!`;
                    } else if (data.type === 'like') {
                        alertText = `${data.fromUserName || 'Someone'} liked your profile!`;
                    }
                    
                    showNotification(alertText, "success");
                }
            });

            isInitial = false; // Mark initial snapshot loaded
        }, (error) => {
            console.error("Error listening to notifications:", error);
        });
        return () => unsub();
    }, [currentUser]);

    // Simplified and more efficient listener for chat notifications
    useEffect(() => {
        if (!currentUser) return;

        // Keep track of active message listeners to prevent duplicate listeners and memory leaks
        const activeListeners = new Map();

        // Query for all chats where the current user is a participant.
        // This is more robust and will detect new chats automatically.
        const chatsQuery = query(collection(db, 'chats'), where('participants', 'array-contains', currentUser.id));

        const unsub = onSnapshot(chatsQuery, (chatsSnapshot) => {
            const currentChatIds = new Set();

            chatsSnapshot.forEach(chatDoc => {
                const chatId = chatDoc.id;
                currentChatIds.add(chatId);

                // If we already have an active listener for this chat, skip creating a new one
                if (activeListeners.has(chatId)) return;

                const messagesRef = collection(db, 'chats', chatId, 'messages');
                
                // Safe way to get otherUserId using participants array
                const chatData = chatDoc.data();
                const participants = chatData.participants || chatId.split('_');
                const otherUserId = participants.find(p => p !== currentUser.id);

                if (!otherUserId) return;

                // Single listener per chat that handles both last message and unread count
                const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'));
                const unsubMessages = onSnapshot(messagesQuery, (snapshot) => {
                    const messages = snapshot.docs
                        .map(doc => ({ id: doc.id, ...doc.data() }))
                        .filter(msg => !(msg.blocked === true && msg.senderId !== currentUser.id));

                    // --- GLOBAL DELIVERY RECEIPT LOGIC ---
                    // If we receive messages where we are the recipient and status is 'sent',
                    // we should mark them as 'delivered' because the app has technically "received" them.
                    messages.forEach(msg => {
                        if (msg.senderId !== currentUser.id && msg.status === 'sent') {
                            // Mark as delivered
                            const msgRef = doc(db, 'chats', chatId, 'messages', msg.id);
                            updateDoc(msgRef, { status: 'delivered' }).catch(err => console.error("Error marking delivered:", err));
                        }
                    });
                    // -------------------------------------

                    if (messages.length > 0) {
                        const lastMessage = messages[0];
                        const newUnreadCount = messages.filter(msg =>
                            msg.senderId === otherUserId &&
                            (msg.status === 'sent' || msg.status === 'delivered')
                        ).length;

                        setUnreadChats(prev => {
                            const oldUnreadCount = prev[chatId]?.count || 0;
                            if (newUnreadCount > oldUnreadCount) {
                                notificationSound.current?.play().catch(e => console.error("Audio play failed:", e));

                                // --- Browser Notification Logic ---
                                if (document.hidden && Notification.permission === 'granted') {
                                    const senderProfile = (profilesRef.current || []).find(p => p.id === otherUserId);
                                    const notification = new Notification(`New message from ${senderProfile?.name || 'Someone'}`, {
                                        body: lastMessage.text,
                                        icon: senderProfile?.images?.[0] || '/mylucky.png'
                                    });
                                }
                            }
                            return {
                                ...prev,
                                [chatId]: {
                                    lastMessage: lastMessage.text,
                                    timestamp: lastMessage.timestamp?.toMillis(),
                                    count: newUnreadCount
                                },
                            };
                        });
                    } else {
                        // If all messages were deleted or there are no messages, remove this chat from unread list
                        setUnreadChats(prev => {
                            const next = { ...prev };
                            delete next[chatId];
                            return next;
                        });
                    }
                }, (error) => {
                    console.error(`Error listening to messages for chat ${chatId}:`, error);
                });

                // Store the unsubscribe function for this chat
                activeListeners.set(chatId, unsubMessages);
            });

            // Clean up listeners for any chats that are no longer in the snapshot (e.g. deleted/archived chats)
            for (const [chatId, unsubMessages] of activeListeners.entries()) {
                if (!currentChatIds.has(chatId)) {
                    unsubMessages();
                    activeListeners.delete(chatId);
                    setUnreadChats(prev => {
                        const next = { ...prev };
                        delete next[chatId];
                        return next;
                    });
                }
            }

        }, (error) => {
            console.error("Error listening to chats collection query:", error);
        });

        // Cleanup function for useEffect: unsubscribe from chats query AND all active messages queries
        return () => {
            unsub();
            for (const unsubMessages of activeListeners.values()) {
                unsubMessages();
            }
            activeListeners.clear();
        };
    }, [currentUser]); // The dependency array for the useEffect

    const handleLikeProfile = async (targetProfile) => {
        if (!currentUser || !targetProfile) return;

        const isPremiumOrStaff = currentUser.isAdmin || currentUser.isViewer || currentUser.isEditor || currentUser.profileComplete;
        if (!isPremiumOrStaff) {
            setShowPaywallModal(true);
            showNotification("Unlock Premium to like profiles.", "info");
            return;
        }

        const isLiked = currentUser.likedUsers?.includes(targetProfile.id);

        try {
            const myProfileRef = doc(db, 'profiles', currentUser.id);

            if (isLiked) {
                // UNLIKE PROCESS:
                // 1. Remove from local array in Firestore
                await updateDoc(myProfileRef, {
                    likedUsers: arrayRemove(targetProfile.id)
                });

                // 2. Delete notification in target's subcollection
                const targetNotifsRef = collection(db, 'profiles', targetProfile.id, 'notifications');
                const q = query(
                    targetNotifsRef,
                    where('type', '==', 'like'),
                    where('fromUserId', '==', currentUser.id)
                );
                const snap = await getDocs(q);
                snap.forEach(async (d) => {
                    await deleteDoc(d.ref);
                });

                showNotification(`Removed like from ${targetProfile.name}'s profile.`, "info");
                return;
            }

            // LIKE PROCESS:
            // 1. Add to local array in Firestore
            await updateDoc(myProfileRef, {
                likedUsers: arrayUnion(targetProfile.id)
            });

            // 2. In-App Notification (Firestore)
            const targetNotifsRef = collection(db, 'profiles', targetProfile.id, 'notifications');
            await addDoc(targetNotifsRef, {
                type: 'like',
                fromUserId: currentUser.id,
                fromUserName: `${currentUser.name} ${currentUser.lastName || ''}`.trim(),
                fromUserProfilePic: currentUser.images?.[0] || '',
                timestamp: serverTimestamp(),
                read: false,
                shortDetails: `${calculateAge(currentUser.dob)} yrs, ${currentUser.city || 'Unknown City'}, ${currentUser.profession || 'Professional'}`
            });

            // Show success toast
            showNotification(`You liked ${targetProfile.name}'s profile!`, "success");

            // 3. EmailJS Notification (Requires Valid Credentials)
            const SERVICE_ID = "service_v1883k8";
            const TEMPLATE_ID = "template_example"; 
            const PUBLIC_KEY = "-example-public-key-";

            if (targetProfile.email && SERVICE_ID !== "service_v1883k8") {
                const templateParams = {
                    to_email: targetProfile.email,
                    to_name: targetProfile.name,
                    from_name: `${currentUser.name} ${currentUser.lastName || ''}`,
                    from_details: `${calculateAge(currentUser.dob)} yrs, ${currentUser.city}`,
                    message: `${currentUser.name} has liked your profile on MyLuckySpouse! Log in to the app to connect with them.`
                };
                
                await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
                console.log("Like email sent via emailjs");
            }

        } catch (error) {
            console.error("Error processing like toggle:", error);
            showNotification("Failed to update like. Please try again.", "error");
        }
    };

    const handleSendChatRequest = async (targetUserId) => {
        if (!currentUser) return;
        const isPremiumOrStaff = currentUser.isAdmin || currentUser.isViewer || currentUser.isEditor || currentUser.profileComplete;
        if (!isPremiumOrStaff) {
            setShowPaywallModal(true);
            showNotification("Unlock Premium to connect with matches.", "info");
            return;
        }
        try {
            const uid1 = currentUser.id;
            const uid2 = targetUserId;
            const requestId = uid1 < uid2 ? `request_${uid1}_${uid2}` : `request_${uid2}_${uid1}`;
            
            await setDoc(doc(db, 'chatRequests', requestId), {
                senderId: currentUser.id,
                receiverId: targetUserId,
                participants: [currentUser.id, targetUserId],
                status: 'pending',
                timestamp: serverTimestamp()
            });

            const notificationsRef = collection(db, 'profiles', targetUserId, 'notifications');
            await addDoc(notificationsRef, {
                type: 'chat_request',
                fromUserId: currentUser.id,
                fromUserName: `${currentUser.name} ${currentUser.lastName || ''}`.trim(),
                fromUserProfilePic: currentUser.images?.[0] || '',
                timestamp: serverTimestamp(),
                read: false,
                shortDetails: `${calculateAge(currentUser.dob)} yrs, ${currentUser.city || 'Unknown City'}`
            });

            showNotification("Connection request sent!", "success");
        } catch (error) {
            console.error("Error sending connection request:", error);
            showNotification("Failed to send request. " + error.message, "error");
        }
    };

    const handleAcceptChatRequest = async (requestId) => {
        if (!currentUser) return;
        const isPremiumOrStaff = currentUser.isAdmin || currentUser.isViewer || currentUser.isEditor || currentUser.profileComplete;
        if (!isPremiumOrStaff) {
            setShowPaywallModal(true);
            showNotification("Unlock Premium to accept connection requests.", "info");
            return;
        }
        try {
            const reqRef = doc(db, 'chatRequests', requestId);
            await updateDoc(reqRef, {
                status: 'accepted'
            });

            const reqSnap = await getDoc(reqRef);
            if (reqSnap.exists()) {
                const reqData = reqSnap.data();
                const senderId = reqData.senderId;

                // Clean up any corresponding 'chat_request' notification in currentUser's notifications
                try {
                    const myNotificationsRef = collection(db, 'profiles', currentUser.id, 'notifications');
                    const q = query(myNotificationsRef, where('type', '==', 'chat_request'), where('fromUserId', '==', senderId));
                    const querySnap = await getDocs(q);
                    querySnap.forEach(async (d) => {
                        await deleteDoc(d.ref);
                    });
                } catch (err) {
                    console.error("Error cleaning up accept notification:", err);
                }

                const notificationsRef = collection(db, 'profiles', senderId, 'notifications');
                await addDoc(notificationsRef, {
                    type: 'chat_accept',
                    fromUserId: currentUser.id,
                    fromUserName: `${currentUser.name} ${currentUser.lastName || ''}`.trim(),
                    fromUserProfilePic: currentUser.images?.[0] || '',
                    timestamp: serverTimestamp(),
                    read: false,
                    shortDetails: "Accepted your connection request"
                });

                const chatId = senderId < currentUser.id ? `${senderId}_${currentUser.id}` : `${currentUser.id}_${senderId}`;
                const chatDocRef = doc(db, 'chats', chatId);
                await setDoc(chatDocRef, {
                    participants: [currentUser.id, senderId],
                    lastMessage: "Connected! You can now chat with each other.",
                    lastMessageTimestamp: serverTimestamp(),
                    lastSenderId: currentUser.id,
                    timestamp: serverTimestamp()
                }, { merge: true });
            }

            showNotification("Connection request accepted!", "success");
        } catch (error) {
            console.error("Error accepting connection request:", error);
            showNotification("Failed to accept request. " + error.message, "error");
        }
    };

    const handleDeclineChatRequest = async (requestId) => {
        if (!currentUser) return;
        try {
            // Find who the sender was before deleting the request
            const reqRef = doc(db, 'chatRequests', requestId);
            const reqSnap = await getDoc(reqRef);
            let senderId = null;
            if (reqSnap.exists()) {
                senderId = reqSnap.data().senderId;
            }

            await deleteDoc(reqRef);

            // Clean up any corresponding 'chat_request' notification in currentUser's notifications
            if (senderId) {
                try {
                    const myNotificationsRef = collection(db, 'profiles', currentUser.id, 'notifications');
                    const q = query(myNotificationsRef, where('type', '==', 'chat_request'), where('fromUserId', '==', senderId));
                    const querySnap = await getDocs(q);
                    querySnap.forEach(async (d) => {
                        await deleteDoc(d.ref);
                    });
                } catch (err) {
                    console.error("Error cleaning up decline notification:", err);
                }
            }

            showNotification("Connection request declined.", "info");
        } catch (error) {
            console.error("Error declining connection request:", error);
            showNotification("Failed to decline request. " + error.message, "error");
        }
    };

    const handleAcceptRequestFromNotification = async (notificationId, senderId) => {
        if (!currentUser || !senderId) return;
        try {
            // Optimistically remove the notification from local state
            setNotifications(prev => prev.filter(n => n.id !== notificationId));

            const uid1 = currentUser.id;
            const uid2 = senderId;
            const requestId = uid1 < uid2 ? `request_${uid1}_${uid2}` : `request_${uid2}_${uid1}`;

            // Perform Firestore updates
            await handleAcceptChatRequest(requestId);
            await deleteDoc(doc(db, 'profiles', currentUser.id, 'notifications', notificationId));
        } catch (error) {
            console.error("Error accepting request from notification:", error);
        }
    };

    const handleDeclineRequestFromNotification = async (notificationId, senderId) => {
        if (!currentUser || !senderId) return;
        try {
            // Optimistically remove the notification from local state
            setNotifications(prev => prev.filter(n => n.id !== notificationId));

            const uid1 = currentUser.id;
            const uid2 = senderId;
            const requestId = uid1 < uid2 ? `request_${uid1}_${uid2}` : `request_${uid2}_${uid1}`;

            // Perform Firestore updates
            await handleDeclineChatRequest(requestId);
            await deleteDoc(doc(db, 'profiles', currentUser.id, 'notifications', notificationId));
        } catch (error) {
            console.error("Error declining request from notification:", error);
        }
    };

    const handleClearNotification = async (notificationId, e) => {
        if (e) e.stopPropagation();
        if (!currentUser) return;
        try {
            await deleteDoc(doc(db, 'profiles', currentUser.id, 'notifications', notificationId));
            showNotification("Notification removed.", "success");
        } catch (error) {
            console.error("Error deleting notification:", error);
            showNotification("Failed to delete notification. " + error.message, "error");
        }
    };

    const handleClearAllNotifications = async () => {
        if (!currentUser || !notifications.length) return;
        if (!window.confirm("Are you sure you want to clear all notifications?")) return;
        try {
            const promises = notifications.map(n => 
                deleteDoc(doc(db, 'profiles', currentUser.id, 'notifications', n.id))
            );
            await Promise.all(promises);
            showNotification("All notifications cleared.", "success");
        } catch (error) {
            console.error("Error clearing all notifications:", error);
            showNotification("Failed to clear notifications. " + error.message, "error");
        }
    };

    const handleDisconnectUser = async (targetUserId) => {
        if (!currentUser) return;
        try {
            const uid1 = currentUser.id;
            const uid2 = targetUserId;
            
            const chatId = uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
            const requestId = uid1 < uid2 ? `request_${uid1}_${uid2}` : `request_${uid2}_${uid1}`;
            
            await deleteDoc(doc(db, 'chats', chatId));
            await deleteDoc(doc(db, 'chatRequests', requestId));
            closeChat(targetUserId);
            
            showNotification("Disconnected successfully.", "success");
        } catch (error) {
            console.error("Error disconnecting:", error);
            showNotification("Failed to disconnect. " + error.message, "error");
        }
    };

    const handleBlockUser = async (targetUserId) => {
        if (!currentUser) return;
        try {
            const userRef = doc(db, 'profiles', currentUser.id);
            await updateDoc(userRef, {
                blockedUsers: arrayUnion(targetUserId)
            });
            // Chat-only block: connection request is NOT deleted, chat widget is NOT closed, view is NOT redirected
        } catch (error) {
            console.error("Error blocking user:", error);
            showNotification("Failed to block user. " + error.message, "error");
        }
    };

    const handleUnblockUser = async (targetUserId) => {
        if (!currentUser) return;
        try {
            const userRef = doc(db, 'profiles', currentUser.id);
            await updateDoc(userRef, {
                blockedUsers: arrayRemove(targetUserId)
            });

            showNotification("User unblocked.", "success");
        } catch (error) {
            console.error("Error unblocking user:", error);
            showNotification("Failed to unblock user. " + error.message, "error");
        }
    };

    const openChat = (profile) => {
        const isPremiumOrStaff = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor || currentUser?.profileComplete;
        if (!isPremiumOrStaff) {
            setShowPaywallModal(true);
            showNotification("Unlock Premium to start a chat/connect.", "info");
            return;
        }
        setIsChatListOpen(false);
        setActiveChats(prev => {
            if (prev.find(c => c.id === profile.id)) return prev;
            const newChats = [...prev, profile];
            if (window.innerWidth >= 768) {
                return newChats.length > 3 ? newChats.slice(newChats.length - 3) : newChats;
            } else {
                return [profile];
            }
        });
    };
    const closeChat = (chatId) => {
        setActiveChats(prev => prev.filter(c => c.id !== chatId));
    };
    const markAllNotificationsAsRead = async () => {
        if (!currentUser || !notifications.length) return;
        const unreadNotifications = notifications.filter(n => !n.read);
        if (unreadNotifications.length === 0) return;

        // Optimistically set all local notifications as read to instantly clear the badge count
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));

        for (const n of unreadNotifications) {
            try {
                const docRef = doc(db, 'profiles', currentUser.id, 'notifications', n.id);
                await updateDoc(docRef, { read: true });
            } catch (e) {
                console.error("Error marking notification as read:", e);
            }
        }
    };
    const toggleNotificationList = () => {
        setIsNotificationListOpen(prev => {
            const next = !prev;
            if (next) markAllNotificationsAsRead();
            return next;
        });
        setIsChatListOpen(false);
    };
    const handleNotificationItemClick = (senderProfile, type) => {
        if (type === 'chat_accept') {
            openChat(senderProfile);
        } else {
            setSelectedProfile(senderProfile);
            setView('detailed-profile');
        }
        setIsNotificationListOpen(false);
    };
    const toggleChatList = () => {
        setIsChatListOpen(prev => !prev);
        setIsNotificationListOpen(false);
    };
    const totalUnreadCount = Object.values(unreadChats).reduce((sum, chat) => sum + (chat.count || 0), 0);

    const handleAdminViewProfile = (profile) => {
        setSelectedProfile(profile);
        setView('detailed-profile-admin');
    };

    const handleLogout = async () => {
        if (currentUser) {
            try {
                // Manually set the user to offline before signing out.
                const userStatusFirestoreRef = doc(db, '/profiles/' + currentUser.id);
                await updateDoc(userStatusFirestoreRef, { isOnline: false, lastSeen: serverTimestamp() });
            } catch (error) {
                console.error("Error setting user offline on logout:", error);
            }
        }
        setMatchesScrollY(0); // Reset scroll position on logout
        await signOut(auth);
    };

    // --- EFFECT: Removed automatic Profile Enforcement on load ---
    // Users can now see the "All Matches" grid immediately after login.
    // The ProfileFormModal will only trigger when they interact with specific elements.

    const MatrimonyPaywallScreen = ({ currentUser, setView, showNotification }) => {
        const [paymentId, setPaymentId] = useState('');
        const [receiptFile, setReceiptFile] = useState(null);
        const [isUploading, setIsUploading] = useState(false);
        const [uploadProgress, setUploadProgress] = useState(0);
        const [showProofForm, setShowProofForm] = useState(false);

        useEffect(() => {
            const scriptId = 'razorpay-embed-btn-js';
            let script = document.getElementById(scriptId);
            
            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://cdn.razorpay.com/static/embed_btn/bundle.js';
                script.defer = true;
                script.onload = () => {
                    const rzp = window['__rzp__'];
                    if (rzp && rzp.init) {
                        rzp.init();
                    }
                };
                document.body.appendChild(script);
            } else {
                const rzp = window['__rzp__'];
                if (rzp && rzp.init) {
                    rzp.init();
                }
            }
        }, []);

        const handleFileChange = (e) => {
            if (e.target.files[0]) {
                setReceiptFile(e.target.files[0]);
            }
        };

        const handleSubmitProof = async (e) => {
            e.preventDefault();
            if (!paymentId.trim()) {
                showNotification("Please enter your Razorpay Payment ID.", "error");
                return;
            }

            setIsUploading(true);
            try {
                let screenshotUrl = '';
                if (receiptFile) {
                    // Upload screenshot to Firebase Storage
                    const storageRef = ref(storage, `payment_receipts/${currentUser.id}_receipt`);
                    const uploadTask = uploadBytesResumable(storageRef, receiptFile);
                    
                    await new Promise((resolve, reject) => {
                        uploadTask.on('state_changed',
                            (snapshot) => {
                                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                setUploadProgress(progress);
                            },
                            (error) => {
                                console.error("Storage upload error:", error);
                                reject(error);
                            },
                            async () => {
                                screenshotUrl = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve();
                            }
                        );
                    });
                }

                // Update user profile in Firestore
                const userRef = doc(db, "profiles", currentUser.id);
                await updateDoc(userRef, {
                    isUnderReview: true,
                    paymentDetails: {
                        paymentId: paymentId.trim(),
                        screenshotUrl: screenshotUrl,
                        submittedAt: Date.now(),
                        status: 'pending_review'
                    }
                });

                showNotification("Payment proof submitted successfully! Review in progress.", "success");
            } catch (error) {
                console.error("Error submitting proof:", error);
                showNotification("Failed to submit payment proof. Please try again.", "error");
            } finally {
                setIsUploading(false);
                setUploadProgress(0);
            }
        };

        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl w-full space-y-8 bg-white border border-stone-100 p-8 sm:p-12 rounded-3xl shadow-soft relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-matrimony-50 via-rose-500 to-amber-500"></div>
                    
                    <div className="text-center">
                        <span className="bg-rose-50 text-rose-600 border border-rose-100 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">Premium Access</span>
                        <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-stone-900 font-serif">Unlock Your Lucky Spouse</h2>
                        <p className="mt-2 text-sm text-stone-500">Complete your subscription to connect with verified matches instantly.</p>
                    </div>

                    {/* Features list */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-stone-50 p-6 rounded-2xl border border-stone-100">
                        <div className="flex items-center gap-3">
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                            </span>
                            <div>
                                <p className="text-sm font-bold text-stone-800">Unlimited Matches</p>
                                <p className="text-xs text-stone-500">Browse and view all verified profiles</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                            </span>
                            <div>
                                <p className="text-sm font-bold text-stone-800">Direct Chatting</p>
                                <p className="text-xs text-stone-500">Instantly start a chat with candidates</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                            </span>
                            <div>
                                <p className="text-sm font-bold text-stone-800">100% Verified Members</p>
                                <p className="text-xs text-stone-500">Contact genuine matrimonial seekers</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                            </span>
                            <div>
                                <p className="text-sm font-bold text-stone-800">AI Compatibility Scores</p>
                                <p className="text-xs text-stone-500">Coming soon / लवकरच येत आहे</p>
                            </div>
                        </div>
                    </div>

                    {/* Highlighted Payment Notice Banner */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 rounded-2xl p-4 md:p-5 flex items-start gap-3.5 shadow-sm animate-fade-in-up text-left">
                        <span className="text-xl md:text-2xl mt-0.5 animate-pulse">ℹ️</span>
                        <div className="space-y-1">
                            <p className="font-bold text-amber-900 text-sm md:text-base leading-snug">
                                If you already paid, please ignore the message below. Our team will contact you shortly.
                            </p>
                            <p className="text-xs text-amber-700 font-medium">
                                जर तुम्ही आधीच पैसे भरले असतील, तर कृपया खालील संदेशाकडे दुर्लक्ष करा. आमची टीम लवकरच तुमच्याशी संपर्क साधेल.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-center p-6 border border-stone-100 rounded-2xl bg-stone-50/50 shadow-inner text-center w-full">
                        <p className="text-xs text-stone-400 uppercase tracking-widest font-extrabold mb-1">Membership Plan</p>
                        <p className="text-2xl font-black text-stone-900 font-serif">₹499 <span className="text-xs text-stone-400 font-sans font-normal">/ Full 1-Year Access</span></p>
                        
                        <div className="mt-6 w-full flex justify-center min-h-[48px] items-center">
                            {(() => {
                                const emailPrefill = currentUser?.email ? encodeURIComponent(currentUser.email) : '';
                                const phonePrefill = currentUser?.phone ? encodeURIComponent(String(currentUser.phone).replace(/[^\d]/g, '')) : '';
                                let targetUrl = 'https://pages.razorpay.com/pl_StuMktNX5Xw0k8/view';
                                const params = [];
                                if (emailPrefill) params.push(`email=${emailPrefill}`);
                                if (phonePrefill) params.push(`phone=${phonePrefill}`);
                                if (params.length > 0) {
                                    targetUrl += `?${params.join('&')}`;
                                }
                                return (
                                    <div 
                                        className="razorpay-embed-btn" 
                                        data-url={targetUrl} 
                                        data-text="Pay Now" 
                                        data-color="#528FF0" 
                                        data-size="large"
                                    ></div>
                                );
                            })()}
                        </div>
                        <p className="mt-2 text-[10px] text-stone-400">Secure 256-bit encryption processed by Razorpay gateway.</p>
                    </div>

                    <div className="border-t border-stone-100 pt-6 text-center">
                        {!showProofForm ? (
                            <button 
                                type="button"
                                onClick={() => setShowProofForm(true)} 
                                className="text-xs font-bold text-matrimony-600 hover:text-matrimony-700 transition-colors uppercase tracking-wider flex items-center justify-center gap-1 mx-auto"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                Already Paid? Submit Payment Proof
                            </button>
                        ) : (
                            <form onSubmit={handleSubmitProof} className="space-y-4 text-left animate-fade-in-up bg-stone-50/70 p-6 rounded-2xl border border-stone-100">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-sm font-bold text-stone-800 uppercase tracking-wider">Submit Payment Verification</h3>
                                    <button type="button" onClick={() => setShowProofForm(false)} className="text-xs font-medium text-stone-400 hover:text-stone-600">Cancel</button>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Razorpay Payment ID *</label>
                                    <input 
                                        type="text" 
                                        required 
                                        value={paymentId} 
                                        onChange={(e) => setPaymentId(e.target.value)} 
                                        placeholder="e.g. pay_Op3Ks7a..." 
                                        className="w-full px-3 py-2 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-matrimony-500/30 focus:border-matrimony-500 text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Receipt Screenshot (Optional)</label>
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleFileChange} 
                                        className="w-full text-xs text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer"
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={isUploading} 
                                    className="w-full py-3 bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold transition-all uppercase tracking-widest"
                                >
                                    {isUploading ? `Uploading Proof... ${Math.round(uploadProgress)}%` : 'Submit for Verification'}
                                </button>
                            </form>
                        )}
                    </div>

                    <div className="flex justify-center gap-4 text-xs font-bold text-stone-400 uppercase tracking-widest pt-2">
                        <button type="button" onClick={() => setView('my-profile')} className="hover:text-stone-600 transition-colors">Edit Profile</button>
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) { return <div className="min-h-screen flex items-center justify-center bg-rose-50"><div className="w-16 h-16 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin"></div></div>; }

    // --- BLOCKING OVERLAY LOGIC MOVED TO renderMainContent ---
    // (Removed early return to prevent Invalid Hook Call errors)

    const renderMainContent = () => {
        if (view === 'privacy-policy') {
            return <PrivacyPolicyPage setView={setView} currentUser={currentUser} />;
        }

        if (!currentUser) {
            return <AuthPage setView={setView} showNotification={showNotification} currentUser={currentUser} setCurrentUser={setCurrentUser} />;
        }

        // The useEffect above now handles redirection for incomplete profiles.
        // This function can now focus solely on rendering the current view.
        const isAdmin = currentUser.isAdmin;
        const isViewer = currentUser.isViewer;
        const isEditor = currentUser.isEditor;
        let finalView = view;

        // For non-admin/non-viewer users, check if they explicitly asked for paywall
        if (!isAdmin && !isViewer && !isEditor) {
            if (finalView === 'paywall') {
                const hasSubmittedPayment = currentUser.paymentDetails && currentUser.paymentDetails.paymentId;

                if (hasSubmittedPayment) {
                    return (
                        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
                            <div className="bg-white max-w-xl w-full p-8 md:p-12 rounded-3xl shadow-soft text-center border border-stone-100 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-rose-500 to-amber-500 animate-pulse"></div>
                                <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-rose-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-rose-500 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <h2 className="text-3xl font-extrabold text-stone-900 mb-2 font-serif">Your Account is Under Review</h2>
                                <h3 className="text-lg font-bold text-stone-500 mb-5 font-sans">तुमचे खाते पुनरावलोकनाधीन आहे</h3>
                                {currentUser.paymentDetails?.paymentId && (
                                    <p className="text-stone-600 text-sm mb-6 font-mono font-medium tracking-wide bg-stone-100 py-1.5 px-3 rounded-lg inline-block">
                                        Payment Ref: {currentUser.paymentDetails.paymentId}
                                    </p>
                                )}
                                <p className="text-base text-stone-600 mb-8 leading-relaxed">
                                    Thank you! Your profile and premium payment are being verified by our team. Your account will be activated and unlocked instantly upon approval.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <button onClick={() => setView('my-profile')} className="px-6 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-bold transition-all text-sm shadow-sm">
                                        View My Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }

                // Render Payment Paywall Screen
                return <MatrimonyPaywallScreen currentUser={currentUser} setView={setView} showNotification={showNotification} />;
            }
        }

        // Standard Routing for authorized users
        switch (finalView) {
            case 'privacy-policy':
                return <PrivacyPolicyPage setView={setView} />;
            case 'paywall':
                const hasSubmittedPayment = currentUser.paymentDetails && currentUser.paymentDetails.paymentId;
                if (hasSubmittedPayment) {
                    return (
                        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
                            <div className="bg-white max-w-xl w-full p-8 md:p-12 rounded-3xl shadow-soft text-center border border-stone-100 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-rose-500 to-amber-500 animate-pulse"></div>
                                <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-rose-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-rose-500 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <h2 className="text-3xl font-extrabold text-stone-900 mb-2 font-serif">Your Account is Under Review</h2>
                                <h3 className="text-lg font-bold text-stone-500 mb-5 font-sans">तुमचे खाते पुनरावलोकनाधीन आहे</h3>
                                {currentUser.paymentDetails?.paymentId && (
                                    <p className="text-stone-600 text-sm mb-6 font-mono font-medium tracking-wide bg-stone-100 py-1.5 px-3 rounded-lg inline-block">
                                        Payment Ref: {currentUser.paymentDetails.paymentId}
                                    </p>
                                )}
                                <p className="text-base text-stone-600 mb-8 leading-relaxed">
                                    Thank you! Your profile and premium payment are being verified by our team. Your account will be activated and unlocked instantly upon approval.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <button onClick={() => setView('my-profile')} className="px-6 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-bold transition-all text-sm shadow-sm">
                                        View My Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }
                return <MatrimonyPaywallScreen currentUser={currentUser} setView={setView} showNotification={showNotification} />;
            case 'admin':
                return (isAdmin || isEditor) ?
                    <AdminPanel profiles={profiles} showNotification={showNotification} onViewProfile={handleAdminViewProfile} currentUser={currentUser} openChat={openChat} /> :
                    <MatchesPage profiles={profiles} setView={setView} setSelectedProfile={setSelectedProfile} openChat={openChat} onLike={handleLikeProfile} isLoading={!profiles.length} currentUser={currentUser} showNotification={showNotification} scrollY={matchesScrollY} setScrollY={setMatchesScrollY} filter={matchesGenderFilter} setFilter={setMatchesGenderFilter} onEditProfile={() => { setProfileModalMode('update'); setShowProfileModal(true); }} />;
            case 'matches':
                return <MatchesPage profiles={profiles} setView={setView} setSelectedProfile={setSelectedProfile} openChat={openChat} onLike={handleLikeProfile} isLoading={!profiles.length} currentUser={currentUser} showNotification={showNotification} scrollY={matchesScrollY} setScrollY={setMatchesScrollY} filter={matchesGenderFilter} setFilter={setMatchesGenderFilter} onEditProfile={() => { setProfileModalMode('update'); setShowProfileModal(true); }} chatRequests={chatRequests} onSendChatRequest={handleSendChatRequest} onAcceptChatRequest={handleAcceptChatRequest} />;
            case 'ai-matches':
                {
                    const isPremiumOrStaff = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor || currentUser?.profileComplete;
                    if (!isPremiumOrStaff) {
                        setShowPaywallModal(true);
                        setView('matches');
                        return null;
                    }
                    return <AIMatchesPage profiles={profiles} setView={setView} setSelectedProfile={setSelectedProfile} openChat={openChat} currentUser={currentUser} showNotification={showNotification} />;
                }
            case 'my-profile':
                return (
                    <>
                        <MyProfilePage currentUser={currentUser} showNotification={showNotification} setView={setView} onEdit={() => { setProfileModalMode('edit'); setShowProfileModal(true); }} />
                    </>
                );
            // case 'complete-profile': removed
            case 'detailed-profile':
                if (selectedProfile) {
                    const isPremiumOrStaff = currentUser?.isAdmin || currentUser?.isViewer || currentUser?.isEditor || currentUser?.profileComplete;
                    if (!isPremiumOrStaff) {
                        setView('paywall');
                        return null;
                    }
                    const latestSelected = profiles.find(p => p.id === selectedProfile.id) || selectedProfile;
                    return <DetailedProfilePage profile={latestSelected} setView={setView} openChat={openChat} currentUser={currentUser} showNotification={showNotification} chatRequests={chatRequests} handleSendChatRequest={handleSendChatRequest} handleAcceptChatRequest={handleAcceptChatRequest} handleBlockUser={handleBlockUser} handleUnblockUser={handleUnblockUser} handleDisconnectUser={handleDisconnectUser} onLike={handleLikeProfile} />;
                }
                // Prevent the "bounce back" issue by showing a loading state instead of redirecting.
                // This gives React time to update the selectedProfile state.
                return <div className="min-h-screen flex items-center justify-center bg-rose-50"><div className="w-16 h-16 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin"></div></div>;
            case 'detailed-profile-admin':
                if (!isAdmin && !isViewer && !isEditor) return <MatchesPage profiles={profiles} setView={setView} setSelectedProfile={setSelectedProfile} openChat={openChat} onLike={handleLikeProfile} isLoading={!profiles.length} currentUser={currentUser} showNotification={showNotification} scrollY={matchesScrollY} setScrollY={setMatchesScrollY} filter={matchesGenderFilter} setFilter={setMatchesGenderFilter} onEditProfile={() => { setProfileModalMode('update'); setShowProfileModal(true); }} chatRequests={chatRequests} onSendChatRequest={handleSendChatRequest} onAcceptChatRequest={handleAcceptChatRequest} />;
                if (selectedProfile) {
                    const latestSelected = profiles.find(p => p.id === selectedProfile.id) || selectedProfile;
                    return <DetailedProfilePage profile={latestSelected} setView={setView} openChat={openChat} backView="admin" currentUser={currentUser} showNotification={showNotification} chatRequests={chatRequests} handleSendChatRequest={handleSendChatRequest} handleAcceptChatRequest={handleAcceptChatRequest} handleBlockUser={handleBlockUser} handleUnblockUser={handleUnblockUser} handleDisconnectUser={handleDisconnectUser} onLike={handleLikeProfile} />;
                }
                setView('admin');
                return null;
            default:
                return <MatchesPage profiles={profiles} setView={setView} setSelectedProfile={setSelectedProfile} openChat={openChat} onLike={handleLikeProfile} isLoading={!profiles.length} currentUser={currentUser} showNotification={showNotification} scrollY={matchesScrollY} setScrollY={setMatchesScrollY} filter={matchesGenderFilter} setFilter={setMatchesGenderFilter} onEditProfile={() => { setProfileModalMode('update'); setShowProfileModal(true); }} chatRequests={chatRequests} onSendChatRequest={handleSendChatRequest} onAcceptChatRequest={handleAcceptChatRequest} />;
        }
    };


    return (
        <div className="min-h-screen flex flex-col bg-stone-50 text-stone-900 font-sans selection:bg-matrimony-200 selection:text-matrimony-900">
            <SplashScreen visible={showSplash} />
            <Notification message={notification.message} type={notification.type} />
            {currentUser && view !== 'admin' && view !== 'complete-profile' && <ParticleBackground />}
            {currentUser && <Header currentUser={currentUser} setView={setView} onInboxClick={toggleChatList} totalUnreadCount={totalUnreadCount} onNotificationClick={toggleNotificationList} unreadNotificationsCount={unreadNotificationsCount} handleLogout={handleLogout} setShowProfileModal={setShowProfileModal} showNotification={showNotification} />}
            {isTermsModalOpen && <TermsAndConditionsModal onClose={() => setIsTermsModalOpen(false)} />}
            {showPaywallModal && (
                <PaywallModal 
                    isOpen={showPaywallModal} 
                    onClose={() => setShowPaywallModal(false)} 
                    currentUser={currentUser} 
                    showNotification={showNotification}
                />
            )}
            <style>{`
                html, body { height: 100%; min-height: 100dvh; overflow-x: hidden; } 
                body { min-height: -webkit-fill-available; } 
                @keyframes ping-slow { 75%, 100% { transform: scale(1.5); opacity: 0; } } 
                .animate-ping-slow { animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite; } 
                @keyframes fade-in-up { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } } 
                .animate-fade-in-up { animation: fade-in-up 0.5s ease-in-out; }
                @keyframes slide-in { 0% { opacity: 0; transform: translateX(30px); } 100% { opacity: 1; transform: translateX(0); } }
                .animate-slide-in { animation: slide-in 0.4s ease-out; }
                @keyframes slide-in-left { 0% { opacity: 0; transform: translateX(-20px); } 100% { opacity: 1; transform: translateX(0); } }
                .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
                @keyframes slide-in-right { 0% { opacity: 0; transform: translateX(20px); } 100% { opacity: 1; transform: translateX(0); } }
                .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
                @keyframes progress-bar { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
                .animate-progress-bar { animation: progress-bar 2s linear infinite; }
            `}</style>
            <main className={`relative flex-grow ${currentUser ? 'pt-20' : ''}`}>{renderMainContent()}</main>
            {currentUser && (<>
                {isChatListOpen && (
                    <ChatListPopup
                        unreadChats={unreadChats}
                        profiles={profiles}
                        onChatItemClick={openChat}
                        onClose={() => setIsChatListOpen(false)}
                        currentUserId={currentUser.id}
                        currentUser={currentUser}
                        chatRequests={chatRequests}
                        onAcceptRequest={handleAcceptChatRequest}
                        onDeclineRequest={handleDeclineChatRequest}
                        onUnblockRequest={handleUnblockUser}
                    />
                )}
                {isNotificationListOpen && (
                    <NotificationListPopup
                        notifications={notifications}
                        onClose={() => setIsNotificationListOpen(false)}
                        onNotificationItemClick={handleNotificationItemClick}
                        profiles={profiles}
                        onAcceptRequest={handleAcceptRequestFromNotification}
                        onDeclineRequest={handleDeclineRequestFromNotification}
                        onClearNotification={handleClearNotification}
                        onClearAllNotifications={handleClearAllNotifications}
                    />
                )}
                {/* Chat Dock */}
                <div className={`fixed inset-x-0 bottom-0 z-[80] pointer-events-none flex flex-col md:flex-row md:justify-end md:items-end md:px-4 md:gap-4 h-0 md:h-auto overflow-visible transition-all duration-300 ${isChatListOpen ? 'md:pr-[26rem]' : ''}`}>
                    {activeChats.map(cUser => {
                        const latestUser = profiles.find(p => p.id === cUser.id) || cUser;
                        return (
                            <div key={cUser.id} className="pointer-events-auto w-full md:w-auto h-full md:h-auto">
                                <ChatWidget
                                    chatUser={latestUser}
                                    currentUser={currentUser}
                                    onClose={() => closeChat(cUser.id)}
                                    showNotification={showNotification}
                                    handleBlockUser={handleBlockUser}
                                    handleUnblockUser={handleUnblockUser}
                                    handleDisconnectUser={handleDisconnectUser}
                                />
                            </div>
                        );
                    })}
                </div>
                <button
                    onClick={() => {
                        // Admins can open chat even if incomplete
                        if (!currentUser.isAdmin && !currentUser.isEditor && calculateProfileCompletion(currentUser) < 100) {
                            showNotification("Please complete your profile to use chat.", "error");
                        } else {
                            toggleChatList();
                        }
                    }}
                    className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 bg-rose-600 text-white p-4 rounded-full shadow-lg hover:bg-rose-700 transition-transform transform hover:scale-110 focus:outline-none z-50 ${(activeChats.length > 0 || isChatListOpen) && 'md:block hidden'}`} aria-label="Open chat inbox">
                    {totalUnreadCount > 0 && !isChatListOpen && (
                        <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs text-white">{totalUnreadCount}</span>
                    )}
                    {isChatListOpen ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            ```
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                    )}
                </button>
            </>)}
            {view !== 'privacy-policy' && (
                <Footer onTermsClick={() => setIsTermsModalOpen(true)} onAdminClick={() => setView('admin')} currentUser={currentUser} setView={setView} />
            )}
            {/* Top Banner for Incomplete Profiles */}
            {currentUser && <IncompleteProfileBanner currentUser={currentUser} onUpdate={() => setShowProfileModal(true)} />}

            {/* ProfilePictureOverlay (Global) */}
            {currentUser && !view.includes('admin') && !currentUser.isAdmin && !currentUser.isEditor && calculateProfileCompletion(currentUser) >= 100 && (!currentUser.images || currentUser.images.length === 0) && <ProfilePictureOverlay currentUser={currentUser} showNotification={showNotification} />}

            {/* ProfileFormModal (Global) */}
            <ProfileFormModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                // Allow users to close the modal to go back to thumbnail browsing
                allowClose={true}
                currentUser={currentUser}
                showNotification={showNotification}
                mode={profileModalMode}
                setView={setView}
                initialStep={profileModalStep}
            />
            {/* Legacy User Update Popup (Inline Form) - Global in App */}
            {showUpdatePopup && currentUser && (
                <div className="fixed inset-0 z-[300] flex justify-center items-start pt-16 px-4 pointer-events-none">
                    <div className="pointer-events-auto bg-white rounded-2xl w-full max-w-sm shadow-2xl transform transition-all animate-slide-down overflow-hidden border border-gray-200">
                        {/* Decorative Header */}
                        <div className="bg-matrimony-600 p-6 flex flex-col items-center text-center relative border-b border-matrimony-700">
                            <button
                                onClick={() => {
                                    setShowUpdatePopup(false);
                                    localStorage.setItem('seen_profile_update_v3', 'true');
                                }}
                                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                            <div className="bg-white/20 p-3 rounded-full mb-3 backdrop-blur-sm shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-white font-serif tracking-wide">Profile Update</h3>
                            <p className="text-matrimony-100 text-xs mt-1 max-w-[80%] font-medium">Help us find your perfect match by adding these missing details.</p>
                        </div>

                        <div className="p-6">
                            <div className="space-y-4 max-h-[50vh] overflow-y-auto px-1 custom-scrollbar-light">
                                {/* Creator Logic */}
                                {(currentUser.createdBy || 'Self').startsWith('Self') === false && !currentUser.creatorName && (
                                    <div className="space-y-3 animate-fade-in-up">
                                        <div>
                                            <label className="block text-xs font-bold text-stone-700 mb-1 uppercase tracking-wider">Creator Name</label>
                                            <input type="text" value={updateFormData.creatorName} onChange={(e) => setUpdateFormData({ ...updateFormData, creatorName: e.target.value })} placeholder="Who created this profile?" className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm focus:border-matrimony-500 focus:ring-2 focus:ring-matrimony-200 outline-none transition-all shadow-sm" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-stone-700 mb-1 uppercase tracking-wider">Creator Phone</label>
                                            <input type="tel" value={updateFormData.creatorPhone} onChange={(e) => setUpdateFormData({ ...updateFormData, creatorPhone: e.target.value })} placeholder="Creator's contact number" className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm focus:border-matrimony-500 focus:ring-2 focus:ring-matrimony-200 outline-none transition-all shadow-sm" />
                                        </div>
                                    </div>
                                )}

                                {/* Birth Details */}
                                {!currentUser.birthTime && (
                                    <div className="animate-fade-in-up">
                                        <label className="block text-xs font-bold text-stone-700 mb-1 uppercase tracking-wider">Birth Time <span className="text-stone-400 font-normal normal-case">(Optional)</span></label>
                                        <input type="time" value={updateFormData.birthTime} onChange={(e) => setUpdateFormData({ ...updateFormData, birthTime: e.target.value })} className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm focus:border-matrimony-500 focus:ring-2 focus:ring-matrimony-200 outline-none transition-all shadow-sm" />
                                    </div>
                                )}
                                {!currentUser.birthPlace && (
                                    <div className="animate-fade-in-up">
                                        <label className="block text-xs font-bold text-stone-700 mb-1 uppercase tracking-wider">Birth Place</label>
                                        <input type="text" value={updateFormData.birthPlace} onChange={(e) => setUpdateFormData({ ...updateFormData, birthPlace: e.target.value })} placeholder="e.g. Mumbai, Pune" className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm focus:border-matrimony-500 focus:ring-2 focus:ring-matrimony-200 outline-none transition-all shadow-sm" />
                                    </div>
                                )}

                                {/* Education */}
                                {!currentUser.specificDegree && (
                                    <div className="animate-fade-in-up">
                                        <label className="block text-xs font-bold text-stone-700 mb-1 uppercase tracking-wider">Specific Degree</label>
                                        <input type="text" value={updateFormData.specificDegree} onChange={(e) => setUpdateFormData({ ...updateFormData, specificDegree: e.target.value })} placeholder="e.g. B.Tech Computer Science" className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm focus:border-matrimony-500 focus:ring-2 focus:ring-matrimony-200 outline-none transition-all shadow-sm" />
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={async () => {
                                    try {
                                        const updateData = {};
                                        if (!currentUser.birthTime && updateFormData.birthTime) updateData.birthTime = updateFormData.birthTime;
                                        if (!currentUser.birthPlace && updateFormData.birthPlace) updateData.birthPlace = updateFormData.birthPlace;
                                        if (!currentUser.specificDegree && updateFormData.specificDegree) updateData.specificDegree = updateFormData.specificDegree;
                                        if ((currentUser.createdBy || 'Self') !== 'Self' && !currentUser.creatorName) {
                                            if (updateFormData.creatorName) updateData.creatorName = updateFormData.creatorName;
                                            if (updateFormData.creatorPhone) updateData.creatorPhone = updateFormData.creatorPhone;
                                        }

                                        if (Object.keys(updateData).length > 0) {
                                            const userRef = doc(db, "profiles", currentUser.id);
                                            await updateDoc(userRef, updateData);
                                            showNotification("Profile updated successfully!");
                                        }
                                        setShowUpdatePopup(false);
                                        localStorage.setItem('seen_profile_update_v3', 'true');
                                    } catch (err) {
                                        console.error("Error updating profile", err);
                                        showNotification("Failed to update profile", "error");
                                    }
                                }}
                                className="mt-8 w-full py-3 bg-matrimony-600 hover:bg-matrimony-700 text-white font-bold rounded-xl shadow-md transition-all transform hover:-translate-y-0.5 active:translate-y-0 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-matrimony-500"
                            >
                                Save & Continue
                            </button>
                            <button
                                onClick={() => {
                                    setShowUpdatePopup(false);
                                    localStorage.setItem('seen_profile_update_v3', 'true');
                                }}
                                className="mt-3 w-full py-2 text-stone-400 hover:text-stone-600 font-medium text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-stone-200 rounded px-2"
                            >
                                I'll do this later
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const PaywallModal = ({ isOpen, onClose, currentUser, showNotification }) => {
    const [paymentId, setPaymentId] = useState('');
    const [receiptFile, setReceiptFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProofForm, setShowProofForm] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const scriptId = 'razorpay-embed-btn-js';
        let script = document.getElementById(scriptId);
        
        if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://cdn.razorpay.com/static/embed_btn/bundle.js';
            script.defer = true;
            script.onload = () => {
                const rzp = window['__rzp__'];
                if (rzp && rzp.init) {
                    rzp.init();
                }
            };
            document.body.appendChild(script);
        } else {
            const rzp = window['__rzp__'];
            if (rzp && rzp.init) {
                rzp.init();
            }
        }
    }, [isOpen]);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setReceiptFile(e.target.files[0]);
        }
    };

    const handleSubmitProof = async (e) => {
        e.preventDefault();
        if (!paymentId.trim()) {
            showNotification("Please enter your Razorpay Payment ID.", "error");
            return;
        }

        setIsUploading(true);
        try {
            let screenshotUrl = '';
            if (receiptFile) {
                const storageRef = ref(storage, `payment_receipts/${currentUser.id}_receipt`);
                const uploadTask = uploadBytesResumable(storageRef, receiptFile);
                
                await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(progress);
                        },
                        (error) => {
                            console.error("Storage upload error:", error);
                            reject(error);
                        },
                        async () => {
                            screenshotUrl = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve();
                        }
                    );
                });
            }

            const userRef = doc(db, "profiles", currentUser.id);
            await updateDoc(userRef, {
                isUnderReview: true,
                paymentDetails: {
                    paymentId: paymentId.trim(),
                    screenshotUrl: screenshotUrl,
                    submittedAt: Date.now(),
                    status: 'pending_review'
                }
            });

            showNotification("Payment proof submitted successfully! Review in progress.", "success");
            onClose();
        } catch (error) {
            console.error("Error submitting proof:", error);
            showNotification("Failed to submit payment proof. Please try again.", "error");
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-md flex justify-center items-center z-[100] p-4 overflow-y-auto animate-fade-in-up">
            <div className="bg-white border border-stone-100 p-6 sm:p-10 rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto relative scrollbar-hide text-center">
                <button onClick={onClose} className="absolute top-5 right-6 text-stone-400 hover:text-stone-700 text-3xl font-bold transition-colors z-30">&times;</button>
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-matrimony-50 via-rose-500 to-amber-500"></div>
                
                <div className="text-center mb-6">
                    <span className="bg-rose-50 text-rose-600 border border-rose-100 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">Premium Access Offer</span>
                    <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold text-stone-900 font-serif">Unlock Your Lucky Spouse</h2>
                    <p className="mt-2 text-sm text-stone-500">Get unlimited access to browse, chat, and connect with all verified profiles.</p>
                </div>

                {/* Features list */}
                <div className="space-y-3 bg-stone-50 p-5 rounded-2xl border border-stone-100 mb-6 text-left">
                    <div className="flex items-center gap-3">
                        <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                        </span>
                        <div>
                            <p className="text-xs font-bold text-stone-800">One-Year Offer ₹499 Package</p>
                            <p className="text-[11px] text-stone-500">Access all profiles, unlimited chats, and direct connects for a full year</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                        </span>
                        <div>
                            <p className="text-xs font-bold text-stone-800">100% Genuine & Verified Members</p>
                            <p className="text-[11px] text-stone-500">Contact verified matrimonial seekers instantly</p>
                        </div>
                    </div>
                </div>

                {/* Highlighted Payment Notice Banner */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 rounded-2xl p-4 flex items-start gap-3 shadow-sm text-left mb-6">
                    <span className="text-lg mt-0.5 animate-pulse">ℹ️</span>
                    <div className="space-y-0.5">
                        <p className="font-bold text-amber-900 text-xs sm:text-sm leading-snug">
                            If you already paid, please ignore this. Our team will verify and activate your account shortly.
                        </p>
                        <p className="text-[11px] text-amber-700 font-medium">
                            जर तुम्ही आधीच पैसे भरले असतील, तर कृपया दुर्लक्ष करा. आमची टीम लवकरच तुमच्याशी संपर्क साधेल.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center p-5 border border-stone-100 rounded-2xl bg-stone-50/50 shadow-inner text-center w-full">
                    <p className="text-[10px] text-stone-400 uppercase tracking-widest font-extrabold mb-1">Membership Plan</p>
                    <p className="text-xl sm:text-2xl font-black text-stone-900 font-serif">₹499 <span className="text-xs text-stone-400 font-sans font-normal">/ Full 1-Year Access</span></p>
                    
                    <div className="mt-5 w-full flex flex-col justify-center min-h-[48px] items-center gap-4">
                        {(() => {
                            const emailPrefill = currentUser?.email ? encodeURIComponent(currentUser.email) : '';
                            const phonePrefill = currentUser?.phone ? encodeURIComponent(String(currentUser.phone).replace(/[^\d]/g, '')) : '';
                            let targetUrl = 'https://pages.razorpay.com/pl_StuMktNX5Xw0k8/view';
                            const params = [];
                            if (emailPrefill) params.push(`email=${emailPrefill}`);
                            if (phonePrefill) params.push(`phone=${phonePrefill}`);
                            if (params.length > 0) {
                                targetUrl += `?${params.join('&')}`;
                            }
                            return (
                                <>
                                    <div 
                                        className="razorpay-embed-btn" 
                                        data-url={targetUrl} 
                                        data-text="Pay Now" 
                                        data-color="#528FF0" 
                                        data-size="large"
                                    ></div>
                                    
                                    {!showProofForm ? (
                                        <button 
                                            onClick={() => setShowProofForm(true)} 
                                            className="text-xs font-bold text-matrimony-600 hover:text-matrimony-700 hover:underline transition-colors mt-2"
                                        >
                                            Already Paid? Submit Payment Proof / आधीच पैसे भरले आहेत का? पुरावा सबमिट करा
                                        </button>
                                    ) : (
                                        <form onSubmit={handleSubmitProof} className="w-full space-y-4 text-left border-t border-stone-200/60 pt-4 mt-2">
                                            <h3 className="text-xs font-black text-stone-700 uppercase tracking-wider">Submit Payment Proof</h3>
                                            
                                            <div>
                                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Razorpay Payment ID *</label>
                                                <input 
                                                    type="text" 
                                                    required
                                                    placeholder="pay_XXXXXXXXXXXXXX"
                                                    value={paymentId}
                                                    onChange={(e) => setPaymentId(e.target.value)}
                                                    className="w-full p-2.5 bg-white border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-matrimony-500/20 focus:border-matrimony-500 text-xs transition-all font-mono"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Screenshot of Receipt (Optional)</label>
                                                <input 
                                                    type="file" 
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="w-full text-xs text-stone-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[11px] file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer"
                                                />
                                            </div>

                                            {isUploading && (
                                                <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
                                                    <div className="bg-matrimony-600 h-1.5 transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                                                </div>
                                            )}

                                            <div className="flex gap-2">
                                                <button 
                                                    type="submit" 
                                                    disabled={isUploading}
                                                    className="flex-1 py-2 bg-gradient-to-r from-matrimony-600 to-rose-500 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5"
                                                >
                                                    {isUploading ? "Uploading..." : "Submit Proof"}
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowProofForm(false)}
                                                    className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-all"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};


